import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Search, Plus, Mail, Phone, Calendar, MapPin,
  User, Users, Euro, Clock, ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
  Printer, MessageSquare, Bell, Settings, Home, Pencil, Trash2, Save, X, ExternalLink,
  CheckSquare, Square, ListChecks, XCircle, TextSearch, Eye, EyeOff, FileText, Loader2, UserPlus
} from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/errors';
import { CHIP_CLASS } from '@/lib/mamvPalette';
import { lookupIbanInfo } from '@/lib/iban';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import { consumeEnrollmentPrefill, hasEnrollmentPrefill } from '@/lib/registrationToEnrollmentPrefill';
import { hrefForSelectedRecord, readSelectedRecord, writeSelectedRecord } from '@/lib/selectedRecord';
import Layout from '@/components/layout/Layout';
import PhoneLink from '@/components/PhoneLink';
import { useActionBarConfig, useActionBar } from '@/contexts/ActionBarContext';
import { useTenant } from '@/contexts/TenantContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import AttendanceStatusBadge from '@/components/AttendanceStatusBadge';
import { attendanceCaptured } from '@/lib/attendanceStatus';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatTime, formatClocks, formatScheduleLabel, formatWeekdayTimeRange, formatDisplayValue, courseDisplayName, tariffFormName, accountHolderLabel, payerNameLabel, formatDeNumber, parseDate } from '@/lib/utils';
import { retryOnCollision } from '@/components/schedule/collisionWarning';
import { statusLabel } from '@/lib/deLabels';
import RecordNavigator from '@/components/ui/record-navigator';
import ListenAusdruckeDialog from '@/pages/Teaching/components/ListenAusdruckeDialog';
import EnrollmentCorrespondence from '@/pages/Teaching/components/EnrollmentCorrespondence';
import { getFooterSettings, footerToParams } from '@/components/FooterDateBar';
import { setVoiceSearchCriteria, VOICE_SEARCH_EVENT } from '@/lib/voiceControl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { AREAL_OPTIONS, getArealLabel, resolveAreal, defaultArealForTenant, toDateInputValue, getMandantKuerzel, formatPersonPk, subjectBaseName, subjectFormSuffix, selectableTeachers, belegungsBasis } from './utils';
import { enrollmentVisibleInFooter } from './enrollmentPeriod';
import EmailLink from '@/components/EmailLink';
import { FormField } from './components/FormField';
import { FieldSuggestDropdown, SUGGESTABLE_FIELD_KEYS, useFieldSearchSuggestions } from './components/FieldSearchSuggest';
import RecordHistoryDialog, { HistoryEyeButton } from './components/RecordHistoryDialog';
import ContactPersonsDialog from './components/ContactPersonsDialog';
import CustodianAssignDialog from './components/CustodianAssignDialog';
import { weitererErziehungsberechtigter } from './custodian';
import { contactRoleLabel } from '@/lib/contactRoles';
import EffectiveDateDialog from './components/EffectiveDateDialog';
import { SelectField } from './components/SelectField';
import { SectionHeader } from './components/SectionHeader';
import { QuicklinksRail, QuicklinksEditDialog, useQuicklinks } from './components/Quicklinks';
import { LiveSearchDropdown } from './components/LiveSearchDropdown';
import EnrollmentCriteriaPanel from './components/EnrollmentCriteriaPanel';
import { validateCriteriaParams, criteriaToParams, searchInputClassName, SEARCH_PLACEHOLDER, TURNUS_OPTIONS, parseCourseRhythm } from './enrollmentSearchCriteria';
import {
  partitionEnrollmentExtraParams,
  nextEnrollmentListQuery,
} from './enrollmentListQuery';
import SubTabs from './components/SubTabs';
import WaitlistPanel from './components/WaitlistPanel';
import CoursePublishTab from './components/CoursePublishTab';
import useTableColumns from '@/hooks/useTableColumns';
import ColumnPicker from '@/components/table/ColumnPicker';
import DateInput from '@/components/ui/date-input';
import NewEnrollmentDialog from './components/NewEnrollmentDialog';
import BulkEnrollStudentsDialog from './components/BulkEnrollStudentsDialog';
import { TurnusCreateButton } from './components/CreateTurnusDialog';
import StudentDiscountsPanel from '@/components/students/StudentDiscountsPanel';
import GuardianBillingPreview from '@/components/billing/GuardianBillingPreview';

// Importierte Fächer: Fach vs. Form — siehe subjectBaseName in ./utils

const UV_FETCH_CHUNK = 500;
const UV_PAGE_SIZE_OPTIONS = [50, 100, 200, 500];
const UV_RESTORE_KEY = 'mamv.uv.restore';
const HONORARIUM_LABELS = {
  per_lesson: 'je Unterrichtseinheit',
  per_month: 'pro Monat',
  percent: '% vom Umsatz',
  one_time: 'einmalig',
};

function formatHonorarium(row) {
  const value = Number(row?.amount || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return row?.kind === 'percent'
    ? `${value} % vom Umsatz`
    : `${value} € ${HONORARIUM_LABELS[row?.kind] || ''}`.trim();
}

export function teacherWeekCalendarUrl(teacherId, referenceDate = null) {
  const params = new URLSearchParams({
    view: 'teachers-week',
    teacher_id: teacherId || '',
    date: referenceDate || new Date().toISOString().slice(0, 10),
  });
  return `/calendar?${params.toString()}`;
}

function saveCoursePeriodInPlace(courseId, periodId, payload) {
  const path = `/courses/${courseId}/periods/${periodId || 'live'}`;
  return retryOnCollision(
    () => api.put(path, payload),
    () => api.put(path, { ...payload, allow_collisions: true }),
  );
}

function saveCoursePeriodWithCollisionWarning(courseId, payload) {
  return retryOnCollision(
    () => api.post(`/courses/${courseId}/periods`, payload),
    () => api.post(`/courses/${courseId}/periods`, { ...payload, allow_collisions: true }),
  );
}

function uvRestoreKey() {
  const tenantId = localStorage.getItem('active_tenant_id');
  return tenantId ? `${UV_RESTORE_KEY}.${tenantId}` : UV_RESTORE_KEY;
}

function readUvRestore() {
  try {
    const raw = sessionStorage.getItem(uvRestoreKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function selectedRecordFrom(enrollment, courseRecord, studentRecord, guardianRecord) {
  if (!enrollment?.id) return null;
  return {
    enrollmentId: enrollment.id,
    studentId: enrollment.student_id || studentRecord?.id || null,
    courseId: enrollment.course_id || courseRecord?.id || null,
    teacherId: courseRecord?.teacher_id || null,
    guardianId: guardianRecord?.id || null,
    familyId: studentRecord?.family_id || null,
    locationId: courseRecord?.location_id || null,
    dayOfWeek: courseRecord?.day_of_week ?? enrollment.course_data?.day_of_week ?? null,
    rhythm: courseRecord?.rhythm || null,
    startDate: enrollment.start_date || null,
    startTime: courseRecord?.start_time || enrollment.course_data?.start_time || null,
  };
}

function writeUvRestore(payload) {
  try {
    const key = uvRestoreKey();
    if (!payload?.enrollmentId) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

/** Erste Belegung je Kurs, in der Reihenfolge der Trefferliste. */
function distinctCourseEnrollments(enrollments) {
  const seen = new Set();
  const out = [];
  for (const enrollment of enrollments || []) {
    const key = enrollment.course_id || enrollment.course?.id || enrollment.id;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(enrollment);
  }
  return out;
}

function mapEnrollmentItems(items) {
  return (items || []).map((item) => ({
    ...item,
    course_data: item.course
      ? {
          id: item.course.id,
          name: item.course.name,
          subject_name: item.course.subject_name,
          day_of_week: item.course.day_of_week,
          duration_minutes: item.course.duration_minutes,
          start_time: item.course.start_time,
        }
      : null,
  }));
}

/** Strip pagination/control keys so callers cannot override page/per_page. */
function stripEnrollmentControlParams(extraParams = {}) {
  const out = { ...extraParams };
  delete out.page;
  delete out.per_page;
  delete out.mode;
  delete out.pageSize;
  delete out.sort_by;
  delete out.sort_dir;
  delete out._keepEnrollmentId;
  delete out._quiet;
  delete out._keepPreviousOnEmpty;
  return out;
}

/**
 * Für listQueryRef: Steuerungs-Keys + Fußleisten-Filter entfernen
 * (Fußleiste wird beim API-Aufruf separat wieder angehängt).
 * `status` gehört zur Suche (Belegungsstatus), nicht zur Fußleiste.
 */
function sanitizeEnrollmentParams(extraParams = {}) {
  const out = stripEnrollmentControlParams(extraParams);
  delete out.period_start;
  delete out.period_end;
  delete out.active_only;
  delete out.active_on;
  delete out.gkz_group_id;
  delete out.gkz;
  return out;
}

/**
 * mode 'all'  = alle Treffer nachladen (Chunks à UV_FETCH_CHUNK)
 * mode 'paged' = genau eine Seite mit pageSize
 * sort_by/sort_dir werden serverseitig VOR Pagination angewendet
 */
async function fetchEnrollmentList(apiClient, {
  mode = 'all',
  page = 1,
  pageSize = 100,
  sort_by = 'name',
  sort_dir = 'asc',
  ...extraParams
}) {
  // Fußleisten-Filter (active_only / Zeitraum / Aktiv ab) müssen an die API durchgereicht werden
  const filters = stripEnrollmentControlParams(extraParams);
  const sortParams = {
    sort_by: sort_by || 'name',
    sort_dir: (sort_dir || 'asc') === 'desc' ? 'desc' : 'asc',
  };

  if (mode === 'paged') {
    const size = Math.min(Math.max(1, Number(pageSize) || 100), 500);
    const response = await apiClient.get('/enrollments', {
      params: {
        ...filters,
        ...sortParams,
        page: Math.max(1, Number(page) || 1),
        per_page: size,
      },
    });
    return {
      items: mapEnrollmentItems(response.data.items),
      total: response.data.total || 0,
    };
  }

  // Alle Treffer: Chunks, gleiche Sortierung auf dem Server
  let items = [];
  let total = null;
  let p = 1;
  const maxPages = 100;
  while (p <= maxPages) {
    const response = await apiClient.get('/enrollments', {
      params: {
        ...filters,
        ...sortParams,
        page: p,
        per_page: UV_FETCH_CHUNK,
      },
    });
    const batch = mapEnrollmentItems(response.data.items);
    if (total == null) total = response.data.total || 0;
    items = items.concat(batch);
    if (!batch.length || items.length >= total) break;
    p += 1;
  }
  return { items, total: total || items.length };
}

// Veranstaltungen-Tab (FileMaker-Layout) Helpers
const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WEEKDAY_OPTIONS = [
  { value: '0', label: 'Montag' },
  { value: '1', label: 'Dienstag' },
  { value: '2', label: 'Mittwoch' },
  { value: '3', label: 'Donnerstag' },
  { value: '4', label: 'Freitag' },
  { value: '5', label: 'Samstag' },
  { value: '6', label: 'Sonntag' },
];
const DURATION_OPTIONS = [
  { value: '30', label: '30 Min.' },
  { value: '45', label: '45 Min.' },
  { value: '60', label: '60 Min.' },
  { value: '90', label: '90 Min.' },
];
const AUSFALL_ACTIONS = [
  { code: 'ea', label: 'EA – Teilnehmer entschuldigt' },
  { code: 'ua', label: 'UA – unentschuldigt, gilt als gehalten' },
  { code: 'la', label: 'LA – Lehrkraft abwesend' },
];
const addMinutesToTime = (t, mins) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const total = h * 60 + m + (Number(mins) || 0);
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};
const ageFromDob = (dob) => {
  if (!dob) return null;
  const b = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) age -= 1;
  return age;
};
const rhythmToTurnus = (r) => {
  const kind = parseCourseRhythm(r);
  if (kind === 'weekly') return '7';
  if (kind === 'biweekly') return '14';
  if (kind === 'individual') return 'ind.';
  return r || '—';
};

const _normId = (v) => (v == null || v === '' ? '' : String(v));
const _normTime = (v) => (v == null || v === '' ? '' : String(v).slice(0, 5));

function courseTemporalChanged(edited, course) {
  if (!edited || !course) return false;
  return (
    _normId(edited.teacher_id) !== _normId(course.teacher_id)
    || _normId(edited.day_of_week) !== _normId(course.day_of_week)
    || _normTime(edited.start_time) !== _normTime(course.start_time)
    || _normId(edited.duration_minutes) !== _normId(course.duration_minutes)
    || (edited.rhythm || 'weekly') !== (course.rhythm || 'weekly')
    || _normId(edited.room_id) !== _normId(course.room_id)
  );
}

function bankFieldsChanged(edited, current) {
  if (!edited || !current) return true;
  const iban = (edited.bank_iban || '').replace(/\s+/g, '').toUpperCase();
  const curIban = (current.bank_iban || '').replace(/\s+/g, '').toUpperCase();
  return (
    iban !== curIban
    || (edited.bank_account_holder || '') !== (current.bank_account_holder || '')
    || (edited.bank_name || '') !== (current.bank_name || '')
    || (edited.bank_bic || '') !== (current.bank_bic || '')
    || (edited.sepa_mandate_reference || '') !== (current.sepa_mandate_reference || '')
    || String(edited.sepa_debit_day || '') !== String(current.sepa_debit_day || '')
  );
}

const OVERVIEW_COLUMNS = [
  { key: 'name', label: 'Schüler:in', visible: true },
  { key: 'dob', label: 'Geburtstag', visible: true },
  { key: 'course', label: 'Fach', visible: true },
  { key: 'dauer', label: 'Dauer', visible: true },
  { key: 'tag', label: 'Tag', visible: true },
  { key: 'zeit', label: 'Unterrichtszeit', visible: true },
  { key: 'raum', label: 'Standort/Raum', visible: true },
  { key: 'areal', label: 'Areal', visible: true },
  { key: 'eintritt', label: 'Vertragsbeginn', visible: true },
  { key: 'austritt', label: 'Vertragsende', visible: true },
  { key: 'status', label: 'Status', visible: true },
];

const overviewEnrollmentFields = (e) => {
  const student = e.student || e.student_data || null;
  const course = e.course || e.course_data || (e.course_name ? { name: e.course_name } : null);
  const room = course?.room_name || course?.room || e.room || null;
  const location = course?.location_name || e.location || null;
  const ortRaum = [location, room].filter(Boolean).join(' / ') || null;
  const areal = student?.areal || course?.region || e.areal || null;
  return { student, course, ortRaum, areal };
};

const FmField = ({ label, value, className = '', testId, trailing }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <span className="text-[10px] text-slate-500 w-24 shrink-0 text-right">{label}</span>
    <span data-testid={testId} className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1 min-h-[26px] leading-tight font-medium text-slate-800">
      {formatDisplayValue(value) || value || '—'}
    </span>
    {trailing}
  </div>
);

const rowCellInput = 'h-7 w-full min-w-0 text-xs border border-slate-300 rounded px-1 bg-white';

function entryNameMatches(query, student) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const last = String(student?.last_name || '').toLowerCase();
  const first = String(student?.first_name || '').toLowerCase();
  const hay = `${last} ${first} ${first} ${last}`;
  return q.split(/\s+/).filter(Boolean).every((word) => hay.includes(word));
}

function dayAfterIso(iso) {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

function RowSaveCancel({ saveTestId, onSave, onCancel, onSaveAndNew, saveAndNewTestId }) {
  return (
    <div className="flex justify-end items-center gap-1">
      <button
        type="button"
        data-testid={saveTestId}
        title="Speichern"
        aria-label="Speichern"
        onClick={() => onSave()}
        className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
      >
        <Save className="w-3.5 h-3.5" />
      </button>
      {onSaveAndNew ? (
        <button
          type="button"
          data-testid={saveAndNewTestId}
          title="Speichern & Neu"
          aria-label="Speichern & Neu"
          onClick={() => onSaveAndNew()}
          className="px-1.5 py-1 text-[11px] leading-none font-medium text-emerald-700 hover:bg-emerald-50 rounded whitespace-nowrap inline-flex items-center gap-1"
        >
          <Save className="w-3.5 h-3.5" />
          & Neu
        </button>
      ) : null}
      <button
        type="button"
        data-testid="vst-row-cancel"
        title="Abbrechen"
        aria-label="Abbrechen"
        onClick={() => onCancel()}
        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function RecordPencil({ testId, title, onClick }) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      aria-label={title}
      onClick={(ev) => {
        ev.stopPropagation();
        onClick();
      }}
      className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded shrink-0"
    >
      <Pencil className="w-3.5 h-3.5" />
    </button>
  );
}

export default function Unterrichtsverwaltung() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeTenant } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const registrationPrefillHandled = useRef(false);
  const registrationPrefillPending = useRef(
    Boolean(location.state?.fromRegistration || hasEnrollmentPrefill()),
  ).current;
  const initialRestore = useRef(registrationPrefillPending ? null : readUvRestore()).current;
  
  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [student, setStudent] = useState(null);
  const [course, setCourse] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [payerAccount, setPayerAccount] = useState(null);
  const [payerAccountLoading, setPayerAccountLoading] = useState(false);
  const [showBankIban, setShowBankIban] = useState(false);
  const { quicklinks, save: saveQuicklinks } = useQuicklinks();
  const [quicklinksOpen, setQuicklinksOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const [fieldQuery, setFieldQuery] = useState(() => initialRestore?.fieldQuery || {});
  const [searchActive, setSearchActive] = useState(() => Boolean(initialRestore?.searchActive));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  /** 'all' = alle Treffer laden; 'paged' = Seitenweise mit wählbarer Größe */
  const [listMode, setListMode] = useState('all');
  const [pageSize, setPageSize] = useState(100);
  const [listPage, setListPage] = useState(1);
  /** Zusätzliche Filter (Suche/Lehrer/…) — bleiben beim Seitenwechsel erhalten */
  const listQueryRef = useRef(initialRestore?.listQuery || {});
  const [activeSection, setActiveSection] = useState('participant');
  const [activeTab, setActiveTab] = useState(() => initialRestore?.tab || 'enrollments');

  // iMikel sub-tabs per main tab
  const [vstSub, setVstSub] = useState('uebersicht');     // Veranstaltungen sub-tab
  const [vstLehrSub, setVstLehrSub] = useState('lehrkraft'); // Lehrkraft/Honorar/Instrument sub-table
  const [courseEnrollments, setCourseEnrollments] = useState([]);
  const [coursePeriods, setCoursePeriods] = useState([]);
  const [courseHonoraria, setCourseHonoraria] = useState([]);
  const [honorariumDraft, setHonorariumDraft] = useState(null);
  const [vstEntryQuery, setVstEntryQuery] = useState('');
  const [effectiveDialog, setEffectiveDialog] = useState(null);
  const [editingEvent, setEditingEvent] = useState(false);
  const [inlineRow, setInlineRow] = useState(null);
  const [editedEvent, setEditedEvent] = useState(null);

  // Fußleiste (global in Layout): Zeitraum / Nur Aktive / Aktiv ab (wie iMikel)
  const [footer, setFooter] = useState(getFooterSettings);
  const footerMounted = useRef(false);
  const selectedEnrollmentIdRef = useRef(initialRestore?.enrollmentId || null);
  const searchModeRef = useRef(false);
  const searchActiveRef = useRef(Boolean(initialRestore?.searchActive));
  /** Letzte erfolgreiche Feldsuche — Fußleiste darf sie nicht mit student_id vermischen */
  const fieldSearchParamsRef = useRef(initialRestore?.fieldQuery || null);
  const restoreNeedsSelectRef = useRef(Boolean(initialRestore?.enrollmentId));

  const publishedSelectionRef = useRef(false);
  useEffect(() => {
    if (selectedEnrollment?.id) {
      publishedSelectionRef.current = true;
      selectedEnrollmentIdRef.current = selectedEnrollment.id;
      writeSelectedRecord(selectedRecordFrom(selectedEnrollment, course, student, guardian));
      writeUvRestore({
        enrollmentId: selectedEnrollment.id,
        tab: activeTab,
        listQuery: listQueryRef.current,
        fieldQuery: fieldSearchParamsRef.current || fieldQuery,
        searchActive,
      });
      return;
    }
    if (publishedSelectionRef.current) {
      publishedSelectionRef.current = false;
      writeSelectedRecord(null);
    }
  }, [selectedEnrollment, course, student, guardian, activeTab, searchActive, fieldQuery]);

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  useEffect(() => {
    searchActiveRef.current = searchActive;
  }, [searchActive]);

  useEffect(() => {
    const h = (e) => setFooter(e.detail);
    window.addEventListener('footer-settings-changed', h);
    return () => window.removeEventListener('footer-settings-changed', h);
  }, []);

  const footerParams = footerToParams;
  const visibleCourseEnrollments = courseEnrollments.filter((entry) => enrollmentVisibleInFooter(entry, footer));

  useEffect(() => {
    if (!footerMounted.current) { footerMounted.current = true; return; }

    // Suchkriterien werden gerade eingegeben → Liste/UI nicht anfassen.
    // Fußleisten-Einstellungen gelten beim nächsten „Suchen“.
    if (searchModeRef.current) return;

    // Aktive Feldsuche: Suchkriterien + Fußleiste — Treffer außerhalb der Filter verschwinden.
    if (fieldSearchParamsRef.current || searchActiveRef.current) {
      const params = { ...(fieldSearchParamsRef.current || listQueryRef.current) };
      listQueryRef.current = params;
      loadEnrollments({
        ...footerParams(footer),
        ...params,
        _keepEnrollmentId: selectedEnrollmentIdRef.current,
        _quiet: true,
      });
      return;
    }

    // Eine bestehende Eingrenzung (Suche, Schülerauswahl) bleibt in listQueryRef stehen und
    // wird von loadEnrollments weiterverwendet. Ohne Eingrenzung bleibt die Liste vollständig —
    // der offene Datensatz wird über _keepEnrollmentId gehalten, nicht über einen student_id-Filter.
    loadEnrollments({
      ...footerParams(footer),
      _keepEnrollmentId: selectedEnrollmentIdRef.current,
      _quiet: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footer]);

  useEffect(() => {
    if (!guardian?.id) {
      setPayerAccount(null);
      setPayerAccountLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPayerAccountLoading(true);
    api.get(`/guardians/${guardian.id}/account`)
      .then((r) => {
        if (!cancelled) setPayerAccount(r.data);
      })
      .catch(() => {
        if (!cancelled) setPayerAccount(null);
      })
      .finally(() => {
        if (!cancelled) setPayerAccountLoading(false);
      });
    return () => { cancelled = true; };
  }, [guardian?.id]);

  const [abrSub, setAbrSub] = useState('betraege');       // Abrechnung sub-tab
  const [ubsSub, setUbsSub] = useState('belegungen');     // Übersicht sub-tab
  const [overviewSort, setOverviewSort] = useState({ key: 'name', dir: 'asc' });
  const {
    columns: overviewColumns,
    setColumns: setOverviewColumns,
    isVisible: overviewColVisible,
  } = useTableColumns('uv-uebersicht', OVERVIEW_COLUMNS);
  const overviewVisibleCols = useMemo(
    () => overviewColumns.filter((c) => c.visible !== false),
    [overviewColumns],
  );
  const [abrechnung, setAbrechnung] = useState(null);     // /enrollments/{id}/abrechnung
  const [abrLoading, setAbrLoading] = useState(false);
  const [abrYear, setAbrYear] = useState(new Date().getFullYear());
  const [cancellations, setCancellations] = useState([]);
  const [cancEditor, setCancEditor] = useState(null);
  const [cancSaving, setCancSaving] = useState(false);
  // An-/Abwesenheits-Tab (iMikel)
  const [attendance, setAttendance] = useState([]);
  const [attendanceFilter, setAttendanceFilter] = useState('all');
  const [attEditor, setAttEditor] = useState(null);
  const [ausfallRow, setAusfallRow] = useState(null);
  const [moveRow, setMoveRow] = useState(null);
  const [moveDate, setMoveDate] = useState('');
  const [attSaving, setAttSaving] = useState(false);
  const [attCorrecting, setAttCorrecting] = useState(null);
  
  // Master data for dropdowns
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [suggestFocus, setSuggestFocus] = useState(null);
  
  // Edit states
  const [editingStudent, setEditingStudent] = useState(false);
  const [editingEnrollment, setEditingEnrollment] = useState(false);
  const [editingPayer, setEditingPayer] = useState(false);
  const [editingCustodian, setEditingCustodian] = useState(false);
  const [personTab, setPersonTab] = useState('payer');
  const [custodianAssignOpen, setCustodianAssignOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [editedStudent, setEditedStudent] = useState(null);
  const [editedEnrollment, setEditedEnrollment] = useState(null);
  const [editedGuardian, setEditedGuardian] = useState(null);
  const [editedCustodian, setEditedCustodian] = useState(null);
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showNewEnrollmentDialog, setShowNewEnrollmentDialog] = useState(false);
  const [enrollmentDialogPrefill, setEnrollmentDialogPrefill] = useState(null);
  const [enrollmentDialogVariant, setEnrollmentDialogVariant] = useState('belegung'); // 'belegung' | 'neuerfassung'
  const [showBulkEnrollDialog, setShowBulkEnrollDialog] = useState(false);
  
  // Live search
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Multi-select states
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkEventOpen, setBulkEventOpen] = useState(false);
  const [bulkEvent, setBulkEvent] = useState({
    teacher_id: '',
    day_of_week: '',
    start_time: '',
    duration_minutes: '',
    rhythm: '',
    room_id: '',
    location_id: '',
    valid_from: '',
  });
  
  // Toggle selection of an enrollment
  const toggleSelection = (enrollmentId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(enrollmentId)) {
        newSet.delete(enrollmentId);
      } else {
        newSet.add(enrollmentId);
      }
      return newSet;
    });
  };
  
  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedIds.size === enrollments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(enrollments.map(e => e.id)));
    }
  };
  
  // Bulk status change
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.size === 0) return;
    
    setBulkActionLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const enrollmentId of selectedIds) {
      const enrollment = enrollments.find(e => e.id === enrollmentId);
      if (!enrollment) continue;
      
      try {
        await api.put(`/courses/${enrollment.course_id}/enrollments/${enrollmentId}`, {
          status: newStatus
        });
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    setBulkActionLoading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} Belegung(en) auf "${newStatus === 'active' ? 'Aktiv' : newStatus === 'cancelled' ? 'Gekündigt' : 'Pausiert'}" gesetzt`);
      
      // Update local state
      setEnrollments(prev => prev.map(e => 
        selectedIds.has(e.id) ? { ...e, status: newStatus } : e
      ));
      
      // Update selected enrollment if it was changed
      if (selectedEnrollment && selectedIds.has(selectedEnrollment.id)) {
        setSelectedEnrollment(prev => ({ ...prev, status: newStatus }));
        setEditedEnrollment(prev => prev ? { ...prev, status: newStatus } : prev);
      }
    }
    
    if (errorCount > 0) {
      toast.error(`${errorCount} Belegung(en) konnten nicht geändert werden`);
    }
    
    setSelectedIds(new Set());
    setShowBulkActions(false);
  };
  
  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    setBulkActionLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const enrollmentId of selectedIds) {
      const enrollment = enrollments.find(e => e.id === enrollmentId);
      if (!enrollment) continue;
      
      try {
        await api.delete(`/courses/${enrollment.course_id}/enrollments/${enrollmentId}`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    setBulkActionLoading(false);
    setShowBulkDeleteDialog(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} Belegung(en) gelöscht`);
      
      // Remove deleted enrollments from state
      const newEnrollments = enrollments.filter(e => !selectedIds.has(e.id));
      setEnrollments(newEnrollments);
      setTotalCount(newEnrollments.length);
      
      // Select first available if current was deleted
      if (selectedEnrollment && selectedIds.has(selectedEnrollment.id)) {
        if (newEnrollments.length > 0) {
          selectEnrollment(newEnrollments[0], 0);
        } else {
          setSelectedEnrollment(null);
          setCourse(null);
          setStudent(null);
        }
      }
    }
    
    if (errorCount > 0) {
      toast.error(`${errorCount} Belegung(en) konnten nicht gelöscht werden`);
    }
    
    setSelectedIds(new Set());
    setShowBulkActions(false);
  };

  const selectedBulkEnrollments = enrollments.filter((e) => selectedIds.has(e.id));
  const bulkCourseIds = [...new Set(selectedBulkEnrollments.map((e) => e.course_id).filter(Boolean))];
  const bulkChangeLabels = () => {
    const labels = [];
    if (bulkEvent.teacher_id) labels.push('Lehrkraft');
    if (bulkEvent.day_of_week !== '' && bulkEvent.day_of_week != null) labels.push('Termin');
    if (bulkEvent.start_time) labels.push('Zeit');
    if (bulkEvent.duration_minutes) labels.push('Dauer');
    if (bulkEvent.rhythm) labels.push('Turnus');
    if (bulkEvent.room_id) labels.push('Raum');
    return labels;
  };

  const submitBulkEvent = async () => {
    const labels = bulkChangeLabels();
    if (!labels.length || !bulkEvent.valid_from || bulkCourseIds.length === 0) return;
    setBulkActionLoading(true);
    const body = { valid_from: bulkEvent.valid_from };
    if (bulkEvent.teacher_id) body.teacher_id = bulkEvent.teacher_id;
    if (bulkEvent.day_of_week !== '' && bulkEvent.day_of_week != null) {
      body.day_of_week = parseInt(bulkEvent.day_of_week, 10);
    }
    if (bulkEvent.start_time) body.start_time = bulkEvent.start_time;
    if (bulkEvent.duration_minutes) body.duration_minutes = parseInt(bulkEvent.duration_minutes, 10);
    if (bulkEvent.rhythm) body.rhythm = bulkEvent.rhythm;
    if (bulkEvent.room_id) body.room_id = bulkEvent.room_id;
    let errors = 0;
    for (const courseId of bulkCourseIds) {
      try {
        const saved = await saveCoursePeriodWithCollisionWarning(courseId, body);
        if (!saved) errors += 1;
      } catch {
        errors += 1;
      }
    }
    setBulkActionLoading(false);
    if (errors === 0) {
      toast.success(`${bulkCourseIds.length} Veranstaltung(en) ab ${formatDate(bulkEvent.valid_from)} geändert`);
      setBulkEventOpen(false);
      if (course?.id && bulkCourseIds.includes(course.id)) {
        try {
          const courseRes = await api.get(`/courses/${course.id}`);
          setCourse(courseRes.data);
        } catch { /* Anzeige bleibt, bis die Belegung neu geladen wird */ }
      }
    } else {
      toast.error(`${errors} Veranstaltung(en) konnten nicht geändert werden`);
    }
  };

  // Lazy-load abrechnung breakdown when the user opens the Abrechnung tab
  useEffect(() => {
    if (activeTab !== 'billing' || !selectedEnrollment?.id) return;
    setAbrLoading(true);
    api.get(`/enrollments/${selectedEnrollment.id}/abrechnung?months=12&start_year=${abrYear}`)
      .then((r) => setAbrechnung(r.data))
      .catch(() => setAbrechnung(null))
      .finally(() => setAbrLoading(false));
  }, [activeTab, selectedEnrollment?.id, abrYear]);

  // Load all participants of the course when the Veranstaltungen tab is open
  useEffect(() => {
    if (activeTab !== 'events' || !course?.id) { setCourseEnrollments([]); return; }
    api.get(`/courses/${course.id}/enrollments`)
      .then((r) => setCourseEnrollments(r.data || []))
      .catch(() => setCourseEnrollments([]));
  }, [activeTab, course?.id]);

  useEffect(() => {
    if (!course?.id) { setCoursePeriods([]); return; }
    api.get(`/courses/${course.id}/periods`)
      .then((r) => setCoursePeriods(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCoursePeriods([]));
  }, [course?.id]);

  useEffect(() => {
    if (!course?.id) { setCourseHonoraria([]); return; }
    api.get(`/courses/${course.id}/honoraria`)
      .then((r) => setCourseHonoraria(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCourseHonoraria([]));
  }, [course?.id]);

  // Load cancellations whenever Ausfall sub-tab becomes visible
  useEffect(() => {
    if (activeTab !== 'billing' || abrSub !== 'ausfall' || !selectedEnrollment?.id) return;
    api.get(`/enrollments/${selectedEnrollment.id}/cancellations`)
      .then((r) => setCancellations(r.data))
      .catch(() => setCancellations([]));
  }, [activeTab, abrSub, selectedEnrollment?.id]);

  const reloadCancellations = async () => {
    if (!selectedEnrollment?.id) return;
    try {
      const r = await api.get(`/enrollments/${selectedEnrollment.id}/cancellations`);
      setCancellations(r.data);
      // Trigger re-load of abrechnung so the Rückerstattungen-row updates
      const r2 = await api.get(`/enrollments/${selectedEnrollment.id}/abrechnung?months=12&start_year=${abrYear}`);
      setAbrechnung(r2.data);
    } catch { /* ignore */ }
  };

  const saveCancellation = async () => {
    if (!cancEditor || !selectedEnrollment?.id) return;
    setCancSaving(true);
    try {
      const body = {
        cancelled_date: cancEditor.cancelled_date,
        category: cancEditor.category,
        notes: cancEditor.notes || '',
        erteilt: !!cancEditor.erteilt,
        erstatten_quotient: parseFloat(cancEditor.erstatten_quotient) || 0,
        erstatten_amount: cancEditor.erstatten_amount === '' || cancEditor.erstatten_amount == null
          ? null
          : parseFloat(cancEditor.erstatten_amount),
      };
      if (cancEditor.id) {
        await api.patch(`/cancellations/${cancEditor.id}`, body);
      } else {
        await api.post(`/enrollments/${selectedEnrollment.id}/cancellations`, body);
      }
      toast.success('Ausfall gespeichert');
      setCancEditor(null);
      await reloadCancellations();
    } catch (err) {
      toast.error(formatApiError(err, 'Speichern fehlgeschlagen'));
    } finally {
      setCancSaving(false);
    }
  };

  const deleteCancellation = async (cid) => {
    if (!window.confirm('Diesen Ausfall wirklich löschen?')) return;
    try {
      await api.delete(`/cancellations/${cid}`);
      toast.success('Ausfall gelöscht');
      await reloadCancellations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const attendanceQuery = () => {
    const params = { status_filter: attendanceFilter, year: abrYear };
    if (footer.zeitraumOn) {
      if (footer.von) params.period_start = footer.von;
      if (footer.bis) params.period_end = footer.bis;
    }
    return params;
  };

  // ---------- Attendance (An-/Abwesenheiten) ----------
  useEffect(() => {
    if (activeTab !== 'billing' || abrSub !== 'anwesenheit' || !selectedEnrollment?.id) return;
    api.get(`/enrollments/${selectedEnrollment.id}/attendance`, { params: attendanceQuery() })
      .then((r) => setAttendance(r.data))
      .catch(() => setAttendance([]));
  }, [activeTab, abrSub, selectedEnrollment?.id, attendanceFilter, abrYear, footer.zeitraumOn, footer.von, footer.bis]);

  const reloadAttendance = async () => {
    if (!selectedEnrollment?.id) return;
    try {
      const r = await api.get(`/enrollments/${selectedEnrollment.id}/attendance`, {
        params: attendanceQuery(),
      });
      setAttendance(r.data);
    } catch { /* ignore */ }
  };

  const saveAttendance = async () => {
    if (!attEditor || !selectedEnrollment?.id) return;
    setAttSaving(true);
    try {
      const body = {
        lesson_date: attEditor.lesson_date,
        relocated_to_date: attEditor.relocated_to_date || null,
        erteilt: !!attEditor.erteilt,
        online_flag: !!attEditor.online_flag,
        notes: attEditor.notes || '',
      };
      await api.post(`/enrollments/${selectedEnrollment.id}/attendance`, body);
      toast.success('Termin gespeichert');
      setAttEditor(null);
      await reloadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setAttSaving(false);
    }
  };

  const toggleAttendanceField = async (row, field, value) => {
    const rowKey = row.lesson_event_id || `planned-${row.lesson_date}`;
    let correctionReason = null;
    if (row.is_finalized) {
      if (attCorrecting?.rowKey !== rowKey || !attCorrecting?.reason) {
        toast.info('Finalisiert — zuerst „korrigieren“ mit Begründung wählen');
        return;
      }
      correctionReason = attCorrecting.reason;
    }
    try {
      // Virtuelle Turnus-Zeilen (noch kein LessonEvent) → wie Lehrerliste materialisieren
      if (!row.lesson_event_id) {
        await api.post(`/enrollments/${selectedEnrollment.id}/attendance`, {
          lesson_date: row.lesson_date,
          erteilt: field === 'erteilt' ? value : !!row.erteilt,
          online_flag: field === 'online_flag' ? value : !!row.online_flag,
          notes: row.notes || '',
          relocated_to_date: row.relocated_to_date || null,
        });
      } else {
        const body = { [field]: value };
        if (correctionReason) body.correction_reason = correctionReason;
        await api.patch(
          `/attendance/${row.lesson_event_id}/by-enrollment/${selectedEnrollment.id}`,
          body
        );
      }
      setAttCorrecting(null);
      await reloadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update fehlgeschlagen');
    }
  };

  const saveAttendanceNotes = async (row, notes) => {
    const text = (notes || '').trim();
    if (text === (row.notes || '')) return;
    if (!selectedEnrollment?.id) return;
    try {
      if (!row.lesson_event_id) {
        await api.post(`/enrollments/${selectedEnrollment.id}/attendance`, {
          lesson_date: row.lesson_date,
          erteilt: !!row.erteilt,
          online_flag: !!row.online_flag,
          notes: text,
        });
      } else {
        const body = { notes: text };
        if (row.is_finalized && attCorrecting?.reason) body.correction_reason = attCorrecting.reason;
        await api.patch(
          `/attendance/${row.lesson_event_id}/by-enrollment/${selectedEnrollment.id}`,
          body,
        );
      }
      await reloadAttendance();
    } catch (err) {
      toast.error(formatApiError(err, 'Bemerkung konnte nicht gespeichert werden'));
    }
  };

  const applyAusfall = async (row, code) => {
    if (!row || !selectedEnrollment?.course_id) return;
    try {
      await api.post(`/attendance-lists/courses/${selectedEnrollment.course_id}/mark`, {
        lesson_date: String(row.lesson_date).slice(0, 10),
        student_id: selectedEnrollment.student_id,
        category_code: code,
      });
      toast.success('Ausfall erfasst');
      setAusfallRow(null);
      await reloadAttendance();
    } catch (err) {
      toast.error(formatApiError(err, 'Ausfall konnte nicht erfasst werden'));
    }
  };

  const lessonEndTime = (start, durationMinutes) => {
    const [hh, mm] = String(start || '15:00').slice(0, 5).split(':').map((n) => parseInt(n, 10) || 0);
    const total = hh * 60 + mm + (parseInt(durationMinutes, 10) || 45);
    const eh = String(Math.floor(total / 60) % 24).padStart(2, '0');
    const em = String(total % 60).padStart(2, '0');
    return `${eh}:${em}`;
  };

  const submitMove = async () => {
    if (!moveRow || !moveDate || !selectedEnrollment?.id) return;
    try {
      let lessonId = moveRow.lesson_event_id;
      if (!lessonId) {
        const created = await api.post(`/enrollments/${selectedEnrollment.id}/attendance`, {
          lesson_date: String(moveRow.lesson_date).slice(0, 10),
          erteilt: !!moveRow.erteilt,
          online_flag: !!moveRow.online_flag,
          notes: moveRow.notes || '',
        });
        lessonId = created.data?.lesson_event_id;
      }
      if (!lessonId) {
        toast.error('Termin konnte nicht angelegt werden');
        return;
      }
      const start = String(course?.start_time || '15:00').slice(0, 5);
      await api.post(`/lessons/${lessonId}/relocate`, {
        date: moveDate,
        start_time: start,
        end_time: lessonEndTime(start, course?.duration_minutes),
      });
      toast.success('Termin verlegt');
      setMoveRow(null);
      setMoveDate('');
      await reloadAttendance();
    } catch (err) {
      toast.error(formatApiError(err, 'Verlegen fehlgeschlagen'));
    }
  };

  const finalizeAttendance = async (row) => {
    try {
      if (!row.lesson_event_id || !row.id) {
        const r = await api.post(`/enrollments/${selectedEnrollment.id}/attendance`, {
          lesson_date: row.lesson_date,
          erteilt: !!row.erteilt,
          online_flag: !!row.online_flag,
          notes: row.notes || '',
        });
        await api.post(`/attendance/${r.data.id}/finalize`);
      } else if (!row.id) {
        const r = await api.patch(
          `/attendance/${row.lesson_event_id}/by-enrollment/${selectedEnrollment.id}`,
          { erteilt: row.erteilt }
        );
        await api.post(`/attendance/${r.data.id}/finalize`);
      } else {
        await api.post(`/attendance/${row.id}/finalize`);
      }
      toast.success('Finalisiert');
      await reloadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Finalisieren fehlgeschlagen');
    }
  };

  const unlockAttendance = async (row) => {
    const reason = window.prompt('Begründung für das Wiederöffnen des finalisierten Termins:');
    if (!reason?.trim()) return;
    try {
      await api.post(`/attendance/${row.id}/unlock`, { reason: reason.trim() });
      setAttCorrecting(null);
      toast.success('Termin wieder geöffnet');
      await reloadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Entsperren fehlgeschlagen');
    }
  };

  const finalizeCapturedAttendance = async () => {
    if (!selectedEnrollment?.id) return;
    if (!window.confirm('Alle erfassten Einträge dieses Jahres finalisieren? Offene Termine bleiben offen.')) return;
    try {
      const r = await api.post(
        `/enrollments/${selectedEnrollment.id}/attendance/finalize-captured`,
        {},
        { params: attendanceQuery() },
      );
      const n = r.data?.finalized || 0;
      toast.success(n ? `${n} Einträge finalisiert` : 'Keine erfassten Einträge zum Finalisieren');
      if (r.data?.skipped_open) toast.info(`${r.data.skipped_open} offene Termine bleiben offen`);
      await reloadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Finalisieren fehlgeschlagen');
    }
  };

  // Load master data (Subjects, Teachers, Locations, Tariffs) for dropdowns
  const loadMasterData = useCallback(async () => {
    try {
      const [subjectsRes, teachersRes, locationsRes, tariffsRes, roomsRes] = await Promise.all([
        api.get('/subjects').catch(() => ({ data: [] })),
        api.get('/teachers').catch(() => ({ data: [] })),
        api.get('/locations').catch(() => ({ data: [] })),
        api.get('/tariffs-discounts/tariffs').catch(() => ({ data: [] })),
        api.get('/locations/rooms').catch(() => api.get('/rooms').catch(() => ({ data: [] }))),
      ]);
      
      setSubjects(subjectsRes.data.map(s => ({ value: s.id, label: s.name })));
      setTeachers(selectableTeachers(teachersRes.data).map(t => ({
        value: t.id,
        label: `${t.first_name} ${t.last_name}`,
        searchText: `${t.last_name} ${t.first_name} ${t.last_name}, ${t.first_name}`,
        suggestLabel: `${t.last_name}, ${t.first_name}`,
      })));
      setLocations(locationsRes.data.map(l => ({
        value: l.id,
        label: l.name,
        searchText: [l.search_term, l.short_code, l.city].filter(Boolean).join(' '),
      })));
      const roomRows = Array.isArray(roomsRes.data) ? roomsRes.data : roomsRes.data?.items || [];
      setRooms(roomRows.map((r) => ({
        value: r.id,
        label: r.name,
        location: r.location_name || r.location?.name || '',
        locationId: r.location_id || r.location?.id || '',
      })));
      setTariffs(tariffsRes.data.map(t => ({
        value: t.id,
        label: tariffFormName(t.name),
        duration_minutes: t.duration_minutes ?? null,
      })));
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  }, []);

  // Load filtered teachers based on subject and tariff selection
  // Also ensures the currently assigned teacher is always included in the list
  const loadFilteredTeachers = useCallback(async (subjectId, tariffId, currentTeacherId = null) => {
    try {
      const params = {};
      if (subjectId) params.subject_id = subjectId;
      if (tariffId) params.tariff_id = tariffId;
      
      // If no filter criteria, show all teachers but ensure current teacher is included
      if (!subjectId && !tariffId) {
        // Show all teachers
        let allTeachers = [...teachers];
        // Ensure current teacher is at the top if assigned
        if (currentTeacherId) {
          const currentIndex = allTeachers.findIndex(t => t.value === currentTeacherId);
          if (currentIndex > 0) {
            const [current] = allTeachers.splice(currentIndex, 1);
            allTeachers = [current, ...allTeachers];
          }
        }
        setFilteredTeachers(allTeachers);
        return;
      }
      
      // Get teachers filtered by subject/tariff
      const response = await api.get('/teachers/filter/by-subject-tariff', { params });
      let filtered = response.data.map(t => ({ value: t.id, label: t.name }));
      
      // Ensure current teacher is in the list (even if they don't have the subject assigned)
      // This prevents the dropdown from showing "..." for an already assigned teacher
      if (currentTeacherId && !filtered.find(t => t.value === currentTeacherId)) {
        const currentTeacher = teachers.find(t => t.value === currentTeacherId);
        if (currentTeacher) {
          // Add current teacher at the beginning with a marker
          filtered = [{ ...currentTeacher, label: `${currentTeacher.label} (aktuell)` }, ...filtered];
        }
      }
      
      setFilteredTeachers(filtered);
    } catch (error) {
      console.error('Error loading filtered teachers:', error);
      // Fallback to all teachers
      setFilteredTeachers(teachers);
    }
  }, [teachers]);

  // Load enrollments — alle oder seitenweise (listMode / pageSize / listPage)
  const loadEnrollments = useCallback(async (extraParams = {}) => {
    const quiet = Boolean(extraParams._quiet);
    const keepEnrollmentId = extraParams._keepEnrollmentId || null;
    const replaceQuery = Boolean(extraParams._replaceQuery);
    try {
      if (!quiet) setLoading(true);
      // Fußleisten-Filter und Suchkriterien trennen.
      // Sortieren/Paginieren übergeben oft nur Footer-Keys — listQueryRef darf dann
      // NICHT geleert werden (bestehende Suche bleibt).
      const { cleanFooter, searchFromCall } = partitionEnrollmentExtraParams(extraParams);
      listQueryRef.current = sanitizeEnrollmentParams(
        nextEnrollmentListQuery(listQueryRef.current, searchFromCall, replaceQuery),
      );
      writeUvRestore({
        enrollmentId: keepEnrollmentId || selectedEnrollmentIdRef.current,
        tab: activeTab,
        listQuery: listQueryRef.current,
        fieldQuery: fieldSearchParamsRef.current,
        searchActive: searchActiveRef.current,
      });

      const { items: enrollmentsData, total } = await fetchEnrollmentList(api, {
        mode: listMode,
        page: listPage,
        pageSize,
        sort_by: overviewSort.key,
        sort_dir: overviewSort.dir,
        ...cleanFooter,
        ...listQueryRef.current,
      });

      setEnrollments(enrollmentsData);
      setTotalCount(total);

      if (enrollmentsData.length > 0) {
        const keepIdx = keepEnrollmentId
          ? enrollmentsData.findIndex((e) => e.id === keepEnrollmentId)
          : -1;
        if (keepIdx >= 0) {
          // Aktuellen Datensatz behalten — nur Index/Liste aktualisieren, Details nicht neu laden
          setCurrentIndex(keepIdx);
          const row = enrollmentsData[keepIdx];
          if (restoreNeedsSelectRef.current) {
            restoreNeedsSelectRef.current = false;
            selectEnrollment(row, keepIdx);
          } else {
            setSelectedEnrollment((prev) => (
              prev?.id === keepEnrollmentId ? { ...prev, ...row } : row
            ));
          }
        } else {
          selectEnrollment(enrollmentsData[0], 0);
        }
      } else {
        setSelectedEnrollment(null);
        setCourse(null);
        setCurrentIndex(0);
        if (quiet || searchActiveRef.current || fieldSearchParamsRef.current) {
          // Suche/Filter aktiv: alten Datensatz nicht stehen lassen
          setStudent(null);
          setGuardian(null);
          setEditedGuardian(null);
          setEditingPayer(false);
          setEditingBank(false);
          toast.info('Keine Belegung passt zu Suche und Fußleisten-Filter (Zeitraum / Nur Aktive / Aktiv ab).', {
            duration: 4500,
          });
        } else {
          setStudent(null);
          setGuardian(null);
          setEditedGuardian(null);
          setEditingPayer(false);
          setEditingBank(false);
        }
      }
    } catch (error) {
      console.error('loadEnrollments failed', error);
      toast.error('Fehler beim Laden der Belegungen');
    } finally {
      if (!quiet) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listMode, listPage, pageSize, overviewSort.key, overviewSort.dir]);

  // Select an enrollment and load details
  const selectEnrollment = async (enrollment, index) => {
    let fresh = enrollment;
    if (enrollment?.id) {
      try {
        const fullRes = await api.get(`/enrollments/${enrollment.id}/full`);
        const en = fullRes.data?.enrollment;
        const c = fullRes.data?.course;
        if (en) {
          fresh = {
            ...enrollment,
            status: en.status,
            start_date: en.start_date,
            end_date: en.end_date,
            course_id: enrollment.course_id || c?.id,
            student_id: enrollment.student_id || fullRes.data?.student?.id,
            course: c ? {
              ...(enrollment.course || {}),
              id: c.id,
              name: c.name,
              day_of_week: c.day_of_week,
              duration_minutes: c.duration_minutes,
              start_time: c.start_time,
              rhythm: c.rhythm,
              subject_id: enrollment.course?.subject_id,
              teacher_id: enrollment.course?.teacher_id,
              tariff_id: fullRes.data?.tariff?.id,
              tariff_name: fullRes.data?.tariff?.name,
            } : enrollment.course,
          };
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.error('Belegung nicht mehr vorhanden – bitte „Alle Filter zurücksetzen“ und erneut suchen');
          return;
        }
      }
    }

    setSelectedEnrollment(fresh);
    setCurrentIndex(index);
    setEditingStudent(false);
    setEditingEnrollment(false);
    setEditingPayer(false);
    setEditingBank(false);
    setShowBankIban(false);
    
    // Load student details
    let loadedStudent = null;
    if (fresh.student_id) {
      try {
        const studentRes = await api.get(`/students/${fresh.student_id}`);
        loadedStudent = studentRes.data;
        setStudent(loadedStudent);
        setEditedStudent(loadedStudent);
        
        if (loadedStudent.guardians && loadedStudent.guardians.length > 0) {
          const payerRel = loadedStudent.guardians.find((g) => g.guardian?.is_payer);
          const loadedGuardian = (payerRel || loadedStudent.guardians[0]).guardian;
          setGuardian(loadedGuardian);
          setEditedGuardian(loadedGuardian);
        } else {
          setGuardian(null);
          setEditedGuardian(null);
        }
      } catch (e) {
        setStudent(null);
        setGuardian(null);
        setEditedGuardian(null);
      }
    }
    
    // Load course details - ALWAYS load full course to get all fields (subject_id, teacher_id, etc.)
    let loadedCourse = null;
    if (fresh.course_id) {
      try {
        const courseRes = await api.get(`/courses/${fresh.course_id}`);
        loadedCourse = courseRes.data;
        setCourse(loadedCourse);
      } catch (e) {
        setCourse(null);
      }
    } else {
      setCourse(null);
    }
    
    setEditingEvent(false);
    setEditedEvent(null);
    setInlineRow(null);
    setEditedEnrollment({
      status: fresh.status,
      start_date: fresh.start_date,
      end_date: fresh.end_date,
      subject_id: loadedCourse?.subject_id || null,
      course_name: loadedCourse?.name || fresh.course_name || '',
      teacher_id: loadedCourse?.teacher_id || null,
      location_id: loadedCourse?.location_id || null,
      day_of_week: loadedCourse?.day_of_week,
      start_time: loadedCourse?.start_time || '',
      duration_minutes: loadedCourse?.duration_minutes || 45,
      rhythm: parseCourseRhythm(loadedCourse?.rhythm),
      room_name: loadedCourse?.room_name || '',
      tariff_id: loadedCourse?.tariff_id || null,
      instrument: '',
      areal: defaultArealForTenant(activeTenant)
    });
  };

  // Select student from live search
  const handleSelectStudent = async (selectedStudent) => {
    setShowSearchDropdown(false);
    setSearchTerm('');

    try {
      setLoading(true);
      const studentDetail = await api.get(`/students/${selectedStudent.id}`);
      const loadedStudent = studentDetail.data;

      setStudent(loadedStudent);
      setEditedStudent(loadedStudent);
      setCourse(null);
      setEditingPayer(false);
      setEditingBank(false);
      if (loadedStudent.guardians?.length > 0) {
        const payerRel = loadedStudent.guardians.find((g) => g.guardian?.is_payer);
        const loadedGuardian = (payerRel || loadedStudent.guardians[0]).guardian;
        setGuardian(loadedGuardian);
        setEditedGuardian(loadedGuardian);
      } else {
        setGuardian(null);
        setEditedGuardian(null);
      }

      // Immer gefiltert laden — nie ungefilterte Schüler-Belegungen (sonst „beendet“ trotz „Nur Aktive“)
      listQueryRef.current = { student_id: selectedStudent.id };
      fieldSearchParamsRef.current = null;
      setSearchActive(false);
      setSearchMode(false);
      const { items: enrichedEnrollments, total: filteredTotal } = await fetchEnrollmentList(api, {
        mode: listMode,
        page: listPage,
        pageSize,
        sort_by: overviewSort.key,
        sort_dir: overviewSort.dir,
        student_id: selectedStudent.id,
        ...footerParams(footer),
      });
      const withStudent = enrichedEnrollments.map((item) => ({
        ...item,
        student: item.student || {
          id: loadedStudent.id,
          first_name: loadedStudent.first_name,
          last_name: loadedStudent.last_name,
          date_of_birth: loadedStudent.date_of_birth,
          areal: loadedStudent.areal,
        },
        student_data: loadedStudent,
        course_data: item.course || item.course_data || null,
      }));

      setSearchActive(true);
      if (withStudent.length === 0) {
        const allRes = await api.get('/enrollments', {
          params: { page: 1, per_page: 50, student_id: selectedStudent.id },
        });
        const allN = allRes.data?.total ?? (allRes.data?.items || []).length;
        setEnrollments([]);
        setTotalCount(0);
        setSelectedEnrollment(null);
        if (allN > 0) {
          toast.info(
            `${allN} Belegung(en) vorhanden, aber keine passt zu den Filtern (Nur Aktive / Zeitraum / Aktiv ab). Filter zurücksetzen oder Belegungen neu importieren.`,
            { duration: 6000 },
          );
        } else {
          toast.info(`${loadedStudent.first_name} ${loadedStudent.last_name} hat keine Belegungen`);
        }
      } else {
        setEnrollments(withStudent);
        setTotalCount(filteredTotal || withStudent.length);
        selectEnrollment(withStudent[0], 0);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Schülerdaten');
    } finally {
      setLoading(false);
    }
  };

  // Select course/Veranstaltung from live search → load its enrollments (Fußleisten-Filter beachten)
  const handleSelectCourse = async (selectedCourse) => {
    setShowSearchDropdown(false);
    setSearchTerm('');
    let cname = selectedCourse.name;
    if (!cname) {
      try { cname = (await api.get(`/courses/${selectedCourse.id}`)).data.name; } catch { cname = 'Veranstaltung'; }
    }
    await loadEnrollmentsFiltered({ course_id: selectedCourse.id }, cname);
  };

  // Load enrollments filtered by teacher/guardian (live search)
  const loadEnrollmentsFiltered = async (params, emptyLabel) => {
    setShowSearchDropdown(false);
    setSearchTerm('');
    try {
      setLoading(true);
      listQueryRef.current = { ...params };
      fieldSearchParamsRef.current = null;
      setListPage(1);
      const { items: data, total } = await fetchEnrollmentList(api, {
        mode: listMode,
        page: 1,
        pageSize,
        sort_by: overviewSort.key,
        sort_dir: overviewSort.dir,
        ...footerParams(footer),
        ...params,
      });
      if (data.length === 0) {
        toast.info(`${emptyLabel} hat keine Belegungen`);
        return;
      }
      setEnrollments(data);
      setTotalCount(total || data.length);
      selectEnrollment(data[0], 0);
      setSearchActive(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Belegungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTeacher = (t) =>
    loadEnrollmentsFiltered({ teacher_id: t.id }, `${t.first_name} ${t.last_name}`);

  const handleSelectGuardian = (g) =>
    loadEnrollmentsFiltered({ guardian_id: g.id }, `${g.first_name} ${g.last_name}`);

  /** Zahlerkonto-Link: genau diese Belegung, unabhängig vom Fußleisten-Filter. */
  const openLinkedEnrollment = async (enrollmentId) => {
    setActiveTab('enrollments');
    try {
      setLoading(true);
      const fullRes = await api.get(`/enrollments/${enrollmentId}/full`);
      const en = fullRes.data?.enrollment;
      const linkedStudent = fullRes.data?.student;
      const linkedCourse = fullRes.data?.course;
      if (!en) {
        toast.error('Belegung nicht gefunden');
        return;
      }
      const studentId = linkedStudent?.id || en.student_id;
      listQueryRef.current = studentId ? { student_id: studentId } : {};
      fieldSearchParamsRef.current = null;
      searchActiveRef.current = true;
      setSearchActive(true);
      setSearchMode(false);
      const { items, total } = await fetchEnrollmentList(api, {
        mode: 'all',
        student_id: studentId,
      });
      let rows = items || [];
      let idx = rows.findIndex((row) => row.id === enrollmentId);
      if (idx < 0) {
        rows = [{
          id: en.id,
          student_id: studentId,
          course_id: linkedCourse?.id || en.course_id,
          status: en.status,
          start_date: en.start_date,
          end_date: en.end_date,
          student: linkedStudent,
          course: linkedCourse,
        }, ...rows];
        idx = 0;
      }
      setEnrollments(rows);
      setTotalCount(total || rows.length);
      await selectEnrollment(rows[idx], idx);
    } catch {
      toast.error('Belegung konnte nicht geöffnet werden');
    } finally {
      setLoading(false);
    }
  };

  /** Enter in Live-Suche: nur Belegungen, die den Suchbegriff selbst treffen. */
  const handleAcceptLiveSearchAll = async (results) => {
    const { students = [], guardians = [], teachers = [], courses = [] } = results || {};
    const term = searchTerm.trim();
    if (term.length < 2 || (!students.length && !guardians.length && !teachers.length && !courses.length)) {
      toast.info('Keine Treffer zum Laden');
      return;
    }
    setShowSearchDropdown(false);
    setSearchTerm('');
    try {
      setLoading(true);
      const mapItem = (item) => ({
        ...item,
        course_data: item.course ? {
          id: item.course.id,
          name: item.course.name,
          day_of_week: item.course.day_of_week,
          duration_minutes: item.course.duration_minutes,
        } : null,
      });
      const byId = new Map();

      const addList = (items) => {
        (items || []).forEach((item) => {
          if (item?.id && !byId.has(item.id)) byId.set(item.id, mapItem(item));
        });
      };

      const listOpts = { mode: 'all', page: 1, pageSize: UV_FETCH_CHUNK };
      // Schüler, Kurse und Lehrkräfte nicht nachladen: das würde jeden weiteren
      // Kurs eines Treffers in die Liste ziehen, auch Gruppen anderer Lehrkräfte.
      try {
        const loaded = await fetchEnrollmentList(api, {
          ...listOpts,
          search: term,
          ...footerParams(footer),
        });
        addList(loaded.items);
      } catch {
        /* leere Liste unten */
      }
      const data = Array.from(byId.values());
      if (data.length === 0) {
        toast.info(`Keine Belegungen zu „${term || 'Treffer'}"`);
        return;
      }
      setEnrollments(data);
      setTotalCount(data.length);
      selectEnrollment(data[0], 0);
      setSearchActive(true);
      toast.success(`${data.length} Belegung(en) geladen — mit Pfeilen durchsteppen`);
    } catch (error) {
      toast.error('Fehler beim Laden der Treffer');
    } finally {
      setLoading(false);
    }
  };

  const liveSearchRef = useRef(null);

  const onLiveSearchKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const term = searchTerm.trim();
    if (term.length < 2) return;
    // Kurz warten, falls Debounce noch läuft
    if (liveSearchRef.current?.isLoading?.()) {
      await new Promise((r) => setTimeout(r, 350));
    }
    const accepted = liveSearchRef.current?.acceptAll?.();
    if (!accepted) {
      // Fallback: direkte Belegungssuche nach Suchbegriff
      await loadEnrollmentsFiltered({ search: term }, `Suche „${term}"`);
    }
  };

  // ---- Feld-Suchmodus (iMikel-Suchenmodus): Kriterien direkt in den Feldern ----
  const suggestCatalogs = useMemo(() => ({
    tariffs, subjects, teachers, locations, rooms,
  }), [tariffs, subjects, teachers, locations, rooms]);

  const { items: fieldSuggestItems, loading: fieldSuggestLoading } = useFieldSearchSuggestions(
    suggestFocus,
    suggestFocus ? (fieldQuery[suggestFocus] || '') : '',
    suggestCatalogs,
    !!searchMode && !!suggestFocus,
  );

  const applySuggest = (key, item, runSearchAfter = false) => {
    const value = item?.value ?? '';
    setFieldQuery((q) => {
      const next = { ...q, [key]: value };
      if (runSearchAfter) {
        queueMicrotask(() => runFieldSearch(next));
      }
      return next;
    });
    setSuggestFocus(null);
  };

  const sf = (key, placeholder = SEARCH_PLACEHOLDER) => {
    if (!searchMode) return {};
    const canSuggest = SUGGESTABLE_FIELD_KEYS.has(key);
    const focused = suggestFocus === key;
    return {
      value: fieldQuery[key] || '',
      editable: true,
      type: 'text',
      placeholder,
      onChange: (v) => setFieldQuery((q) => ({ ...q, [key]: v })),
      onFocus: () => { if (canSuggest) setSuggestFocus(key); },
      onBlur: () => {
        // kurz warten, damit mousedown auf Vorschlag noch greift
        setTimeout(() => setSuggestFocus((f) => (f === key ? null : f)), 150);
      },
      suggestItems: focused ? fieldSuggestItems : [],
      suggestLoading: focused && fieldSuggestLoading,
      onSuggestSelect: canSuggest
        ? (item, opts) => applySuggest(key, item, !!opts?.submit)
        : undefined,
      'data-testid': `search-field-${key}`,
    };
  };

  // Kleine Such-Inputs für Tabellen-Suchzeilen (Übersicht-Tab)
  const si = (key, ph) => {
    const canSuggest = SUGGESTABLE_FIELD_KEYS.has(key);
    const focused = suggestFocus === key;
    return (
      <div className="relative">
        <input
          value={fieldQuery[key] || ''}
          onChange={(e) => setFieldQuery((q) => ({ ...q, [key]: e.target.value }))}
          onFocus={() => { if (canSuggest) setSuggestFocus(key); }}
          onBlur={() => setTimeout(() => setSuggestFocus((f) => (f === key ? null : f)), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && focused && fieldSuggestItems.length > 0) {
              e.preventDefault();
              e.stopPropagation();
              applySuggest(key, fieldSuggestItems[0], true);
            }
          }}
          placeholder={ph || SEARCH_PLACEHOLDER}
          data-testid={`search-field-${key}`}
          autoComplete="off"
          className={`w-full px-1.5 py-0.5 border rounded text-[11px] focus:outline-none focus:border-brand-red ${searchInputClassName(fieldQuery[key])}`}
        />
        {focused && (
          <FieldSuggestDropdown
            items={fieldSuggestItems}
            loading={fieldSuggestLoading}
            highlight={0}
            onSelect={(item) => applySuggest(key, item, false)}
          />
        )}
      </div>
    );
  };

  const runFieldSearch = async (overrideQuery) => {
    const src = overrideQuery || fieldQuery;
    const params = criteriaToParams(src);
    const validationError = validateCriteriaParams(params);
    if (validationError) {
      if (validationError.startsWith('Bitte')) toast.info(validationError);
      else toast.error(validationError);
      return;
    }
    try {
      setLoading(true);
      listQueryRef.current = { ...params };
      fieldSearchParamsRef.current = { ...params };
      setVoiceSearchCriteria(params);
      setListPage(1);
      const { items: data, total } = await fetchEnrollmentList(api, {
        mode: listMode,
        page: 1,
        pageSize,
        sort_by: overviewSort.key,
        sort_dir: overviewSort.dir,
        ...footerParams(footer),
        ...params,
      });
      if (data.length === 0) {
        setEnrollments([]);
        setTotalCount(0);
        setSelectedEnrollment(null);
        setStudent(null);
        setCourse(null);
        setGuardian(null);
        setEditedGuardian(null);
        setCurrentIndex(0);
        setSearchMode(false);
        setSearchActive(true);
        setSuggestFocus(null);
        toast.info('Keine Belegungen zu Suche und Fußleisten-Filter gefunden');
        return;
      }
      setEnrollments(data);
      setTotalCount(total || data.length);
      setSearchMode(false);
      setSearchActive(true);
      setSuggestFocus(null);
      selectEnrollment(data[0], 0);
    } catch {
      toast.error('Suche fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const clearFieldSearch = () => {
    setFieldQuery({});
    setSearchActive(false);
    setSearchMode(false);
    setSuggestFocus(null);
    listQueryRef.current = {};
    fieldSearchParamsRef.current = null;
    setVoiceSearchCriteria({});
    setListPage(1);
    loadEnrollments({ ...footerParams(footer), _replaceQuery: true });
  };

  // Die Sprachsteuerung setzt dieselben Bedingungen wie der Baukasten. "refine"
  // behält die bereits gesetzten, damit „davon nur die Klavierschüler“ eingrenzt
  // statt neu zu suchen.
  const voiceSearchRef = useRef(null);
  voiceSearchRef.current = { fieldQuery, runFieldSearch, clearFieldSearch };
  useEffect(() => {
    const h = (e) => {
      const { criteria, mode } = e.detail || {};
      const current = voiceSearchRef.current || {};
      const base = mode === 'refine' ? criteriaToParams(current.fieldQuery || {}) : {};
      const next = { ...base, ...criteriaToParams(criteria || {}) };
      setFieldQuery(next);
      if (Object.keys(next).length === 0) {
        current.clearFieldSearch?.();
        return;
      }
      setSearchActive(true);
      current.runFieldSearch?.(next);
    };
    window.addEventListener(VOICE_SEARCH_EVENT, h);
    return () => window.removeEventListener(VOICE_SEARCH_EVENT, h);
  }, []);

  useEffect(() => {
    if (!searchMode) return;
    const h = (e) => {
      if (e.key === 'Enter') {
        const target = e.target;
        const inCriterion = target instanceof Element && target.closest(
          '[data-testid="criteria-add-row"], [data-testid="criteria-value-input"], [data-testid="criteria-value-select"]',
        );
        // Das Kriterienfeld übernimmt den Begriff selbst. Sonst sucht Enter mit dem alten Stand.
        if (inCriterion) return;
        // FormField/si übernehmen Enter, solange Vorschläge offen sind
        if (suggestFocus && fieldSuggestItems.length > 0) return;
        e.preventDefault();
        runFieldSearch();
      }
      if (e.key === 'Escape') {
        if (suggestFocus) {
          setSuggestFocus(null);
          return;
        }
        setSearchMode(false);
        if (!searchActiveRef.current) setFieldQuery({});
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  // Navigation
  const goToPrevious = () => {
    if (currentIndex > 0) {
      selectEnrollment(enrollments[currentIndex - 1], currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < enrollments.length - 1) {
      selectEnrollment(enrollments[currentIndex + 1], currentIndex + 1);
    }
  };

  // Save student changes
  const handleSaveStudent = async () => {
    if (!student || !editedStudent) return;
    
    try {
      await api.put(`/students/${student.id}`, {
        first_name: editedStudent.first_name,
        last_name: editedStudent.last_name,
        email: editedStudent.email,
        phone: editedStudent.phone,
        address: editedStudent.address,
        city: editedStudent.city,
        postal_code: editedStudent.postal_code,
        date_of_birth: toDateInputValue(editedStudent.date_of_birth) || null,
        notes: editedStudent.notes
      });
      
      setStudent({
        ...editedStudent,
        date_of_birth: toDateInputValue(editedStudent.date_of_birth) || null,
      });
      setEditingStudent(false);
      toast.success('Schülerdaten gespeichert');
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Speichern'));
    }
  };

  const openNewEnrollment = (opts = {}) => {
    const variant = opts.variant || 'belegung';
    setEnrollmentDialogVariant(variant);
    const prefill = {};
    // Neuerfassung: immer leere Vollmaske. Neue Belegung: ggf. aktuelle Person übernehmen.
    if (variant !== 'neuerfassung') {
      if (opts.withStudent && student) {
        prefill.existingStudentId = student.id;
        prefill.student = {
          first_name: student.first_name || '',
          last_name: student.last_name || '',
          date_of_birth: toDateInputValue(student.date_of_birth) || '',
          email: student.email || '',
          phone: student.phone || '',
          address: student.address || '',
          postal_code: student.postal_code || '',
          city: student.city || '',
          areal: resolveAreal(student.areal, activeTenant),
          notes: student.notes || '',
        };
      }
      if (opts.withGuardian && guardian) {
        prefill.existingGuardianId = guardian.id;
        prefill.payer = {
          first_name: guardian.first_name || '',
          last_name: guardian.last_name || '',
          email: guardian.email || '',
          phone: guardian.phone || '',
          address: guardian.address || '',
          postal_code: guardian.postal_code || '',
          city: guardian.city || '',
        };
        if (guardian.bank_iban) {
          prefill.bank = {
            account_holder: guardian.bank_account_holder || '',
            iban: guardian.bank_iban || '',
            bic: guardian.bank_bic || '',
          };
        }
      }
    }
    setEnrollmentDialogPrefill(Object.keys(prefill).length ? prefill : null);
    setShowNewEnrollmentDialog(true);
  };

  const bankIsEmpty = !guardian?.bank_iban && !guardian?.sepa_mandate_reference;
  // Der Stift beim Zahlungspflichtigen schaltet auch dessen Kontaktangaben in
  // der Karte darunter auf Eingabe um. Im Suchmodus dienen dieselben Felder als
  // Suchschlitze, dann wird nichts bearbeitet.
  const payerEditing = editingPayer && !searchMode;
  // Dasselbe für den Teilnehmer: dessen Telefon und E-Mail waren bisher nur in
  // der Schülerakte erreichbar, obwohl `handleSaveStudent` sie längst mitsendet.
  const studentEditing = editingStudent && !searchMode;
  // Alle weiteren Angehörigen — der als Zahler angezeigte steht schon oben in
  // der Karte. Ohne diese Liste blieben zweite Personen aus Online-Anmeldung
  // und Import unsichtbar.
  const weitereKontakte = (student?.guardians || []).filter(
    (rel) => rel.guardian && rel.guardian.id !== guardian?.id,
  );
  const custodianRel = weitererErziehungsberechtigter(student?.guardians, guardian?.id);
  const custodian = custodianRel?.guardian || null;
  const zeigtErzieher = personTab === 'custodian' && !searchMode;
  const kartenPerson = zeigtErzieher ? custodian : guardian;
  const kartenBearbeiten = zeigtErzieher ? (editingCustodian && !searchMode) : payerEditing;
  const kartenEntwurf = zeigtErzieher ? editedCustodian : editedGuardian;
  const setKartenEntwurf = zeigtErzieher ? setEditedCustodian : setEditedGuardian;

  // Nach Änderungen im Kontaktpersonen-Dialog: Schüler neu holen, damit Karte
  // und Dialog denselben Stand zeigen. Offene Bearbeitungen bleiben stehen.
  const reloadStudent = async () => {
    if (!student?.id) return;
    try {
      const { data } = await api.get(`/students/${student.id}`);
      setStudent(data);
      if (!editingStudent) setEditedStudent(data);
      const rel = data.guardians?.find((g) => g.guardian?.is_payer) || data.guardians?.[0];
      setGuardian(rel?.guardian || null);
      if (!editingPayer && !editingBank) setEditedGuardian(rel?.guardian || null);
    } catch (e) {
      toast.error('Kontaktpersonen konnten nicht neu geladen werden');
    }
  };

  const beginEditGuardian = (section) => {
    if (!guardian) return;
    setEditedGuardian({ ...guardian });
    setEditingPayer(section === 'payer');
    setEditingBank(section === 'bank');
  };

  const cancelEditGuardian = () => {
    setEditingPayer(false);
    setEditingBank(false);
    setEditedGuardian(guardian);
  };

  const handleSavePayer = async () => {
    if (!guardian || !editedGuardian) return;

    try {
      const { data } = await api.put(`/guardians/${guardian.id}`, {
        first_name: editedGuardian.first_name,
        last_name: editedGuardian.last_name,
        address: editedGuardian.address,
        city: editedGuardian.city,
        postal_code: editedGuardian.postal_code,
        gkz: (editedGuardian.gkz || '').trim() || null,
        email: editedGuardian.email,
        phone: editedGuardian.phone,
        date_of_birth: toDateInputValue(editedGuardian.date_of_birth) || null,
      });
      const next = { ...guardian, ...editedGuardian, ...data };
      setGuardian(next);
      setEditedGuardian(next);
      setEditingPayer(false);
      toast.success('Zahlungspflichtige:r gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleSaveCustodian = async () => {
    if (!custodian || !editedCustodian) return;
    try {
      await api.put(`/guardians/${custodian.id}`, {
        first_name: editedCustodian.first_name,
        last_name: editedCustodian.last_name,
        address: editedCustodian.address,
        city: editedCustodian.city,
        postal_code: editedCustodian.postal_code,
        gkz: (editedCustodian.gkz || '').trim() || null,
        email: editedCustodian.email,
        phone: editedCustodian.phone,
        date_of_birth: toDateInputValue(editedCustodian.date_of_birth) || null,
      });
      setEditingCustodian(false);
      toast.success('Erziehungsberechtigte:r gespeichert');
      await reloadStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const stiftZahler = () => {
    setPersonTab('payer');
    setEditingCustodian(false);
    beginEditGuardian('payer');
  };

  const stiftErzieher = () => {
    setPersonTab('custodian');
    setEditingPayer(false);
    setEditingBank(false);
    if (!student) return;
    if (!custodian) {
      setCustodianAssignOpen(true);
      return;
    }
    setEditedCustodian({ ...custodian });
    setEditingCustodian(true);
  };

  const sendBeideZugaenge = async () => {
    if (!student?.id) return;
    try {
      const { data } = await api.post(`/students/${student.id}/app-zugaenge`);
      const sent = data?.sent || 0;
      toast.success(sent
        ? `Zugang zur App an ${sent} ${sent === 1 ? 'Person' : 'Personen'} geschickt.`
        : 'Kennungen sind vorbereitet. Eine Mail ist nicht rausgegangen.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'App-Zugang konnte nicht verschickt werden');
    }
  };

  const handleSaveBank = async (validFrom) => {
    if (!guardian || !editedGuardian) return;
    if (typeof validFrom !== 'string') {
      if (bankFieldsChanged(editedGuardian, guardian)) {
        setEffectiveDialog({ kind: 'bank' });
        return;
      }
    }

    const iban = (editedGuardian.bank_iban || '').replace(/\s+/g, '').toUpperCase();
    const mandateDate = toDateInputValue(editedGuardian.sepa_mandate_date);
    const payload = {
      bank_account_holder: editedGuardian.bank_account_holder,
      bank_name: editedGuardian.bank_name,
      bank_iban: iban || null,
      bank_bic: editedGuardian.bank_bic,
      sepa_mandate_reference: editedGuardian.sepa_mandate_reference,
      sepa_mandate_date: mandateDate ? new Date(mandateDate).toISOString() : null,
      sepa_debit_day: editedGuardian.sepa_debit_day ? Number(editedGuardian.sepa_debit_day) : null,
    };

    try {
      if (typeof validFrom === 'string') {
        await api.post(`/guardians/${guardian.id}/bank-periods`, {
          valid_from: validFrom,
          ...payload,
        });
      } else {
        await api.put(`/guardians/${guardian.id}`, payload);
      }
      const { data } = await api.get(`/guardians/${guardian.id}`);
      const next = { ...guardian, ...editedGuardian, ...data, bank_iban: iban || data?.bank_iban || null };
      setGuardian(next);
      setEditedGuardian(next);
      setEditingBank(false);
      toast.success('Bankdaten gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  // Eintritt, Austritt und Status gehören zur einzelnen Belegung.
  const handleSaveEnrollment = async () => {
    if (!selectedEnrollment || !editedEnrollment) return;
    try {
      await api.put(`/courses/${selectedEnrollment.course_id}/enrollments/${selectedEnrollment.id}`, {
        status: editedEnrollment.status,
        start_date: editedEnrollment.start_date || null,
        end_date: editedEnrollment.end_date || null,
      });
      const updatedEnrollment = {
        ...selectedEnrollment,
        status: editedEnrollment.status,
        start_date: editedEnrollment.start_date,
        end_date: editedEnrollment.end_date,
      };
      setSelectedEnrollment(updatedEnrollment);
      setEnrollments((prevEnrollments) =>
        prevEnrollments.map((e) => (e.id === selectedEnrollment.id ? updatedEnrollment : e))
      );
      setEditingEnrollment(false);
      toast.success('Belegung gespeichert');
    } catch (error) {
      console.error('Save error:', error);
      toast.error(formatApiError(error, 'Fehler beim Speichern der Belegung'));
    }
  };

  const patchInline = (partial) => {
    setInlineRow((prev) => (prev ? { ...prev, draft: { ...prev.draft, ...partial } } : prev));
  };

  const todayIso = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };

  const reloadCourseAfterInline = async () => {
    if (!course?.id) return [];
    const courseRes = await api.get(`/courses/${course.id}`);
    setCourse(courseRes.data);
    setEditedEnrollment((prev) => (prev ? {
      ...prev,
      course_name: courseRes.data.name ?? prev.course_name,
      subject_id: courseRes.data.subject_id || prev.subject_id,
      tariff_id: courseRes.data.tariff_id || prev.tariff_id,
      teacher_id: courseRes.data.teacher_id || prev.teacher_id,
      day_of_week: courseRes.data.day_of_week,
      start_time: courseRes.data.start_time || prev.start_time,
      duration_minutes: courseRes.data.duration_minutes ?? prev.duration_minutes,
      rhythm: parseCourseRhythm(courseRes.data.rhythm),
      location_id: courseRes.data.location_id || null,
      room_name: courseRes.data.room_name || prev.room_name,
    } : prev));
    let periods = [];
    try {
      const pr = await api.get(`/courses/${course.id}/periods`);
      periods = Array.isArray(pr.data) ? pr.data : [];
      setCoursePeriods(periods);
    } catch { /* Historie bleibt, bis sie neu geladen wird */ }
    try {
      const er = await api.get(`/courses/${course.id}/enrollments`);
      setCourseEnrollments(Array.isArray(er.data) ? er.data : []);
    } catch { /* Teilnehmerliste bleibt, bis sie neu geladen wird */ }
    return periods;
  };

  const beginEditStudentRow = (enrollmentRow) => {
    const s = enrollmentRow.student || {};
    setInlineRow({
      kind: 'student',
      id: enrollmentRow.id,
      draft: {
        studentId: s.id,
        first_name: s.first_name || '',
        last_name: s.last_name || '',
        date_of_birth: toDateInputValue(s.date_of_birth) || '',
        start_date: toDateInputValue(enrollmentRow.start_date) || '',
        end_date: toDateInputValue(enrollmentRow.end_date) || '',
        status: enrollmentRow.status || 'active',
      },
    });
  };

  const saveStudentRow = async () => {
    if (inlineRow?.kind !== 'student' || !course?.id) return;
    const d = inlineRow.draft;
    if (!d.studentId) {
      toast.error('Schüler:in ohne Datensatz');
      return;
    }
    try {
      await api.put(`/students/${d.studentId}`, {
        first_name: d.first_name,
        last_name: d.last_name,
        date_of_birth: d.date_of_birth || null,
      });
      await api.put(`/courses/${course.id}/enrollments/${inlineRow.id}`, {
        status: d.status || 'active',
        start_date: d.start_date || null,
        end_date: d.end_date || null,
      });
      if (student?.id === d.studentId) {
        setStudent((prev) => (prev ? {
          ...prev,
          first_name: d.first_name,
          last_name: d.last_name,
          date_of_birth: d.date_of_birth || null,
        } : prev));
      }
      if (selectedEnrollment?.id === inlineRow.id) {
        setSelectedEnrollment((prev) => (prev ? {
          ...prev,
          start_date: d.start_date || null,
          end_date: d.end_date || null,
        } : prev));
      }
      const er = await api.get(`/courses/${course.id}/enrollments`);
      setCourseEnrollments(Array.isArray(er.data) ? er.data : []);
      setInlineRow(null);
      toast.success('Teilnehmer:in gespeichert');
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Speichern der Teilnehmer:in'));
    }
  };

  const beginEditTeacherRow = (period) => {
    const teacherId = period.teacher_id || course?.teacher_id || '';
    setInlineRow({
      kind: 'teacher',
      id: period.id || 'live',
      baselineTeacherId: teacherId,
      baselineUntil: toDateInputValue(period.valid_until) || '',
      draft: {
        teacher_id: teacherId,
        valid_from: toDateInputValue(period.valid_from) || todayIso(),
        valid_until: toDateInputValue(period.valid_until) || '',
      },
    });
  };

  const beginHonorarium = () => {
    setHonorariumDraft({
      kind: 'per_lesson',
      amount: '',
      valid_from: todayIso(),
      valid_until: '',
      notes: '',
    });
  };

  const saveHonorarium = async () => {
    if (!course?.id || !honorariumDraft) return;
    const amount = Number(honorariumDraft.amount);
    if (!honorariumDraft.valid_from || !Number.isFinite(amount) || amount <= 0) {
      toast.error('Bitte Gültig-ab und einen positiven Honorarbetrag angeben.');
      return;
    }
    if (honorariumDraft.valid_until
      && honorariumDraft.valid_until < honorariumDraft.valid_from) {
      toast.error('Das Datum „bis“ liegt vor „von“.');
      return;
    }
    try {
      await api.post(`/courses/${course.id}/honoraria`, {
        ...honorariumDraft,
        amount,
        valid_until: honorariumDraft.valid_until || null,
        notes: honorariumDraft.notes || null,
      });
      const response = await api.get(`/courses/${course.id}/honoraria`);
      setCourseHonoraria(Array.isArray(response.data) ? response.data : []);
      setHonorariumDraft(null);
      toast.success('Individuelles Veranstaltungshonorar gespeichert');
    } catch (error) {
      toast.error(formatApiError(error, 'Honorarregel konnte nicht gespeichert werden'));
    }
  };

  const saveTeacherRow = async (andNew = false) => {
    if (inlineRow?.kind !== 'teacher' || !course?.id) return;
    const d = inlineRow.draft;
    const rowId = inlineRow.id;
    const untilChanged = (d.valid_until || '') !== (inlineRow.baselineUntil || '');
    if (!andNew && _normId(d.teacher_id) === _normId(inlineRow.baselineTeacherId) && !untilChanged) {
      setInlineRow(null);
      return;
    }
    if (andNew && !d.valid_until) {
      toast.error('Für Speichern & Neu bitte das Datum „bis“ setzen.');
      return;
    }
    if (!d.valid_from) {
      toast.error('Bitte das Datum „von“ setzen.');
      return;
    }
    if (d.valid_until && d.valid_until < d.valid_from) {
      toast.error('Das Datum „bis“ liegt vor „von“.');
      return;
    }
    const nextFrom = andNew ? dayAfterIso(d.valid_until) : d.valid_from;
    const periodPayload = {
      valid_from: nextFrom,
      valid_until: andNew ? null : (d.valid_until || null),
      teacher_id: d.teacher_id || null,
      day_of_week: course.day_of_week ?? null,
      start_time: course.start_time ? String(course.start_time).slice(0, 5) : null,
      duration_minutes: course.duration_minutes ?? null,
      rhythm: parseCourseRhythm(course.rhythm),
      room_id: course.room_id || null,
    };
    try {
      const savedPeriod = andNew
        ? await saveCoursePeriodWithCollisionWarning(course.id, periodPayload)
        : await saveCoursePeriodInPlace(course.id, rowId, {
          ...periodPayload,
          valid_from: d.valid_from || nextFrom,
        });
      if (!savedPeriod) return;
      const periods = await reloadCourseAfterInline();
      if (andNew) {
        const follow = periods.find((p) => p.valid_from === nextFrom && !p.valid_until);
        setInlineRow({
          kind: 'teacher',
          id: follow?.id || rowId,
          baselineTeacherId: d.teacher_id,
          baselineUntil: '',
          draft: {
            teacher_id: d.teacher_id || '',
            valid_from: nextFrom,
            valid_until: '',
          },
        });
        toast.success('Lehrkraft beendet, Fortsetzung angelegt');
      } else {
        setInlineRow(null);
        toast.success('Lehrkraft gespeichert');
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Speichern der Lehrkraft'));
    }
  };

  const beginEditTerminRow = (period) => {
    const start = period.start_time || course?.start_time || '';
    const draft = {
      teacher_id: period.teacher_id || course?.teacher_id || '',
      valid_from: toDateInputValue(period.valid_from) || todayIso(),
      valid_until: toDateInputValue(period.valid_until) || '',
      day_of_week: period.day_of_week ?? course?.day_of_week ?? '',
      start_time: start ? String(start).slice(0, 5) : '',
      duration_minutes: String(period.duration_minutes ?? course?.duration_minutes ?? ''),
      rhythm: parseCourseRhythm(period.rhythm || course?.rhythm),
      location_id: (period.id && period.id !== 'live')
        ? (period.location_id || '')
        : (period.location_id || course?.location_id || ''),
      room_id: (period.id && period.id !== 'live')
        ? (period.room_id || '')
        : (period.room_id || course?.room_id || ''),
      max_capacity: course?.max_capacity ?? '',
    };
    setInlineRow({
      kind: 'termin',
      id: period.id || 'live',
      baseline: draft,
      draft: { ...draft },
    });
  };

  const saveTerminRow = async (andNew = false) => {
    if (inlineRow?.kind !== 'termin' || !course?.id) return;
    const d = inlineRow.draft;
    const rowId = inlineRow.id;
    const base = inlineRow.baseline || {};
    const temporal = (
      _normId(d.teacher_id) !== _normId(base.teacher_id)
      || _normId(d.day_of_week) !== _normId(base.day_of_week)
      || _normTime(d.start_time) !== _normTime(base.start_time)
      || _normId(d.duration_minutes) !== _normId(base.duration_minutes)
      || parseCourseRhythm(d.rhythm) !== parseCourseRhythm(base.rhythm)
      || _normId(d.room_id) !== _normId(base.room_id)
    );
    const locationChanged = _normId(d.location_id) !== _normId(base.location_id);
    const capChanged = _normId(d.max_capacity) !== _normId(base.max_capacity);
    const untilChanged = (d.valid_until || '') !== (base.valid_until || '');
    if (!andNew && !temporal && !locationChanged && !capChanged && !untilChanged) {
      setInlineRow(null);
      return;
    }
    if (andNew && !d.valid_until) {
      toast.error('Für Speichern & Neu bitte das Datum „bis“ setzen.');
      return;
    }
    if ((temporal || untilChanged || locationChanged || andNew) && !d.valid_from) {
      toast.error('Bitte das Datum „von“ setzen.');
      return;
    }
    if (d.valid_until && d.valid_from && d.valid_until < d.valid_from) {
      toast.error('Das Datum „bis“ liegt vor „von“.');
      return;
    }
    try {
      if (temporal || untilChanged || locationChanged || andNew) {
        const day = d.day_of_week;
        const nextFrom = andNew ? dayAfterIso(d.valid_until) : d.valid_from;
        const periodPayload = {
          valid_from: nextFrom,
          valid_until: andNew ? null : (d.valid_until || null),
          teacher_id: d.teacher_id || null,
          day_of_week: (day !== undefined && day !== null && day !== '') ? parseInt(day, 10) : null,
          start_time: d.start_time || null,
          duration_minutes: d.duration_minutes ? parseInt(d.duration_minutes, 10) : null,
          rhythm: parseCourseRhythm(d.rhythm),
          room_id: d.room_id || null,
          location_id: d.location_id || null,
        };
        const savedPeriod = andNew
          ? await saveCoursePeriodWithCollisionWarning(course.id, periodPayload)
          : await saveCoursePeriodInPlace(course.id, rowId, periodPayload);
        if (!savedPeriod) return;
      }
      if (capChanged) {
        await api.put(`/courses/${course.id}`, {
          max_capacity: d.max_capacity === '' || d.max_capacity == null
            ? null
            : parseInt(d.max_capacity, 10),
        });
      }
      const periods = await reloadCourseAfterInline();
      if (andNew) {
        const nextFrom = dayAfterIso(d.valid_until);
        const follow = periods.find((p) => p.valid_from === nextFrom && !p.valid_until);
        setInlineRow({
          kind: 'termin',
          id: follow?.id || rowId,
          baseline: { ...d, valid_from: nextFrom, valid_until: '' },
          draft: { ...d, valid_from: nextFrom, valid_until: '' },
        });
        toast.success('Termin beendet, Fortsetzung angelegt');
      } else {
        setInlineRow(null);
        toast.success('Termin gespeichert');
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Speichern des Termins'));
    }
  };

  const beginEditName = () => {
    if (!course) return;
    setInlineRow({
      kind: 'name',
      id: 'bezeichnung',
      draft: { course_name: course.name || '' },
    });
  };

  const saveName = async () => {
    if (inlineRow?.kind !== 'name' || !course?.id) return;
    try {
      await api.put(`/courses/${course.id}`, {
        name: String(inlineRow.draft.course_name || '').trim(),
      });
      await reloadCourseAfterInline();
      setInlineRow(null);
      toast.success('Bezeichnung gespeichert');
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Speichern der Bezeichnung'));
    }
  };

  const beginEditEvent = () => {
    if (!course) return;
    setEditingEvent(true);
    setEditedEvent({
      subject_id: course.subject_id || '',
      tariff_id: course.tariff_id || '',
      course_name: course.name || '',
      teacher_id: course.teacher_id || '',
      day_of_week: course.day_of_week,
      start_time: course.start_time ? String(course.start_time).slice(0, 5) : '',
      duration_minutes: course.duration_minutes,
      rhythm: parseCourseRhythm(course.rhythm),
      room_id: course.room_id || '',
      location_id: course.location_id || '',
    });
  };

  const eventMetaChanged = (edited, current) => {
    if (!edited || !current) return false;
    return (
      _normId(edited.subject_id) !== _normId(current.subject_id)
      || _normId(edited.tariff_id) !== _normId(current.tariff_id)
      || (edited.course_name || '') !== (current.name || '')
      || _normId(edited.location_id) !== _normId(current.location_id)
    );
  };

  // Lehrkraft, Termin und Raum gelten ab einem Datum. Fach, Tarif und Bezeichnung
  // gehören zur Veranstaltung und werden mitgeschrieben.
  const handleSaveEvent = async (validFrom) => {
    if (!course?.id || !editedEvent) return;
    const temporal = courseTemporalChanged(editedEvent, course);
    const meta = eventMetaChanged(editedEvent, course);
    if (!temporal && !meta) {
      setEditingEvent(false);
      return;
    }
    if (temporal && typeof validFrom !== 'string') {
      setEffectiveDialog({ kind: 'event' });
      return;
    }
    try {
      if (temporal) {
        const day = editedEvent.day_of_week;
        const savedPeriod = await saveCoursePeriodWithCollisionWarning(course.id, {
          valid_from: validFrom,
          teacher_id: editedEvent.teacher_id || null,
          day_of_week: (day !== undefined && day !== null && day !== '') ? parseInt(day, 10) : null,
          start_time: editedEvent.start_time || null,
          duration_minutes: editedEvent.duration_minutes
            ? parseInt(editedEvent.duration_minutes, 10) : null,
          rhythm: parseCourseRhythm(editedEvent.rhythm),
          room_id: editedEvent.room_id || null,
          location_id: editedEvent.location_id || null,
        });
        if (!savedPeriod) return;
      }
      if (meta) {
        const courseUpdateData = {};
        if (editedEvent.subject_id) courseUpdateData.subject_id = editedEvent.subject_id;
        if (editedEvent.tariff_id) {
          courseUpdateData.tariff_id = editedEvent.tariff_id;
          const tar = tariffs.find((t) => t.value === editedEvent.tariff_id);
          if (tar?.duration_minutes && !temporal) {
            courseUpdateData.duration_minutes = parseInt(tar.duration_minutes, 10);
          }
        }
        if (editedEvent.course_name != null) courseUpdateData.name = String(editedEvent.course_name).trim();
        if (_normId(editedEvent.location_id) !== _normId(course.location_id)) {
          courseUpdateData.location_id = editedEvent.location_id || null;
        }
        if (Object.keys(courseUpdateData).length > 0) {
          await api.put(`/courses/${course.id}`, courseUpdateData);
        }
      }
      await reloadCourseAfterInline();
      setEditingEvent(false);
      toast.success('Veranstaltung gespeichert');
    } catch (error) {
      toast.error(formatApiError(error, 'Fehler beim Speichern der Veranstaltung'));
    }
  };

  // Delete enrollment
  const handleDeleteEnrollment = async () => {
    if (!selectedEnrollment) return;
    
    try {
      await api.delete(`/courses/${selectedEnrollment.course_id}/enrollments/${selectedEnrollment.id}`);
      
      toast.success('Belegung gelöscht');
      setShowDeleteDialog(false);
      
      // Remove from list and select next
      const newEnrollments = enrollments.filter(e => e.id !== selectedEnrollment.id);
      setEnrollments(newEnrollments);
      setTotalCount(newEnrollments.length);
      
      if (newEnrollments.length > 0) {
        const newIndex = Math.min(currentIndex, newEnrollments.length - 1);
        selectEnrollment(newEnrollments[newIndex], newIndex);
      } else {
        setSelectedEnrollment(null);
        setCourse(null);
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Deep-Links aus dem Startfenster: ?student_id= / ?course_id= / ?teacher_id= / ?guardian_id=
  const deepLinkHandled = useRef(false);
  const skipNextListReloadRef = useRef(false);

  // Online-Anmeldung → Manuell Einteilen: Neuerfassung mit Prefill öffnen
  useEffect(() => {
    if (registrationPrefillHandled.current) return;
    const state = location.state;
    const stored = (!state?.fromRegistration) ? consumeEnrollmentPrefill() : null;
    const fromRegistration = state?.fromRegistration || stored?.fromRegistration;
    const enrollmentPrefill = state?.enrollmentPrefill || stored?.enrollmentPrefill;
    if (!fromRegistration || !enrollmentPrefill) return;
    registrationPrefillHandled.current = true;
    setEnrollmentDialogVariant('neuerfassung');
    setEnrollmentDialogPrefill(enrollmentPrefill);
    setShowNewEnrollmentDialog(true);
    if (state?.fromRegistration) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Deep-Links (Startfenster-Suche / Strg+S): auch bei Param-Änderung im gemounteten Zustand
  useEffect(() => {
    const sid = searchParams.get('student_id');
    const cid = searchParams.get('course_id');
    const tid = searchParams.get('teacher_id');
    const gid = searchParams.get('guardian_id');
    const eid = searchParams.get('enrollment_id');
    if (!(sid || cid || tid || gid || eid)) return;
    deepLinkHandled.current = true;
    // Einmaligen Reload-Skip: Deep-Link lädt selbst; der loadEnrollments-Effect
    // darf denselben Moment nicht ungefiltert überschreiben — spätere Sort-/Page-
    // Reloads müssen aber weiterlaufen (Filter bleibt in listQueryRef).
    skipNextListReloadRef.current = true;
    setSearchParams({}, { replace: true });
    if (eid) openLinkedEnrollment(eid);
    else if (sid) handleSelectStudent({ id: sid, first_name: '', last_name: '' });
    else if (cid) handleSelectCourse({ id: cid });
    else if (tid) loadEnrollmentsFiltered({ teacher_id: tid }, 'Diese Lehrkraft');
    else if (gid) loadEnrollmentsFiltered({ guardian_id: gid }, 'Diese:r Zahlungspflichtige');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (skipNextListReloadRef.current) {
      skipNextListReloadRef.current = false;
    } else {
      loadEnrollments({
        ...footerParams(footer),
        _keepEnrollmentId: selectedEnrollmentIdRef.current,
      });
    }
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadEnrollments, loadMasterData]);

  const eventRecords = useMemo(() => distinctCourseEnrollments(enrollments), [enrollments]);
  const viewingEvents = activeTab === 'events';
  const navRecords = viewingEvents ? eventRecords : enrollments;
  const selectedCourseKey = selectedEnrollment?.course_id || selectedEnrollment?.course?.id || selectedEnrollment?.id;
  const eventIndex = eventRecords.findIndex((enrollment) => (
    (enrollment.course_id || enrollment.course?.id || enrollment.id) === selectedCourseKey
  ));
  const navIndex = viewingEvents ? (eventIndex >= 0 ? eventIndex : 0) : currentIndex;

  // Zähler an globale Fußleiste melden
  useEffect(() => {
    const text = viewingEvents
      ? `${eventRecords.length} Veranstaltungen`
      : `${totalCount} Belegungen`;
    window.dispatchEvent(new CustomEvent('footer-count', { detail: text }));
  }, [totalCount, viewingEvents, eventRecords.length]);
  useEffect(() => () => window.dispatchEvent(new CustomEvent('footer-count', { detail: '' })), []);

  const calculateAge = (dateOfBirth) => {
    const birthDate = parseDate(dateOfBirth);
    if (!birthDate) return '-';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 0 ? '-' : age;
  };

  useActionBarConfig({
    selected: guardian
      ? { kind: 'guardian', id: guardian.id, name: `${guardian.first_name} ${guardian.last_name}`, email: guardian.email, phone: guardian.phone }
      : (student ? { kind: 'student', id: student.id, name: `${student.first_name} ${student.last_name}` } : null),
    list: { kind: 'student', ids: [...new Set(enrollments.map((e) => e.student_id || e.student?.id).filter(Boolean))] },
    newLabel: 'Neu anlegen',
    newMenu: [
      {
        id: 'belegung',
        label: 'Neue Belegung',
        description: student || guardian
          ? 'Weitere Belegung — übernimmt die aktuelle Person, falls geladen'
          : 'Belegung anlegen (Schüler:in / Zahler:in / Kurs)',
        testId: 'actionbar-new-belegung',
        onClick: () => openNewEnrollment({ withStudent: !!student, withGuardian: !!guardian, variant: 'belegung' }),
      },
      {
        id: 'neuerfassung',
        label: 'Neuerfassung',
        description: 'Komplette Anmeldung manuell: neue Person, Zahler:in, Bank und Belegung',
        testId: 'actionbar-new-neuerfassung',
        onClick: () => openNewEnrollment({ variant: 'neuerfassung' }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardian?.id, student?.id, enrollments]);

  const { requestAction } = useActionBar();
  const [exportBusy, setExportBusy] = useState(false);
  const [listenOpen, setListenOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [contactPersonsOpen, setContactPersonsOpen] = useState(false);

  const exportEnrollments = async () => {
    setExportBusy(true);
    try {
      const response = await api.get('/exports/enrollments.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'einschreibungen.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Einschreibungen gestartet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export fehlgeschlagen');
    } finally {
      setExportBusy(false);
    }
  };

  const runToolbarAction = (key) => {
    switch (key) {
      case 'drucken':
        setListenOpen(true);
        break;
      case 'brief':
        requestAction('print');
        break;
      case 'e-mail':
        if (guardian || student) requestAction('mail-single');
        else requestAction('mail-bulk');
        break;
      case 'sms':
        requestAction('sms');
        break;
      case 'app':
        requestAction('app');
        break;
      case 'rechnung':
        requestAction('invoice');
        break;
      case 'kalender':
        navigate(hrefForSelectedRecord('/calendar', readSelectedRecord()));
        break;
      case 'export':
        exportEnrollments();
        break;
      default:
        break;
    }
  };

  return (
    <Layout>
    <div className="flex-1 min-h-0 bg-slate-50 flex flex-col">
      {/* Werkzeugleiste unter dem globalen Blau-/Zurück-Balken — links fixiert */}
      <div className="bg-white border-b border-slate-200 px-4 py-1.5 flex items-center justify-start gap-2 shrink-0">
          {/* Live Search + Feld-Suchmodus */}
          <div className="relative shrink-0">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Alles durchsuchen…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSearchDropdown(true);
                }}
                onFocus={() => setShowSearchDropdown(true)}
                onKeyDown={onLiveSearchKeyDown}
                className="w-56 h-7 text-xs shrink-0"
                data-testid="search-student-input"
                title="Enter: alle Treffer laden und durchsteppen"
              />
              <button
                type="button"
                onClick={() => {
                  const next = !searchMode;
                  setSearchMode(next);
                  if (next) {
                    if (searchActive && fieldSearchParamsRef.current) {
                      setFieldQuery({ ...fieldSearchParamsRef.current });
                    } else if (!searchActive) {
                      setFieldQuery({});
                    }
                  }
                  setEditingStudent(false);
                  setEditingPayer(false);
                  setEditingBank(false);
                  setEditingEnrollment(false);
                  if (next && !['enrollments', 'events', 'overview', 'billing'].includes(activeTab)) setActiveTab('enrollments');
                }}
                className={`p-1.5 rounded text-slate-600 shrink-0 ${searchMode ? 'bg-brand-red text-white' : 'hover:bg-slate-100'}`}
                title="Schrittweise Suche: mehrere Kriterien UND-verknüpft"
                data-testid="field-search-toggle"
              >
                <TextSearch className="w-4 h-4" />
              </button>
            </div>
            <LiveSearchDropdown
              ref={liveSearchRef}
              searchTerm={searchTerm}
              onSelect={handleSelectStudent}
              onSelectCourse={handleSelectCourse}
              onSelectTeacher={handleSelectTeacher}
              onSelectGuardian={handleSelectGuardian}
              onAcceptAll={handleAcceptLiveSearchAll}
              isOpen={showSearchDropdown}
              onClose={() => setShowSearchDropdown(false)}
            />
          </div>
          
          {/* Record Navigator — Name links, „gehe zu N von Total“ rechts */}
          <div className="flex-1 min-w-0">
            <RecordNavigator
              className="w-full"
              currentIndex={navIndex}
              totalCount={navRecords.length}
              onNavigate={(newIndex) => {
                const row = navRecords[newIndex];
                if (!row) return;
                const idx = enrollments.findIndex((enrollment) => enrollment.id === row.id);
                selectEnrollment(row, idx >= 0 ? idx : 0);
              }}
              darkMode={false}
              records={navRecords}
              getRecordLabel={(enrollment) => {
                if (viewingEvents) {
                  return formatClocks(enrollment.course?.name || enrollment.course_data?.name || enrollment.course_name) || 'Veranstaltung';
                }
                if (enrollment.student && enrollment.student.last_name) {
                  return `${enrollment.student.last_name}, ${enrollment.student.first_name || ''}`.replace(/,\s*$/, '').trim();
                }
                if (enrollment.student_name) {
                  // Nur Name — alles nach „ - “ (Kurs/Instrument) abschneiden
                  return String(enrollment.student_name).split(' - ')[0].trim();
                }
                if (enrollment.student_data && enrollment.student_data.last_name) {
                  return `${enrollment.student_data.last_name}, ${enrollment.student_data.first_name || ''}`.replace(/,\s*$/, '').trim();
                }
                return 'Unbekannt';
              }}
            />
          </div>

          <HistoryEyeButton
            disabled={!selectedEnrollment?.id}
            onClick={() => setHistoryOpen(true)}
            title="Änderungsprotokoll (was / wann / von wem)"
          />
          
          {/* Multi-Select Toggle */}
          <button 
            onClick={() => {
              setShowBulkActions(!showBulkActions);
              if (showBulkActions) setSelectedIds(new Set());
            }}
            className={`p-1.5 rounded text-slate-600 shrink-0 ${showBulkActions ? 'bg-brand-red text-white' : 'hover:bg-slate-100'}`}
            title="Mehrfachauswahl"
            data-testid="uv-bulk-toggle"
          >
            <ListChecks className="w-4 h-4" />
          </button>
      </div>

      {(searchMode || searchActive) && (
        <EnrollmentCriteriaPanel
          criteria={fieldQuery}
          onCriteriaChange={setFieldQuery}
          onSearch={(next) => runFieldSearch(next)}
          onClear={clearFieldSearch}
          onCancel={() => {
            setSearchMode(false);
            if (!searchActive) setFieldQuery({});
          }}
          catalogs={suggestCatalogs}
          searchActive={searchActive}
          resultCount={viewingEvents ? eventRecords.length : totalCount}
          resultUnit={viewingEvents ? 'Veranstaltung(en)' : 'Belegung(en)'}
        />
      )}

      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm text-amber-800 hover:text-amber-900"
              data-testid="uv-bulk-select-all"
            >
              {selectedIds.size === enrollments.length && enrollments.length > 0 ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              {selectedIds.size === enrollments.length && enrollments.length > 0 ? 'Alle abwählen' : 'Alle auswählen'}
            </button>
            <span className="text-sm text-amber-700">
              {selectedIds.size} von {enrollments.length} ausgewählt
            </span>
          </div>
          
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-amber-700 mr-2">Status setzen:</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkStatusChange('active')}
                disabled={bulkActionLoading}
                className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              >
                Aktiv
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkStatusChange('cancelled')}
                disabled={bulkActionLoading}
                className="h-7 text-xs bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              >
                Gekündigt
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkStatusChange('paused')}
                disabled={bulkActionLoading}
                className="h-7 text-xs bg-amber-50 border-amber-300 text-amber-700 hover:bg-brand-red/10"
              >
                Pausiert
              </Button>
              <div className="w-px h-6 bg-amber-300 mx-2" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setBulkEvent({
                    teacher_id: '',
                    day_of_week: '',
                    start_time: '',
                    duration_minutes: '',
                    rhythm: '',
                    room_id: '',
                    location_id: '',
                    valid_from: '',
                  });
                  setBulkEventOpen(true);
                }}
                disabled={bulkActionLoading}
                className="h-7 text-xs"
                data-testid="uv-bulk-event"
              >
                Veranstaltung ändern
              </Button>
              <div className="w-px h-6 bg-amber-300 mx-2" />
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowBulkDeleteDialog(true)}
                disabled={bulkActionLoading}
                className="h-7 text-xs"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Löschen ({selectedIds.size})
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Left Quicklinks Rail (nur wenn Quicklinks definiert) */}
        <QuicklinksRail quicklinks={quicklinks} onEdit={() => setQuicklinksOpen(true)} />
        <QuicklinksEditDialog
          open={quicklinksOpen}
          onOpenChange={setQuicklinksOpen}
          quicklinks={quicklinks}
          onSave={saveQuicklinks}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Tabs */}
          <div className="bg-white border-b border-slate-200 px-4 flex items-center justify-between">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="h-10 bg-transparent">
                <TabsTrigger value="events" className="text-xs py-2 px-3">Veranstaltungen</TabsTrigger>
                <TabsTrigger value="enrollments" className="text-xs py-2 px-3">Belegungen</TabsTrigger>
                <TabsTrigger value="billing" className="text-xs py-2 px-3">Abrechnung</TabsTrigger>
                <TabsTrigger value="overview" className="text-xs py-2 px-3" data-testid="uv-tab-overview">Übersicht</TabsTrigger>
              </TabsList>
            </Tabs>
            {quicklinks.length === 0 && (
              <button
                onClick={() => setQuicklinksOpen(true)}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 py-2"
                title="Eigene Quicklinks definieren"
                data-testid="quicklinks-define-btn"
              >
                <Settings className="w-3.5 h-3.5" />
                Quicklinks
              </button>
            )}
          </div>

          {/* Content Area — Belegungen: kein Seiten-Scroll, kompakt in Viewport */}
          <div className={`flex-1 min-h-0 p-2 ${activeTab === 'enrollments' ? 'overflow-hidden' : 'overflow-auto'}`}>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : !student && !selectedEnrollment && activeTab !== 'overview' ? (
              <div className="text-center text-slate-500 py-8">
                <Search className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="font-medium text-sm">Keine Belegung ausgewählt</p>
                <p className="text-xs mt-1">Verwenden Sie die Suche oben, um einen Teilnehmer zu finden, oder wechseln Sie zu „Übersicht"</p>
              </div>
            ) : activeTab === 'events' ? (
              /* Veranstaltungen Tab — FileMaker-Layout: Kopf + Übersicht mit 3 Tabellen */
              <div className="bg-white rounded border border-slate-200">
                {/* Suchmodus: Kriterien direkt in Veranstaltungs-Felder eingeben */}
                {searchMode ? (
                  <div className="p-3 border-b border-slate-200 bg-slate-50" data-testid="vst-search-header">
                    <div className="grid grid-cols-12 gap-2">
                      <FormField label="Bezeichnung" className="col-span-6" {...sf('q_bezeichnung')} />
                      <FormField label="Fach" className="col-span-3" {...sf('q_fach')} />
                      <FormField label="Tarif" className="col-span-3" {...sf('q_unterrichtsform')} />
                      <FormField label="Lehrkraft" className="col-span-4" {...sf('q_lehrkraft')} />
                      <FormField label="Termin (z.B. Mo)" className="col-span-2" {...sf('q_termin')} />
                      <FormField label="Zeit" className="col-span-2" {...sf('q_zeit')} />
                      <FormField label="Dauer" className="col-span-2" {...sf('q_dauer')} />
                      <FormField label="Raum" className="col-span-2" {...sf('q_raum')} />
                      <FormField label="Unterrichtsort" className="col-span-2" {...sf('q_standort', 'Standort…')} />
                    </div>
                  </div>
                ) : (
                (() => {
                  const active = visibleCourseEnrollments.filter((e) => e.status === 'active');
                  const starts = visibleCourseEnrollments.map((e) => e.start_date).filter(Boolean).sort();
                  const ends = visibleCourseEnrollments.map((e) => e.end_date);
                  const allEnded = visibleCourseEnrollments.length > 0 && ends.every(Boolean);
                  const basis = belegungsBasis(course?.name, course?.max_capacity, active.length);
                  return (
                    <div className="p-3 border-b border-slate-200 bg-slate-50/60" data-testid="vst-header">
                      {course?.id && (
                        <div className="flex justify-end mb-2">
                          <button
                            type="button"
                            onClick={() => navigate(`/courses/${course.id}`)}
                            data-testid="open-course-from-event"
                            className="inline-flex items-center h-8 px-3 rounded-md border border-slate-300 bg-white text-xs font-semibold text-slate-800 hover:border-brand-red hover:text-brand-red"
                          >
                            Kurs öffnen
                          </button>
                        </div>
                      )}
                      <div className="grid grid-cols-12 gap-x-4 gap-y-1.5">
                        <FmField label="Beginn" value={starts.length ? formatDate(starts[0]) : '—'} className="col-span-3" testId="vst-beginn" />
                        <FmField label="Ende" value={allEnded ? formatDate(ends.filter(Boolean).sort().slice(-1)[0]) : '—'} className="col-span-3" testId="vst-ende" />
                        <FmField label="Kurs Nr." value={course?.id?.slice(0, 8)} className="col-span-6" testId="vst-kursnr" />
                        {inlineRow?.kind === 'name' ? (
                          <div className="col-span-6 flex items-center gap-2">
                            <span className="text-[10px] text-slate-500 w-24 shrink-0 text-right">Bezeichnung</span>
                            <div data-testid="vst-bezeichnung" className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1 min-h-[26px]">
                              <input
                                data-testid="vst-bezeichnung-input"
                                aria-label="Bezeichnung"
                                className="w-full bg-transparent outline-none font-medium text-slate-800"
                                value={inlineRow.draft.course_name}
                                onChange={(ev) => patchInline({ course_name: ev.target.value })}
                              />
                            </div>
                            <RowSaveCancel
                              saveTestId="vst-save-bezeichnung"
                              onSave={saveName}
                              onCancel={() => setInlineRow(null)}
                            />
                          </div>
                        ) : (
                          <FmField
                            label="Bezeichnung"
                            value={courseDisplayName(course?.name, course?.subject_name) || '—'}
                            className="col-span-6"
                            testId="vst-bezeichnung"
                            trailing={course && !editingEvent ? (
                              <RecordPencil
                                testId="vst-edit-bezeichnung"
                                title="Bezeichnung bearbeiten"
                                onClick={beginEditName}
                              />
                            ) : null}
                          />
                        )}
                        <FmField label="Kommentar" value={course?.description || '—'} className="col-span-6" testId="vst-kommentar" />
                        <FmField label="Fach" value={subjectBaseName(course?.subject_name)} className="col-span-6" testId="vst-fach" />
                        <div className="col-span-6 flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 w-24 shrink-0 text-right">Belegungen</span>
                          <span data-testid="vst-belegungen" className="w-12 text-center text-xs font-medium bg-slate-100 border border-slate-200 rounded px-2 py-1">{basis.belegungen}</span>
                          <span className="text-[10px] text-slate-500">min</span>
                          <span className="w-12 text-center text-xs font-medium bg-white border border-slate-200 rounded px-2 py-1">—</span>
                          <span className="text-[10px] text-slate-500">max</span>
                          <span data-testid="vst-max" className="w-12 text-center text-xs font-medium bg-white border border-slate-200 rounded px-2 py-1">{basis.max ?? '—'}</span>
                          <span className="text-[10px] text-slate-500">frei</span>
                          <span data-testid="vst-frei" className="w-12 text-center text-xs font-medium bg-slate-100 border border-slate-200 rounded px-2 py-1">{basis.frei ?? '—'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()
                )}
                <SubTabs
                  value={vstSub}
                  onChange={setVstSub}
                  items={[
                    { value: 'uebersicht', label: 'Übersicht' },
                    { value: 'teilnehmer', label: 'Teilnehmer:innen' },
                    { value: 'terminliste', label: 'Terminliste', disabled: true },
                    { value: 'bemerkung', label: 'Bemerkung', disabled: true },
                    { value: 'veroeffentlichen', label: 'Veröffentlichen' },
                    { value: 'tarif', label: 'Tarif' },
                    { value: 'statistik', label: 'Statistik', disabled: true },
                    { value: 'chart', label: 'Chart', disabled: true },
                    { value: 'kontierung', label: 'Kontierung', disabled: true },
                  ]}
                />
                {vstSub === 'veroeffentlichen' && (
                  <CoursePublishTab courseId={course?.id} />
                )}
                <div className="p-4" hidden={vstSub === 'veroeffentlichen'}>
                  {vstSub === 'uebersicht' && (
                    <div className="space-y-5">
                      {!searchMode && course && editingEvent && (
                        <div className="flex items-start justify-between gap-3" data-testid="vst-edit-bar">
                          <p className="text-[11px] text-slate-500 leading-snug">
                            Lehrkraft, Termin und Raum gelten für alle Belegungen dieser Veranstaltung.
                            Der bisherige Stand bleibt bis zum Datum „gültig ab“ erhalten.
                          </p>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              data-testid="vst-save"
                              title="Veranstaltung speichern"
                              onClick={() => handleSaveEvent()}
                              className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                            >
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Abbrechen"
                              onClick={() => { setEditingEvent(false); setEditedEvent(null); }}
                              className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                      {editingEvent && editedEvent && !searchMode && (
                        <div className="grid grid-cols-4 gap-x-2 gap-y-1 border border-slate-200 rounded p-2" data-testid="vst-edit-form">
                          <FormField
                            label="Bezeichnung"
                            value={editedEvent.course_name}
                            className="col-span-4"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, course_name: v })}
                          />
                          <SelectField
                            label="Fach"
                            value={editedEvent.subject_id}
                            options={subjects}
                            className="col-span-2"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, subject_id: v })}
                          />
                          <SelectField
                            label="Tarif"
                            value={editedEvent.tariff_id}
                            options={tariffs}
                            className="col-span-2"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, tariff_id: v })}
                          />
                          <SelectField
                            label="Lehrkraft"
                            value={editedEvent.teacher_id}
                            options={teachers}
                            className="col-span-2"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, teacher_id: v })}
                            testId="vst-teacher"
                          />
                          <SelectField
                            label="Termin"
                            value={editedEvent.day_of_week?.toString()}
                            options={WEEKDAY_OPTIONS}
                            className="col-span-1"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, day_of_week: parseInt(v, 10) })}
                            testId="vst-day"
                          />
                          <FormField
                            label="Zeit"
                            value={editedEvent.start_time || ''}
                            className="col-span-1"
                            editable
                            type="time"
                            onChange={(v) => setEditedEvent({ ...editedEvent, start_time: v })}
                            data-testid="vst-zeit"
                          />
                          <SelectField
                            label="Dauer"
                            value={editedEvent.duration_minutes?.toString()}
                            options={DURATION_OPTIONS}
                            className="col-span-1"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, duration_minutes: parseInt(v, 10) })}
                          />
                          <SelectField
                            label="Turnus"
                            value={parseCourseRhythm(editedEvent.rhythm)}
                            options={TURNUS_OPTIONS}
                            className="col-span-1"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, rhythm: v })}
                            testId="vst-turnus"
                          />
                          <SelectField
                            label="Unterrichtsort"
                            value={editedEvent.location_id}
                            options={locations}
                            className="col-span-2"
                            editable
                            onChange={(v) => {
                              const roomOk = rooms.some((r) => r.value === editedEvent.room_id && (!r.locationId || r.locationId === v));
                              setEditedEvent({
                                ...editedEvent,
                                location_id: v,
                                room_id: roomOk ? editedEvent.room_id : '',
                              });
                            }}
                          />
                          <SelectField
                            label="Raum"
                            value={editedEvent.room_id}
                            options={rooms.filter((r) => !editedEvent.location_id || !r.locationId || r.locationId === editedEvent.location_id)}
                            className="col-span-2"
                            editable
                            onChange={(v) => setEditedEvent({ ...editedEvent, room_id: v })}
                            testId="vst-room"
                          />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Input
                          value={vstEntryQuery}
                          onChange={(e) => setVstEntryQuery(e.target.value)}
                          placeholder="Einträge durchsuchen…"
                          className="h-8 text-xs max-w-sm"
                          data-testid="vst-entry-search"
                        />
                      </div>
                      {/* 1) Teilnehmer:innen */}
                      <div data-testid="vst-teilnehmer-tabelle">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-slate-300 text-slate-500 text-[10px] uppercase tracking-wide">
                              <th className="text-left py-1.5 px-2 font-medium w-24">Eintritt</th>
                              <th className="text-left py-1.5 px-2 font-medium w-24">Austritt</th>
                              <th className="text-left py-1.5 px-2 font-medium">Name</th>
                              <th className="text-left py-1.5 px-2 font-medium w-28">Geburtsdatum</th>
                              <th className="text-right py-1.5 px-2 font-medium w-12">Alter</th>
                              <th className="text-left py-1.5 px-2 font-medium w-32">Instrument</th>
                              <th className="whitespace-nowrap" />
                            </tr>
                          </thead>
                          <tbody>
                            {visibleCourseEnrollments.length === 0 ? (
                              <tr><td colSpan={7} className="py-3 px-2 text-slate-400 italic">Keine Teilnehmer:innen</td></tr>
                            ) : visibleCourseEnrollments.filter((e) => entryNameMatches(vstEntryQuery, e.student)).map((e) => {
                              const s = e.student || {};
                              const isCurrent = s.id && s.id === student?.id;
                              const editingStudent = inlineRow?.kind === 'student' && inlineRow.id === e.id;
                              const dob = editingStudent ? inlineRow.draft.date_of_birth : s.date_of_birth;
                              return (
                                <tr key={e.id} data-testid={`vst-teilnehmer-row-${e.id}`}
                                    className={`border-b border-slate-100 ${isCurrent ? 'bg-brand-red/5' : 'hover:bg-slate-50'}`}>
                                  <td className="py-1.5 px-2 bg-amber-50/60">
                                    {editingStudent ? (
                                      <DateInput
                                        value={inlineRow.draft.start_date}
                                        onChange={(ev) => patchInline({ start_date: ev.target.value })}
                                        className="h-7 text-xs px-1 py-0"
                                        aria-label="Eintritt"
                                      />
                                    ) : (formatDate(e.start_date) || '—')}
                                  </td>
                                  <td className="py-1.5 px-2">
                                    {editingStudent ? (
                                      <DateInput
                                        value={inlineRow.draft.end_date}
                                        onChange={(ev) => patchInline({ end_date: ev.target.value })}
                                        className="h-7 text-xs px-1 py-0"
                                        aria-label="Austritt"
                                      />
                                    ) : (formatDate(e.end_date) || '')}
                                  </td>
                                  <td className="py-1.5 px-2 font-medium">
                                    {editingStudent ? (
                                      <div className="flex gap-1">
                                        <input
                                          aria-label="Nachname"
                                          className={rowCellInput}
                                          value={inlineRow.draft.last_name}
                                          onChange={(ev) => patchInline({ last_name: ev.target.value })}
                                        />
                                        <input
                                          aria-label="Vorname"
                                          className={rowCellInput}
                                          value={inlineRow.draft.first_name}
                                          onChange={(ev) => patchInline({ first_name: ev.target.value })}
                                        />
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="text-left text-brand-red hover:underline"
                                        onClick={() => openLinkedEnrollment(e.id)}
                                        data-testid={`vst-open-enrollment-${e.id}`}
                                      >
                                        {s.last_name ? `${s.last_name}, ${s.first_name}` : '—'}
                                      </button>
                                    )}
                                  </td>
                                  <td className="py-1.5 px-2">
                                    {editingStudent ? (
                                      <DateInput
                                        value={inlineRow.draft.date_of_birth}
                                        onChange={(ev) => patchInline({ date_of_birth: ev.target.value })}
                                        className="h-7 text-xs px-1 py-0"
                                        aria-label="Geburtsdatum"
                                      />
                                    ) : (formatDate(s.date_of_birth) || '—')}
                                  </td>
                                  <td className="py-1.5 px-2 text-right text-slate-500 font-medium">{ageFromDob(dob) ?? ''}</td>
                                  <td className="py-1.5 px-2 text-slate-500">—</td>
                                  <td className="py-1 px-1 text-right">
                                    {editingStudent ? (
                                      <RowSaveCancel
                                        saveTestId={`vst-save-teilnehmer-${e.id}`}
                                        onSave={saveStudentRow}
                                        onCancel={() => setInlineRow(null)}
                                      />
                                    ) : !searchMode && (
                                      <RecordPencil
                                        testId={`vst-edit-teilnehmer-${e.id}`}
                                        title="Teilnehmer:in bearbeiten"
                                        onClick={() => beginEditStudentRow(e)}
                                      />
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* 2) Termin & Ort */}
                      <div data-testid="vst-termin-tabelle">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-slate-300 text-slate-500 text-[10px] uppercase tracking-wide">
                              <th className="text-left py-1.5 px-2 font-medium w-24">von</th>
                              <th className="text-left py-1.5 px-2 font-medium w-24">bis</th>
                              <th className="text-left py-1.5 px-2 font-medium w-36">Termin</th>
                              <th className="text-right py-1.5 px-2 font-medium w-14">Dauer</th>
                              <th className="text-right py-1.5 px-2 font-medium w-14">Turnus</th>
                              <th className="text-left py-1.5 px-2 font-medium">Unterrichtsort</th>
                              <th className="text-left py-1.5 px-2 font-medium w-32">Raum</th>
                              <th className="text-left py-1.5 px-2 font-medium w-20">Areal</th>
                              <th className="text-right py-1.5 px-2 font-medium w-28">Limitierte Anzahl</th>
                              <th className="whitespace-nowrap" />
                            </tr>
                          </thead>
                          <tbody>
                            {(coursePeriods.length ? coursePeriods : [{
                              id: 'live',
                              valid_from: null,
                              valid_until: null,
                              day_of_week: course?.day_of_week,
                              start_time: course?.start_time,
                              duration_minutes: course?.duration_minutes,
                              rhythm: course?.rhythm,
                              location_id: course?.location_id || '',
                              location_name: course?.location_name,
                              room_id: course?.room_id || '',
                              room_name: course?.room_name,
                            }]).map((p) => {
                              const rowId = p.id || 'live';
                              const editingTermin = inlineRow?.kind === 'termin' && inlineRow.id === rowId;
                              const durationValue = editingTermin ? String(inlineRow.draft.duration_minutes ?? '') : '';
                              const durationOptions = durationValue && !DURATION_OPTIONS.some((o) => o.value === durationValue)
                                ? [{ value: durationValue, label: `${durationValue} Min.` }, ...DURATION_OPTIONS]
                                : DURATION_OPTIONS;
                              const roomOptions = rooms.filter((r) => !inlineRow?.draft?.location_id || !r.locationId || r.locationId === inlineRow.draft.location_id);
                              return (
                              <tr key={p.id || `${p.valid_from}-${p.teacher_id}`} className="border-b border-slate-100"
                                  data-testid={`vst-termin-row-${rowId}`}>
                                <td className="py-1.5 px-2 bg-amber-50/60">
                                  {editingTermin ? (
                                    <DateInput
                                      value={inlineRow.draft.valid_from}
                                      onChange={(ev) => patchInline({ valid_from: ev.target.value })}
                                      className="h-7 text-xs px-1 py-0"
                                      aria-label="gültig ab"
                                    />
                                  ) : (p.valid_from ? formatDate(p.valid_from) : '—')}
                                </td>
                                <td className="py-1.5 px-2">
                                  {editingTermin ? (
                                    <DateInput
                                      value={inlineRow.draft.valid_until}
                                      onChange={(ev) => patchInline({ valid_until: ev.target.value })}
                                      className="h-7 text-xs px-1 py-0"
                                      aria-label="bis"
                                    />
                                  ) : (p.valid_until ? formatDate(p.valid_until) : '')}
                                </td>
                                <td className="py-1.5 px-2 font-medium">
                                  {editingTermin ? (
                                    <div className="flex gap-1">
                                      <select
                                        aria-label="Wochentag"
                                        className={rowCellInput}
                                        value={inlineRow.draft.day_of_week === '' || inlineRow.draft.day_of_week == null ? '' : String(inlineRow.draft.day_of_week)}
                                        onChange={(ev) => patchInline({ day_of_week: ev.target.value })}
                                      >
                                        <option value="">—</option>
                                        {WEEKDAY_OPTIONS.map((o) => (
                                          <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                      </select>
                                      <input
                                        type="time"
                                        aria-label="Uhrzeit"
                                        className={rowCellInput}
                                        value={formatTime(inlineRow.draft.start_time) || ''}
                                        onChange={(ev) => patchInline({ start_time: ev.target.value })}
                                      />
                                    </div>
                                  ) : (p.day_of_week != null && p.start_time
                                    ? formatWeekdayTimeRange(
                                        p.day_of_week,
                                        p.start_time,
                                        addMinutesToTime(p.start_time, p.duration_minutes),
                                      )
                                    : (p.day_of_week != null ? WEEKDAYS_SHORT[p.day_of_week] : '—'))}
                                </td>
                                <td className="py-1.5 px-2 text-right font-medium">
                                  {editingTermin ? (
                                    <select
                                      aria-label="Dauer"
                                      className={rowCellInput}
                                      value={durationValue}
                                      onChange={(ev) => patchInline({ duration_minutes: ev.target.value })}
                                    >
                                      <option value="">—</option>
                                      {durationOptions.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                      ))}
                                    </select>
                                  ) : (p.duration_minutes ?? '—')}
                                </td>
                                <td className="py-1.5 px-2 text-right font-medium">
                                  {editingTermin ? (
                                    <select
                                      data-testid="vst-turnus-trigger"
                                      aria-label="Turnus"
                                      className={rowCellInput}
                                      value={inlineRow.draft.rhythm}
                                      onChange={(ev) => patchInline({ rhythm: ev.target.value })}
                                    >
                                      {TURNUS_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                      ))}
                                    </select>
                                  ) : rhythmToTurnus(p.rhythm)}
                                </td>
                                <td className="py-1.5 px-2">
                                  {editingTermin ? (
                                    <select
                                      aria-label="Unterrichtsort"
                                      className={rowCellInput}
                                      value={inlineRow.draft.location_id || ''}
                                      onChange={(ev) => {
                                        const v = ev.target.value;
                                        const roomOk = rooms.some((r) => r.value === inlineRow.draft.room_id && (!r.locationId || r.locationId === v));
                                        patchInline({ location_id: v, room_id: roomOk ? inlineRow.draft.room_id : '' });
                                      }}
                                    >
                                      <option value="">—</option>
                                      {locations.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                      ))}
                                    </select>
                                  ) : (p.location_name || (rowId === 'live' ? course?.location_name : '') || '—')}
                                </td>
                                <td className="py-1.5 px-2">
                                  {editingTermin ? (
                                    <select
                                      aria-label="Raum"
                                      className={rowCellInput}
                                      value={inlineRow.draft.room_id || ''}
                                      onChange={(ev) => patchInline({ room_id: ev.target.value })}
                                    >
                                      <option value="">—</option>
                                      {roomOptions.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                      ))}
                                    </select>
                                  ) : (p.room_name || course?.room_name || '—')}
                                </td>
                                <td className="py-1.5 px-2">{getArealLabel?.(course?.region) || course?.region || '—'}</td>
                                <td className="py-1.5 px-2 text-right font-medium">
                                  {editingTermin ? (
                                    <input
                                      aria-label="Limitierte Anzahl"
                                      type="number"
                                      min="1"
                                      className={rowCellInput}
                                      value={inlineRow.draft.max_capacity ?? ''}
                                      onChange={(ev) => patchInline({ max_capacity: ev.target.value })}
                                    />
                                  ) : (course?.max_capacity ?? '—')}
                                </td>
                                <td className="py-1 px-1 text-right">
                                  {editingTermin ? (
                                    <RowSaveCancel
                                      saveTestId={!p.valid_until ? 'vst-save' : `vst-save-termin-${rowId}`}
                                      saveAndNewTestId={`vst-save-and-new-termin-${rowId}`}
                                      onSave={() => saveTerminRow(false)}
                                      onSaveAndNew={() => saveTerminRow(true)}
                                      onCancel={() => setInlineRow(null)}
                                    />
                                  ) : !searchMode && course && !editingEvent && (
                                    <RecordPencil
                                      testId={!p.valid_until ? 'vst-edit' : `vst-edit-termin-${p.id || 'row'}`}
                                      title="Termin bearbeiten"
                                      onClick={() => beginEditTerminRow(p)}
                                    />
                                  )}
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* 3) Lehrkraft / Honorar / Instrument */}
                      <div data-testid="vst-lehrkraft-block">
                        <div className="flex border-b border-slate-300">
                          {[['lehrkraft', 'Lehrkraft'], ['honorar', 'Honorar'], ['instrument', 'Instrument']].map(([val, lbl]) => (
                            <button key={val} type="button" data-testid={`vst-lehrsub-${val}`}
                              onClick={() => setVstLehrSub(val)}
                              className={`flex-1 text-center text-[11px] py-1.5 border-t border-x rounded-t transition-colors ${
                                vstLehrSub === val
                                  ? 'bg-white border-slate-300 font-semibold text-slate-800 -mb-px'
                                  : 'bg-slate-50 border-transparent text-slate-500 hover:text-slate-700'
                              }`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                        {vstLehrSub === 'lehrkraft' && (
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="border-b border-slate-300 text-slate-500 text-[10px] uppercase tracking-wide">
                                <th className="text-left py-1.5 px-2 font-medium w-24">von</th>
                                <th className="text-left py-1.5 px-2 font-medium w-24">bis</th>
                                <th className="text-left py-1.5 px-2 font-medium">Lehrkraft</th>
                                <th className="text-left py-1.5 px-2 font-medium w-24">Vertretung</th>
                                <th className="text-left py-1.5 px-2 font-medium w-16">Pool</th>
                                <th className="text-left py-1.5 px-2 font-medium w-24">Kategorie</th>
                                <th className="text-right py-1.5 px-2 font-medium w-14">Dauer</th>
                                <th className="text-left py-1.5 px-2 font-medium w-14">Fkt.</th>
                                <th className="text-left py-1.5 px-2 font-medium w-20">Zusatz</th>
                                <th className="whitespace-nowrap" />
                              </tr>
                            </thead>
                            <tbody>
                              {(coursePeriods.length ? coursePeriods : [{
                                id: 'live',
                                valid_from: null,
                                valid_until: null,
                                teacher_id: course?.teacher_id,
                                teacher_name: course?.teacher_name,
                                duration_minutes: course?.duration_minutes,
                              }]).map((p) => {
                                const rowId = p.id || 'live';
                                const editingTeacher = inlineRow?.kind === 'teacher' && inlineRow.id === rowId;
                                const currentTeacherId = editingTeacher ? (inlineRow.draft.teacher_id || '') : '';
                                const teacherLabel = p.teacher_name || course?.teacher_name || 'Lehrkraft';
                                const teacherOptions = currentTeacherId && !teachers.some((t) => t.value === currentTeacherId)
                                  ? [{ value: currentTeacherId, label: teacherLabel }, ...teachers]
                                  : teachers;
                                return (
                                <tr key={p.id || `${p.valid_from}-t`} className="border-b border-slate-100"
                                    data-testid={`vst-lehrkraft-row-${rowId}`}>
                                  <td className="py-1.5 px-2 bg-amber-50/60">
                                    {editingTeacher ? (
                                      <DateInput
                                        value={inlineRow.draft.valid_from}
                                        onChange={(ev) => patchInline({ valid_from: ev.target.value })}
                                        className="h-7 text-xs px-1 py-0"
                                        aria-label="gültig ab"
                                      />
                                    ) : (p.valid_from ? formatDate(p.valid_from) : '—')}
                                  </td>
                                  <td className="py-1.5 px-2">
                                    {editingTeacher ? (
                                      <DateInput
                                        value={inlineRow.draft.valid_until}
                                        onChange={(ev) => patchInline({ valid_until: ev.target.value })}
                                        className="h-7 text-xs px-1 py-0"
                                        aria-label="bis"
                                      />
                                    ) : (p.valid_until ? formatDate(p.valid_until) : '')}
                                  </td>
                                  <td className="py-1.5 px-2 font-medium" data-testid="vst-lehrkraft-name">
                                    {editingTeacher ? (
                                      <select
                                        data-testid={`vst-lehrkraft-select-${rowId}`}
                                        aria-label="Lehrkraft"
                                        className={rowCellInput}
                                        value={currentTeacherId}
                                        onChange={(ev) => patchInline({ teacher_id: ev.target.value })}
                                      >
                                        <option value="">—</option>
                                        {teacherOptions.map((o) => (
                                          <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="inline-flex items-center gap-1">
                                        {p.teacher_name || course?.teacher_name || '—'}
                                        {(p.teacher_id || course?.teacher_id) && (
                                          <button
                                            type="button"
                                            title="Lehrkraft-Wochenkalender öffnen"
                                            aria-label={`Kalender ${teacherLabel}`}
                                            className="rounded p-0.5 text-brand-red hover:bg-brand-red/10"
                                            onClick={() => navigate(teacherWeekCalendarUrl(
                                              p.teacher_id || course.teacher_id,
                                              p.valid_from || null,
                                            ))}
                                            data-testid={`vst-teacher-calendar-${rowId}`}
                                          >
                                            <Calendar className="h-3.5 w-3.5" />
                                          </button>
                                        )}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-1.5 px-2 text-slate-400">—</td>
                                  <td className="py-1.5 px-2 text-slate-400">—</td>
                                  <td className="py-1.5 px-2 text-slate-400">—</td>
                                  <td className="py-1.5 px-2 text-right font-medium">{p.duration_minutes ?? '—'}</td>
                                  <td className="py-1.5 px-2 text-slate-400">—</td>
                                  <td className="py-1.5 px-2 text-slate-400">—</td>
                                  <td className="py-1 px-1 text-right">
                                    {editingTeacher ? (
                                      <RowSaveCancel
                                        saveTestId={`vst-save-lehrkraft-${rowId}`}
                                        saveAndNewTestId={`vst-save-and-new-lehrkraft-${rowId}`}
                                        onSave={() => saveTeacherRow(false)}
                                        onSaveAndNew={() => saveTeacherRow(true)}
                                        onCancel={() => setInlineRow(null)}
                                      />
                                    ) : !searchMode && course && !editingEvent && (
                                      <RecordPencil
                                        testId={`vst-edit-lehrkraft-${rowId}`}
                                        title="Lehrkraft bearbeiten"
                                        onClick={() => beginEditTeacherRow(p)}
                                      />
                                    )}
                                  </td>
                                </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                        {vstLehrSub === 'honorar' && (
                          <div className="border-b border-slate-100 text-xs">
                            <div className="flex items-center justify-between px-2 py-2 bg-slate-50">
                              <span className="text-slate-600">
                                Individuelle Regeln ersetzen für diese Veranstaltung die Sätze des Lehrerprofils.
                              </span>
                              <button
                                type="button"
                                className="text-brand-red font-medium hover:underline"
                                aria-label="Honorarregel hinzufügen"
                                onClick={beginHonorarium}
                              >
                                + Regel
                              </button>
                            </div>
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="border-b border-slate-200 text-[10px] uppercase text-slate-500">
                                  <th className="text-left py-1.5 px-2">von</th>
                                  <th className="text-left py-1.5 px-2">bis</th>
                                  <th className="text-left py-1.5 px-2">Individuelles Veranstaltungshonorar</th>
                                  <th className="text-left py-1.5 px-2">Notiz</th>
                                </tr>
                              </thead>
                              <tbody>
                                {courseHonoraria.map((row) => (
                                  <tr key={row.id} className="border-b border-slate-100">
                                    <td className="py-1.5 px-2">{formatDate(row.valid_from)}</td>
                                    <td className="py-1.5 px-2">{row.valid_until ? formatDate(row.valid_until) : 'offen'}</td>
                                    <td className="py-1.5 px-2 font-medium">{formatHonorarium(row)}</td>
                                    <td className="py-1.5 px-2 text-slate-500">{row.notes || '—'}</td>
                                  </tr>
                                ))}
                                {!courseHonoraria.length && !honorariumDraft && (
                                  <tr><td colSpan={4} className="py-3 px-2 text-slate-400 italic">
                                    Keine individuelle Regel – es gelten die Sätze des Lehrerprofils.
                                  </td></tr>
                                )}
                                {honorariumDraft && (
                                  <tr className="border-b border-slate-200 bg-amber-50/50">
                                    <td className="p-1">
                                      <DateInput
                                        aria-label="Honorar gültig ab"
                                        value={honorariumDraft.valid_from}
                                        onChange={(e) => setHonorariumDraft((d) => ({ ...d, valid_from: e.target.value }))}
                                        className="h-7 text-xs"
                                      />
                                    </td>
                                    <td className="p-1">
                                      <DateInput
                                        aria-label="Honorar gültig bis"
                                        value={honorariumDraft.valid_until}
                                        onChange={(e) => setHonorariumDraft((d) => ({ ...d, valid_until: e.target.value }))}
                                        className="h-7 text-xs"
                                      />
                                    </td>
                                    <td className="p-1">
                                      <div className="flex gap-1">
                                        <select
                                          aria-label="Honorarart"
                                          className={rowCellInput}
                                          value={honorariumDraft.kind}
                                          onChange={(e) => setHonorariumDraft((d) => ({ ...d, kind: e.target.value }))}
                                        >
                                          <option value="per_lesson">je Unterrichtseinheit</option>
                                          <option value="per_month">pro Monat</option>
                                          <option value="percent">% vom Umsatz</option>
                                          <option value="one_time">einmalig</option>
                                        </select>
                                        <input
                                          aria-label="Honorarbetrag"
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          className={`${rowCellInput} w-24`}
                                          value={honorariumDraft.amount}
                                          onChange={(e) => setHonorariumDraft((d) => ({ ...d, amount: e.target.value }))}
                                        />
                                      </div>
                                    </td>
                                    <td className="p-1">
                                      <div className="flex gap-1">
                                        <input
                                          aria-label="Honorarnotiz"
                                          className={rowCellInput}
                                          value={honorariumDraft.notes}
                                          onChange={(e) => setHonorariumDraft((d) => ({ ...d, notes: e.target.value }))}
                                        />
                                        <button type="button" aria-label="Honorarregel speichern"
                                          className="text-emerald-700 px-1" onClick={saveHonorarium}><Save className="w-4 h-4" /></button>
                                        <button type="button" aria-label="Honorarregel abbrechen"
                                          className="text-slate-500 px-1" onClick={() => setHonorariumDraft(null)}><X className="w-4 h-4" /></button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {vstLehrSub === 'instrument' && (
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="border-b border-slate-300 text-slate-500 text-[10px] uppercase tracking-wide">
                                <th className="text-left py-1.5 px-2 font-medium">Instrument</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-slate-100">
                                <td className="py-1.5 px-2 font-medium">{subjectBaseName(course?.subject_name) || '—'}</td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                  {vstSub === 'teilnehmer' && (
                    <div className="text-xs">
                      <p className="text-slate-500 mb-2">Alle Teilnehmer:innen dieser Veranstaltung — Klick öffnet Detail.</p>
                      <button
                        type="button"
                        onClick={() => navigate(`/courses/${course?.id}`)}
                        className="text-xs px-2 py-1 bg-brand-red/10 text-brand-red rounded hover:bg-brand-red/20"
                        data-testid="open-course-participants"
                      >
                        Kurs öffnen → Teilnehmer-Liste
                      </button>
                    </div>
                  )}
                  {vstSub === 'tarif' && (
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div className="border border-slate-200 rounded p-3">
                        <h3 className="text-[11px] font-bold uppercase tracking-wider text-brand-red mb-2">Tarif-Konfiguration</h3>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                          <div className="text-slate-500">Tarif</div>
                          <div className="font-medium">{tariffFormName(course?.tariff_name) || '—'}</div>
                          <div className="text-slate-500">Betrag</div>
                          <div className="font-medium">
                            {course?.tariff_amount != null ? `${formatDeNumber(course.tariff_amount)} €` : '—'}
                          </div>
                          <div className="text-slate-500">Abrechnung</div>
                          <div>{course?.tariff_billing_type || 'monthly'}</div>
                          <div className="text-slate-500">GEMA</div>
                          <div>{course?.tariff_gema_applicable ? '✓ +1,20 €/Mo' : '—'}</div>
                        </div>
                      </div>
                      <div className="text-slate-400 text-[11px] italic flex items-center justify-center">
                        Erwachsenenzuschlag und Sozialfonds verwaltbar unter „Gebührenverwaltung"
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'billing' ? (
              /* Abrechnung Tab — iMikel-Layout: Beträge/Summen/Rabatte/Ausfall/An-Abwesenheiten/Buchungen/Tarif */
              <div className="bg-white rounded border border-slate-200">
                <SubTabs
                  value={abrSub}
                  onChange={setAbrSub}
                  items={[
                    { value: 'betraege', label: 'Beträge' },
                    { value: 'summen', label: 'Summen' },
                    { value: 'rabatte', label: 'Rabatte & Zuschläge' },
                    { value: 'ausfall', label: 'Ausfall' },
                    { value: 'anwesenheit', label: 'An-/Abwesenheiten' },
                    { value: 'buchungen', label: 'Buchungen', disabled: true },
                    { value: 'tarif', label: 'Tarif' },
                  ]}
                />
                <div className="p-4">
                  {(() => {
                    const fmtName = (p) => {
                      if (!p) return '';
                      if (typeof p === 'string') return p;
                      return `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|,\s*$/g, '').trim();
                    };
                    const tnName = fmtName(student)
                      || abrechnung?.student_name
                      || fmtName(selectedEnrollment?.student)
                      || selectedEnrollment?.student_name
                      || '—';
                    const payerLabel = fmtName(guardian) || abrechnung?.payer_name || '—';
                    const courseLabel = courseDisplayName(
                      course?.name
                      || abrechnung?.course_name
                      || selectedEnrollment?.course_name
                      || selectedEnrollment?.course?.name
                      || '',
                      course?.subject_name,
                    ) || '—';
                    return (
                      <div className="mb-3 rounded border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs" data-testid="uv-abrechnung-person-header">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
                          <div>
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Teilnehmer:in</span>
                            <div className="font-semibold text-slate-900 truncate" title={tnName}>{tnName}</div>
                          </div>
                          <div>
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Zahlungspflichtige:r</span>
                            <div className="font-semibold text-slate-900 truncate" title={payerLabel}>{payerLabel}</div>
                          </div>
                          <div>
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Kurs / Belegung</span>
                            <div className="font-semibold text-slate-900 truncate" title={courseLabel}>{courseLabel}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                      {searchMode && (
                    <div className="mb-3 grid grid-cols-12 gap-2" data-testid="uv-abrechnung-search">
                      <FormField label="Rabattart" className="col-span-4" {...sf('q_rabatt', 'z. B. Familienpass')} />
                      <FormField label="Betrag" className="col-span-2" {...sf('q_rabatt_betrag', '15 € oder 10%')} />
                      <div className="col-span-3">
                        <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">Status</label>
                        <select
                          value={fieldQuery.q_rabatt_status || ''}
                          onChange={(e) => setFieldQuery((q) => ({ ...q, q_rabatt_status: e.target.value }))}
                          data-testid="search-field-q_rabatt_status"
                          className={`w-full h-6 text-xs rounded border px-2 ${searchInputClassName(fieldQuery.q_rabatt_status)}`}
                        >
                          <option value="">—</option>
                          <option value="aktiv">aktiv</option>
                          <option value="zukuenftig">zukünftig</option>
                          <option value="abgelaufen">abgelaufen</option>
                        </select>
                      </div>
                      <div className="col-span-3">
                        <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">Vorhanden</label>
                        <select
                          value={fieldQuery.q_rabatt_art || ''}
                          onChange={(e) => setFieldQuery((q) => ({ ...q, q_rabatt_art: e.target.value }))}
                          data-testid="search-field-q_rabatt_art"
                          className={`w-full h-6 text-xs rounded border px-2 ${searchInputClassName(fieldQuery.q_rabatt_art)}`}
                        >
                          <option value="">—</option>
                          <option value="any">irgendein Rabatt</option>
                          <option value="none">kein Rabatt</option>
                        </select>
                      </div>
                      <FormField label="Beginn" className="col-span-4" {...sf('q_rabatt_beginn', '01.01.2026-31.12.2026')} />
                      <FormField label="Ende" className="col-span-4" {...sf('q_rabatt_ende', 'Dezember 2026')} />
                      <FormField label="Gültig im Zeitraum" className="col-span-4" {...sf('q_rabatt_gueltig', 'Oktober 2026')} />
                      <FormField label="Laufzeit" className="col-span-4" {...sf('q_rabatt_laufzeit', 'unbefristet oder 12 Monate')} />
                      <FormField label="Aussteller" className="col-span-4" {...sf('q_rabatt_aussteller', 'Aussteller')} />
                      <FormField label="Ausweis-Nr." className="col-span-4" {...sf('q_rabatt_ausweis', 'Ausweisnummer')} />
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-brand-red">
                      {abrSub === 'betraege' ? 'Beträge' :
                       abrSub === 'summen' ? 'Summen' :
                       abrSub === 'rabatte' ? 'Rabatte & Zuschläge' :
                       abrSub === 'ausfall' ? 'Ausfall' :
                       abrSub === 'anwesenheit' ? 'An-/Abwesenheiten' :
                       'Tarif'} · {abrYear}
                    </div>
                    {(abrSub === 'betraege' || abrSub === 'summen' || abrSub === 'anwesenheit') && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setAbrYear(abrYear - 1)} className="h-6 w-6 p-0" data-testid="abr-year-prev-uv">‹</Button>
                        <span className="font-medium text-xs w-12 text-center">{abrYear}</span>
                        <Button size="sm" variant="ghost" onClick={() => setAbrYear(abrYear + 1)} className="h-6 w-6 p-0" data-testid="abr-year-next-uv">›</Button>
                      </div>
                    )}
                  </div>

                  {abrLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-red" />
                    </div>
                  ) : abrSub === 'betraege' ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="uv-abrechnung-betraege">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                          <tr>
                            <th className="text-left py-1.5 px-2">Monat</th>
                            <th className="text-left py-1.5 px-2">Kategorie</th>
                            <th className="text-right py-1.5 px-2">Wert</th>
                            <th className="text-right py-1.5 px-2">Betrag</th>
                            <th className="text-right py-1.5 px-2">Summe (Mo)</th>
                            <th className="text-right py-1.5 px-2">Lfd</th>
                          </tr>
                        </thead>
                        <tbody>
                          {abrechnung?.tariff_missing && (
                            <tr className="border-b border-slate-100">
                              <td colSpan={6} className="py-3 px-2 text-amber-800 bg-amber-50 text-center" data-testid="abrechnung-tariff-missing-hint">
                                Kein passender Tarif zur Unterrichtsform gefunden
                                {abrechnung?.course_name ? ` (Kurs: „${formatScheduleLabel(abrechnung.course_name)}“)` : ''}.
                                {' '}Bitte unter Stammdaten einen Tarif anlegen oder am Kurs manuell zuordnen.
                              </td>
                            </tr>
                          )}
                          {!abrechnung?.tariff_missing && (abrechnung?.months || []).length === 0 && (
                            <tr className="border-b border-slate-100">
                              <td colSpan={6} className="py-6 px-2 text-slate-400 italic text-center">
                                Keine Beträge für diesen Zeitraum
                              </td>
                            </tr>
                          )}
                          {(abrechnung?.months || []).flatMap((m, mi) =>
                            m.items.length === 0
                              ? [<tr key={`m-${mi}`} className="border-b border-slate-100 text-slate-400 italic">
                                  <td className="py-1.5 px-2 font-medium">{m.month_label}</td>
                                  <td className="py-1.5 px-2" colSpan={5}>— inaktiv —</td>
                                </tr>]
                              : m.items.map((it, ii) => (
                                  <tr key={`${mi}-${ii}`} className="border-b border-slate-100">
                                    <td className="py-1.5 px-2 font-medium text-slate-700">{ii === 0 ? m.month_label : ''}</td>
                                    <td className="py-1.5 px-2">{it.kategorie}</td>
                                    <td className="py-1.5 px-2 text-right font-medium text-slate-500">{it.wert != null && it.wert !== '' ? (typeof it.wert === 'string' && String(it.wert).includes('%') ? it.wert : formatDeNumber(it.wert)) : ''}</td>
                                    <td className="py-1.5 px-2 text-right font-medium">{formatDeNumber(it.betrag)}</td>
                                    <td className="py-1.5 px-2 text-right font-medium font-semibold">{ii === m.items.length - 1 ? formatDeNumber(m.month_total) : ''}</td>
                                    <td className="py-1.5 px-2 text-right font-medium text-slate-400">{ii === m.items.length - 1 ? formatDeNumber(m.cumulative) : ''}</td>
                                  </tr>
                                ))
                          )}
                        </tbody>
                        <tfoot>
                          <tr className="border-t-2 border-slate-300">
                            <td colSpan={4} className="py-2 px-2 text-right font-bold">Jahres-Summe</td>
                            <td className="py-2 px-2 text-right font-medium font-bold text-brand-red">
                              {abrechnung?.totals?.summe != null ? formatDeNumber(abrechnung.totals.summe) + ' €' : '—'}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : abrSub === 'summen' ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs" data-testid="uv-abrechnung-summen">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                          <tr>
                            <th className="text-left py-1.5 px-2">Monat</th>
                            <th className="text-right py-1.5 px-2">Netto</th>
                            <th className="text-right py-1.5 px-2">Mwst.</th>
                            <th className="text-right py-1.5 px-2">Summe</th>
                            <th className="text-right py-1.5 px-2">Lfd</th>
                            <th className="text-right py-1.5 px-2">Buchung</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(abrechnung?.months || []).map((m) => (
                            <tr key={m.month_label} className="border-b border-slate-100">
                              <td className="py-1.5 px-2 font-medium text-slate-700">{m.month_label}</td>
                              <td className="py-1.5 px-2 text-right font-medium">{formatDeNumber(m.month_total)}</td>
                              <td className="py-1.5 px-2 text-right font-medium text-slate-400">0,00</td>
                              <td className="py-1.5 px-2 text-right font-medium font-semibold">{formatDeNumber(m.month_total)}</td>
                              <td className="py-1.5 px-2 text-right font-medium text-slate-400">{formatDeNumber(m.cumulative)}</td>
                              <td className="py-1.5 px-2 text-right text-slate-300 italic">—</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : abrSub === 'ausfall' ? (
                    /* Ausfall-Tab: iMikel-style cancellation list */
                    <div className="space-y-3" data-testid="uv-ausfall">
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] text-slate-500">
                          {cancellations.length} Ausfall-Datensätze ·
                          Σ erstattet: <span className="font-medium font-semibold text-brand-red">
                            {formatDeNumber(cancellations.reduce((a, c) => a + (c.erstatten_amount || 0), 0))} €
                          </span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => setCancEditor({
                            cancelled_date: new Date().toISOString().slice(0, 10),
                            category: 'entschuldigt abwesend',
                            notes: '',
                            erteilt: false,
                            erstatten_quotient: 0.5,
                            erstatten_amount: '',
                          })}
                          className="h-7 text-xs bg-brand-red hover:bg-brand-red/90"
                          data-testid="uv-ausfall-add"
                        >
                          + Ausfall erfassen
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                            <tr>
                              <th className="text-left py-1.5 px-2">Nr.</th>
                              <th className="text-left py-1.5 px-2">Datum</th>
                              <th className="text-left py-1.5 px-2">Kategorie</th>
                              <th className="text-left py-1.5 px-2">Bemerkungen</th>
                              <th className="text-center py-1.5 px-2">Erteilt</th>
                              <th className="text-right py-1.5 px-2">Quotient</th>
                              <th className="text-right py-1.5 px-2">Erstatten</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {cancellations.length === 0 ? (
                              <tr><td colSpan={8} className="py-6 text-center text-slate-400 italic">Keine Ausfälle erfasst</td></tr>
                            ) : cancellations.map((c) => (
                              <tr key={c.id} className="border-b border-slate-100 hover:bg-brand-red/5">
                                <td className="py-1.5 px-2 font-medium text-slate-500">{c.sequence_no}</td>
                                <td className="py-1.5 px-2 font-medium">{formatDate(c.cancelled_date)}</td>
                                <td className="py-1.5 px-2">
                                  <span className="inline-flex items-center gap-1.5">
                                    <AttendanceStatusBadge
                                      source={{ category_code: c.category_code }}
                                      size="sm"
                                      testId={`ausfall-status-${c.id}`}
                                    />
                                    {c.category}
                                  </span>
                                </td>
                                <td className="py-1.5 px-2 text-slate-600 truncate max-w-[200px]" title={c.notes}>{c.notes || '—'}</td>
                                <td className="py-1.5 px-2 text-center">
                                  {c.erteilt ? <Badge className="text-[9px] py-0 px-1 bg-emerald-100 text-emerald-700 border-emerald-300">✓</Badge> : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="py-1.5 px-2 text-right font-medium">{formatDeNumber(c.erstatten_quotient)}</td>
                                <td className="py-1.5 px-2 text-right font-medium font-semibold text-brand-red">
                                  {formatDeNumber(c.erstatten_amount)} €
                                </td>
                                <td className="py-1.5 px-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => setCancEditor({ ...c })}
                                      className="text-brand-red hover:text-brand-red text-[10px]"
                                      data-testid={`ausfall-edit-${c.id}`}
                                    >
                                      Bearbeiten
                                    </button>
                                    <span className="text-slate-200">·</span>
                                    <button
                                      type="button"
                                      onClick={() => deleteCancellation(c.id)}
                                      className="text-rose-600 hover:text-rose-700 text-[10px]"
                                      data-testid={`ausfall-del-${c.id}`}
                                    >
                                      Löschen
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : abrSub === 'anwesenheit' ? (
                    /* An-/Abwesenheiten — iMikel Sub-Tab */
                    <div className="space-y-3" data-testid="uv-anwesenheit">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        {/* Filter buttons exactly as iMikel */}
                        <div className="flex items-center gap-1 text-[11px]">
                          {[
                            { value: 'all', label: 'alle' },
                            { value: 'not_finalized', label: 'nur nicht finalisierte' },
                          ].map((f) => (
                            <button
                              key={f.value}
                              type="button"
                              onClick={() => setAttendanceFilter(f.value)}
                              data-testid={`att-filter-${f.value}`}
                              className={`px-2 py-1 rounded transition-colors ${
                                attendanceFilter === f.value
                                  ? 'bg-brand-red text-white font-semibold'
                                  : 'text-slate-600 hover:bg-brand-red/10'
                              }`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <TurnusCreateButton
                            courseId={selectedEnrollment?.course_id}
                            visible={course?.day_of_week == null}
                            onSuccess={async () => {
                              await reloadAttendance();
                              if (selectedEnrollment?.course_id) {
                                try {
                                  const courseRes = await api.get(`/courses/${selectedEnrollment.course_id}`);
                                  setCourse(courseRes.data);
                                } catch { /* ignore */ }
                              }
                            }}
                            testId="uv-turnus-create"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={finalizeCapturedAttendance}
                            className="h-7 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                            data-testid="att-bulk-finalize"
                          >
                            Einträge finalisieren
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setAttEditor({
                              lesson_date: new Date().toISOString().slice(0, 10),
                              relocated_to_date: '',
                              erteilt: true,
                              online_flag: false,
                              notes: '',
                            })}
                            className="h-7 text-xs bg-brand-red hover:bg-brand-red/90"
                            data-testid="uv-att-add"
                          >
                            + Termin erfassen
                          </Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                            <tr>
                              <th className="text-left py-1.5 px-2">Datum</th>
                              <th className="text-left py-1.5 px-2">Anwesenheit</th>
                              <th className="text-left py-1.5 px-2">Verlegung</th>
                              <th className="text-left py-1.5 px-2">Lehrkraft</th>
                              <th className="text-center py-1.5 px-2">Online</th>
                              <th className="text-center py-1.5 px-2">Erteilt</th>
                              <th className="text-left py-1.5 px-2">Bemerkungen</th>
                              <th className="text-center py-1.5 px-2">Bearbeitung</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {attendance.length === 0 ? (
                              <tr><td colSpan={9} className="py-6 text-center text-slate-400 italic" data-testid="uv-att-empty">
                                {course?.day_of_week == null
                                  ? 'Keine Termine — bitte zuerst Wochentag/Turnus in den Belegungsdetails setzen oder „Turnus erstellen“.'
                                  : 'Keine Termine in diesem Jahr (Filter oder außerhalb des Belegungszeitraums).'}
                              </td></tr>
                            ) : attendance.map((a) => {
                              const rowKey = a.lesson_event_id || `planned-${a.lesson_date}`;
                              const rowLocked = !!a.is_finalized && attCorrecting?.rowKey !== rowKey;
                              return (
                              <tr key={rowKey} className="border-b border-slate-100 hover:bg-brand-red/5" data-testid={`att-row-${rowKey}`}>
                                <td className="py-1.5 px-2 font-medium">{formatDate(a.lesson_date)}</td>
                                <td className="py-1.5 px-2">
                                  {attendanceCaptured(a) ? (
                                    <AttendanceStatusBadge
                                      source={{
                                        category_code: a.category_code,
                                        cancel_category_code: a.cancel_category_code,
                                        status: a.teacher_status,
                                        relocated_to_date: a.relocated_to_date,
                                        erteilt: a.erteilt,
                                      }}
                                      size="sm"
                                      testId={`att-status-${rowKey}`}
                                    />
                                  ) : (
                                    <span className="text-slate-300" data-testid={`att-status-${rowKey}`}>—</span>
                                  )}
                                </td>
                                <td className="py-1.5 px-2 font-medium text-slate-500">
                                  {a.relocated_to_date && <div>auf {formatDate(a.relocated_to_date)}</div>}
                                  {a.relocated_from_date && <div>vom {formatDate(a.relocated_from_date)}</div>}
                                  {!a.relocated_to_date && !a.relocated_from_date && <span className="text-slate-300">—</span>}
                                </td>
                                <td className="py-1.5 px-2">{a.teacher_name || '—'}</td>
                                <td className="py-1.5 px-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={!!a.online_flag}
                                    disabled={rowLocked}
                                    onChange={(e) => toggleAttendanceField(a, 'online_flag', e.target.checked)}
                                    className={rowLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                                    title={rowLocked ? 'Finalisiert — zuerst korrigieren' : undefined}
                                    data-testid={`att-online-${rowKey}`}
                                  />
                                </td>
                                <td className="py-1.5 px-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={!!a.erteilt}
                                    disabled={rowLocked}
                                    onChange={(e) => toggleAttendanceField(a, 'erteilt', e.target.checked)}
                                    className={rowLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                                    title={rowLocked ? 'Finalisiert — zuerst korrigieren' : undefined}
                                    data-testid={`att-erteilt-${rowKey}`}
                                  />
                                </td>
                                <td className="py-1.5 px-2">
                                  <input
                                    key={`${rowKey}-${a.notes || ''}`}
                                    defaultValue={a.notes || ''}
                                    disabled={rowLocked}
                                    onBlur={(e) => saveAttendanceNotes(a, e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                    className="w-full h-6 text-xs border border-slate-200 rounded px-1 disabled:bg-slate-50"
                                    placeholder="Bemerkung"
                                    data-testid={`att-notes-${rowKey}`}
                                  />
                                </td>
                                <td className="py-1.5 px-2 text-center">
                                  {a.is_finalized ? (
                                    <Badge className={`text-[9px] py-0 px-1 border ${CHIP_CLASS.success}`}>finalisiert</Badge>
                                  ) : attendanceCaptured(a) ? (
                                    <Badge className={`text-[9px] py-0 px-1 border ${CHIP_CLASS.neutral}`} data-testid={`att-workflow-${rowKey}`}>nicht finalisiert</Badge>
                                  ) : (
                                    <Badge className={`text-[9px] py-0 px-1 border ${CHIP_CLASS.neutral}`} data-testid={`att-workflow-${rowKey}`}>offen</Badge>
                                  )}
                                </td>
                                <td className="py-1.5 px-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => setAusfallRow(a)}
                                      disabled={rowLocked}
                                      className="text-slate-700 hover:text-slate-900 text-[10px] disabled:opacity-40"
                                      data-testid={`att-ausfall-${rowKey}`}
                                    >
                                      Ausfall
                                    </button>
                                    <span className="text-slate-200">·</span>
                                    <button
                                      type="button"
                                      onClick={() => { setMoveRow(a); setMoveDate(''); }}
                                      disabled={rowLocked}
                                      className="text-slate-700 hover:text-slate-900 text-[10px] disabled:opacity-40"
                                      data-testid={`att-move-${rowKey}`}
                                    >
                                      Verlegen
                                    </button>
                                    {!a.is_finalized && (
                                      <>
                                        <span className="text-slate-200">·</span>
                                        <button
                                          type="button"
                                          onClick={() => finalizeAttendance(a)}
                                          className="text-emerald-600 hover:text-emerald-700 text-[10px]"
                                          data-testid={`att-finalize-${rowKey}`}
                                        >
                                          finalisieren
                                        </button>
                                      </>
                                    )}
                                    {a.is_finalized && (
                                      <button
                                        type="button"
                                        onClick={() => unlockAttendance(a)}
                                        className="text-amber-700 hover:text-amber-800 text-[10px]"
                                        data-testid={`att-unlock-${rowKey}`}
                                      >
                                        entsperren
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : abrSub === 'rabatte' ? (
                    <div data-testid="uv-abrechnung-rabatte">
                      <p className="text-xs text-slate-500 mb-3">
                        Nachweise und Zuschläge dieser Person. Über die schrittweise Suche oben lassen sich
                        alle Belegungen nach Name, Höhe oder „kein Rabatt“ UND-verknüpft filtern.
                      </p>
                      {student?.id ? (
                        <StudentDiscountsPanel
                          studentId={student.id}
                          enrollments={selectedEnrollment ? [selectedEnrollment] : []}
                        />
                      ) : (
                        <p className="text-sm text-slate-500">Keine Belegung ausgewählt.</p>
                      )}
                    </div>
                  ) : abrSub === 'tarif' ? (
                    <div className="text-xs grid grid-cols-2 gap-x-4 gap-y-1.5 max-w-md">
                      <div className="text-slate-500">Tarif</div>
                      <div className="font-medium">{tariffFormName(course?.tariff_name) || '—'}</div>
                      <div className="text-slate-500">Betrag</div>
                      <div className="font-medium">{course?.tariff_amount != null ? `${formatDeNumber(course.tariff_amount)} €` : '—'}</div>
                      <div className="text-slate-500">GEMA</div>
                      <div>{course?.tariff_gema_applicable ? '+1,20 €/Mo' : '—'}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : activeTab === 'overview' ? (
              /* Übersicht Tab — iMikel: Toolbar + Suche + Tabelle aller Belegungen */
              <div className="bg-white rounded border border-slate-200">
                <SubTabs
                  value={ubsSub}
                  onChange={setUbsSub}
                  items={[
                    { value: 'belegungen', label: 'Belegungen' },
                    { value: 'adressen', label: 'Adressen', disabled: true },
                    { value: 'warteliste', label: 'Warteliste' },
                    { value: 'eigene', label: 'Eigene Ansichten', disabled: true },
                  ]}
                />
                {ubsSub === 'warteliste' ? (
                  <WaitlistPanel />
                ) : (
                <>
                {/* iMikel-style toolbar */}
                <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50/50 overflow-x-auto">
                  <button
                    type="button"
                    title="Schüler ohne Belegung einem Kurs zuordnen"
                    onClick={() => setShowBulkEnrollDialog(true)}
                    data-testid="uv-toolbar-bulk-enroll"
                    className="flex flex-col items-center justify-center px-2 py-1 rounded hover:bg-brand-red/10 text-slate-600 hover:text-brand-red min-w-[64px]"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="text-[9px] mt-0.5">Zuordnen</span>
                  </button>
                  {[
                    { icon: Printer, label: 'Drucken', key: 'drucken', title: 'Listen und Ausdrucke' },
                    { icon: FileText, label: 'Brief', key: 'brief', title: 'Brief aus Vorlage erstellen' },
                    { icon: Mail, label: 'E-Mail', key: 'e-mail', title: 'E-Mail an ausgewählten Datensatz' },
                    { icon: MessageSquare, label: 'SMS', key: 'sms', title: 'SMS versenden' },
                    { icon: Bell, label: 'App', key: 'app', title: 'App-Nachricht an ausgewählte Personen' },
                    { icon: Euro, label: 'Rechnung', key: 'rechnung', title: 'Monatsrechnung für Zahler erstellen' },
                    { icon: Calendar, label: 'Kalender', key: 'kalender', title: 'Kalender öffnen' },
                    { icon: exportBusy ? Loader2 : ExternalLink, label: 'Export', key: 'export', title: 'Belegungen als CSV exportieren' },
                  ].map(({ icon: Ico, label, key, title }) => (
                    <button
                      key={key}
                      type="button"
                      title={title || label}
                      onClick={() => runToolbarAction(key)}
                      disabled={key === 'export' && exportBusy}
                      data-testid={`uv-toolbar-${key}`}
                      className="flex flex-col items-center justify-center px-2 py-1 rounded hover:bg-brand-red/10 text-slate-600 hover:text-brand-red min-w-[52px] disabled:opacity-50"
                    >
                      <Ico className={`w-4 h-4 ${key === 'export' && exportBusy ? 'animate-spin' : ''}`} />
                      <span className="text-[9px] mt-0.5">{label}</span>
                    </button>
                  ))}
                </div>
                {/* Listenmodus + Pager oben (nicht unten) */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs" data-testid="uv-list-pager">
                  <div className="text-slate-600">
                    Anzahl: <span className="font-bold text-slate-800">{enrollments.length}</span>
                    {totalCount > 0 && totalCount !== enrollments.length && (
                      <span className="ml-2 text-slate-400">von {totalCount}</span>
                    )}
                    {listMode === 'all' && totalCount > 0 && enrollments.length === totalCount && (
                      <span className="ml-2 text-emerald-700">(alle)</span>
                    )}
                    {listMode === 'all' && totalCount > enrollments.length && (
                      <span className="ml-2 text-amber-700">— unvollständig, bitte neu laden</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded border border-slate-200 overflow-hidden">
                      <button
                        type="button"
                        data-testid="uv-list-mode-all"
                        className={`px-2 py-1 ${listMode === 'all' ? 'bg-brand-red text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        onClick={() => {
                          setListPage(1);
                          setListMode('all');
                        }}
                        title="Alle Treffer laden und durchscrollen"
                      >
                        Alle
                      </button>
                      <button
                        type="button"
                        data-testid="uv-list-mode-paged"
                        className={`px-2 py-1 border-l border-slate-200 ${listMode === 'paged' ? 'bg-brand-red text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        onClick={() => {
                          setListPage(1);
                          setListMode('paged');
                        }}
                        title="Seitenweise mit fortlaufender Sortierung (A→Z über alle Seiten)"
                      >
                        Seitenweise
                      </button>
                    </div>
                    {listMode === 'paged' && (
                      <>
                        <label className="flex items-center gap-1 text-slate-500">
                          max.
                          <select
                            data-testid="uv-page-size"
                            className="h-7 rounded border border-slate-300 bg-white px-1 text-xs"
                            value={pageSize}
                            onChange={(e) => {
                              setListPage(1);
                              setPageSize(Number(e.target.value));
                            }}
                          >
                            {UV_PAGE_SIZE_OPTIONS.map((n) => (
                              <option key={n} value={n}>{n}</option>
                            ))}
                          </select>
                        </label>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            data-testid="uv-page-prev"
                            disabled={listPage <= 1}
                            className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-200 bg-white disabled:opacity-40"
                            onClick={() => setListPage((p) => Math.max(1, p - 1))}
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>
                          <span className="tabular-nums text-slate-600 min-w-[4.5rem] text-center">
                            {listPage} / {Math.max(1, Math.ceil((totalCount || 0) / pageSize))}
                          </span>
                          <button
                            type="button"
                            data-testid="uv-page-next"
                            disabled={listPage >= Math.max(1, Math.ceil((totalCount || 0) / pageSize))}
                            className="h-7 w-7 inline-flex items-center justify-center rounded border border-slate-200 bg-white disabled:opacity-40"
                            onClick={() => setListPage((p) => p + 1)}
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                    <span className="text-slate-500">Stichtag: <span className="font-medium">{formatDate(new Date())}</span></span>
                    <ColumnPicker
                      columns={overviewColumns}
                      onColumnsChange={setOverviewColumns}
                      label="Spalten"
                      testId="uv-column-picker"
                    />
                  </div>
                </div>
                {/* iMikel-style enrollment table */}
                <div className="overflow-x-auto" data-testid="uv-uebersicht-table">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-100 text-slate-600 uppercase text-[10px]">
                      <tr>
                        {showBulkActions && <th className="w-8" />}
                        {overviewVisibleCols.map(({ key, label }) => {
                          const active = overviewSort.key === key;
                          const SortIcon = active && overviewSort.dir === 'desc' ? ArrowDown : ArrowUp;
                          return (
                            <th key={key} className="text-left py-1.5 px-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setListPage(1);
                                  setOverviewSort((prev) => (
                                    prev.key === key
                                      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                                      : { key, dir: 'asc' }
                                  ));
                                }}
                                className={`inline-flex items-center gap-0.5 uppercase tracking-wide select-none hover:text-brand-red ${
                                  active ? 'text-brand-red font-semibold' : ''
                                }`}
                                title={`${label} sortieren (über alle Seiten)`}
                                data-testid={`uv-sort-${key}`}
                              >
                                {label}
                                <SortIcon className={`w-2.5 h-2.5 ${active ? 'opacity-100' : 'opacity-30'}`} />
                              </button>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {searchMode && (
                        <tr className="bg-slate-50 border-b border-slate-200" data-testid="uv-uebersicht-search-row">
                          {showBulkActions && <td />}
                          {overviewColVisible('name') && <td className="py-1 px-1.5">{si('q_student_name', 'Name…')}</td>}
                          {overviewColVisible('dob') && <td className="py-1 px-1.5">{si('q_student_age', '>18')}</td>}
                          {overviewColVisible('course') && <td className="py-1 px-1.5">{si('q_fach', 'Fach…')}</td>}
                          {overviewColVisible('dauer') && <td className="py-1 px-1.5">{si('q_dauer', 'Min.')}</td>}
                          {overviewColVisible('tag') && <td className="py-1 px-1.5">{si('q_termin', 'Mo…')}</td>}
                          {overviewColVisible('zeit') && <td className="py-1 px-1.5">{si('q_zeit', '16:30')}</td>}
                          {overviewColVisible('raum') && <td className="py-1 px-1.5">{si('q_raum', 'Raum…')}</td>}
                          {overviewColVisible('areal') && <td className="py-1 px-1.5" />}
                          {overviewColVisible('eintritt') && <td className="py-1 px-1.5">{si('q_eintritt', 'TT.MM.JJJJ')}</td>}
                          {overviewColVisible('austritt') && <td className="py-1 px-1.5">{si('q_austritt', 'TT.MM.JJJJ')}</td>}
                          {overviewColVisible('status') && <td className="py-1 px-1.5" />}
                        </tr>
                      )}
                      {/* Server liefert bereits sortiert; kein slice — sonst nur 50 Zeilen sichtbar */}
                      {enrollments.map((e, idx) => {
                        const { student, course, ortRaum, areal } = overviewEnrollmentFields(e);
                        const dob = student?.date_of_birth || e.student_dob;
                        const age = dob ? new Date().getFullYear() - new Date(dob).getFullYear() : null;
                        const sName = student
                          ? `${student.last_name || ''}, ${student.first_name || ''}`.replace(/^,\s*/, '')
                          : '—';
                        const cName = courseDisplayName(course?.name, course?.subject_name) || '—';
                        const dayLabel = course?.day_of_week != null
                          ? ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][course.day_of_week]
                          : null;
                        return (
                          <tr
                            key={e.id}
                            onClick={() => {
                              const origIdx = enrollments.findIndex((x) => x.id === e.id);
                              selectEnrollment(e, origIdx >= 0 ? origIdx : idx);
                              setActiveTab('enrollments');
                            }}
                            className={`border-b border-slate-100 cursor-pointer hover:bg-brand-red/5 ${selectedEnrollment?.id === e.id ? 'bg-brand-red/10' : ''}`}
                            data-testid={`uv-row-${e.id}`}
                          >
                            {showBulkActions && (
                              <td className="py-1 px-1" onClick={(ev) => ev.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => toggleSelection(e.id)}
                                  className="p-0.5 rounded hover:bg-slate-100"
                                  data-testid={`uv-bulk-row-${e.id}`}
                                  aria-label={selectedIds.has(e.id) ? 'Abwählen' : 'Auswählen'}
                                >
                                  {selectedIds.has(e.id) ? (
                                    <CheckSquare className="w-3.5 h-3.5 text-slate-700" />
                                  ) : (
                                    <Square className="w-3.5 h-3.5 text-slate-400" />
                                  )}
                                </button>
                              </td>
                            )}
                            {overviewColVisible('name') && <td className="py-1 px-2 font-medium">{sName}</td>}
                            {overviewColVisible('dob') && (
                              <td className="py-1 px-2 font-medium text-slate-500">
                                {dob ? formatDate(dob) : '—'} {age != null && <span className="text-slate-400">({age})</span>}
                              </td>
                            )}
                            {overviewColVisible('course') && <td className="py-1 px-2">{cName}</td>}
                            {overviewColVisible('dauer') && (
                              <td className="py-1 px-2 text-slate-500">{course?.duration_minutes ? `${course.duration_minutes} Min.` : '—'}</td>
                            )}
                            {overviewColVisible('tag') && (
                              <td className="py-1 px-2 font-medium text-[10px]">
                                {dayLabel || '—'}
                              </td>
                            )}
                            {overviewColVisible('zeit') && (
                              <td className="py-1 px-2 font-medium text-[10px]">
                                {course?.start_time ? `${formatTime(course.start_time)} Uhr` : '—'}
                              </td>
                            )}
                            {overviewColVisible('raum') && (
                              <td className={`py-1 px-2 ${ortRaum ? 'text-slate-600' : 'text-slate-300'}`}>{ortRaum || '—'}</td>
                            )}
                            {overviewColVisible('areal') && (
                              <td className={`py-1 px-2 ${areal ? 'text-slate-600' : 'text-slate-300'}`}>{getArealLabel(resolveAreal(areal, activeTenant)) || '—'}</td>
                            )}
                            {overviewColVisible('eintritt') && (
                              <td className="py-1 px-2 font-medium text-[10px]">{e.start_date ? formatDate(e.start_date) : '—'}</td>
                            )}
                            {overviewColVisible('austritt') && (
                              <td className="py-1 px-2 font-medium text-[10px]">{e.end_date ? formatDate(e.end_date) : '—'}</td>
                            )}
                            {overviewColVisible('status') && (
                              <td className="py-1 px-2">
                                <Badge variant="outline" className={`text-[9px] py-0 px-1 ${
                                  e.status === 'active' ? 'border-emerald-200 text-emerald-700 bg-emerald-50' :
                                  e.status === 'cancelled' || e.status === 'ended' ? 'border-rose-200 text-rose-700 bg-rose-50' :
                                  'border-slate-200 text-slate-600 bg-slate-50'
                                }`}>
                                  {statusLabel(e.status)}
                                </Badge>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {enrollments.length === 0 && (
                    <p className="py-8 text-center text-slate-400 text-xs italic">Keine Belegungen geladen</p>
                  )}
                </div>
                </>
                )}
              </div>
            ) : (
              /* Belegungen Tab (default) - Detailansicht */
              <div className="grid grid-cols-2 gap-2 h-full min-h-0 overflow-hidden">
                {/* Left Column - Participant & Payer */}
                <div className="flex flex-col gap-2 min-h-0 h-full overflow-y-auto pb-6" data-testid="uv-left-column">
                  {/* Participant Section */}
                  <div className="bg-white rounded-lg border border-slate-200 p-2 shrink-0">
                    <SectionHeader 
                      title="Teilnehmer:in" 
                      color="amber"
                      onTitleClick={student ? () => navigate(`/students/${student.id}`) : undefined}
                      onEdit={student && !searchMode ? () => {
                        setEditedStudent({...student});
                        setEditingStudent(true);
                      } : undefined}
                      editing={editingStudent}
                      onSave={handleSaveStudent}
                      onCancel={() => {
                        setEditingStudent(false);
                        setEditedStudent(student);
                      }}
                    />
                    {!student && !searchMode ? (
                      <div className="text-center py-3 text-slate-400 text-xs space-y-2" data-testid="empty-participant">
                        <p>Kein:e Teilnehmer:in ausgewählt</p>
                        <Button
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => openNewEnrollment()}
                          data-testid="create-participant-btn"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Teilnehmer:in anlegen
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs"
                          onClick={() => setShowBulkEnrollDialog(true)}
                          data-testid="bulk-enroll-from-empty"
                        >
                          <UserPlus className="w-3.5 h-3.5 mr-1" /> Ohne Belegung zuordnen
                        </Button>
                      </div>
                    ) : (
                    <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                      {searchMode ? (
                        <>
                          <FormField label="Anrede" className="col-span-1" {...sf('q_anrede')} />
                          <FormField label="Titel" className="col-span-1" {...sf('q_titel')} />
                          <FormField label="PK" className="col-span-2" {...sf('q_student_pk')} />
                        </>
                      ) : (
                        <>
                          <FormField label="Anrede" value="An" className="col-span-1" />
                          <FormField label="Titel" value="" className="col-span-1" />
                          <FormField label="PK" value={student ? formatPersonPk(activeTenant, student?.gkz, student.id) : '-'} className="col-span-2" />
                        </>
                      )}
                      
                      {editingStudent && !searchMode ? (
                        <>
                          <FormField 
                            label="Nachname" 
                            value={editedStudent?.last_name} 
                            className="col-span-2"
                            editable
                            onChange={(v) => setEditedStudent({...editedStudent, last_name: v})}
                          />
                          <FormField 
                            label="Vorname" 
                            value={editedStudent?.first_name} 
                            className="col-span-2"
                            editable
                            onChange={(v) => setEditedStudent({...editedStudent, first_name: v})}
                          />
                        </>
                      ) : (
                        <FormField 
                          label="Name/Firma" 
                          value={student ? `${student.last_name}, ${student.first_name}` : '-'} 
                          className="col-span-4" 
                          {...sf('q_student_name', 'Name…')}
                        />
                      )}
                      
                      <FormField 
                        label="Adresse" 
                        value={editingStudent && !searchMode ? editedStudent?.address : student?.address} 
                        className="col-span-3"
                        editable={editingStudent && !searchMode}
                        onChange={(v) => setEditedStudent({...editedStudent, address: v})}
                        {...sf('q_student_address', 'Straße…')}
                      />
                      {searchMode ? (
                        <FormField label="Gkz" className="col-span-1" {...sf('q_gkz', 'Gkz…')} />
                      ) : (
                        <FormField
                          label="Gkz"
                          value={guardian?.gkz || student?.gkz || ''}
                          className="col-span-1"
                          data-testid="participant-gkz"
                        />
                      )}
                      
                      {editingStudent && !searchMode ? (
                        <>
                          <FormField 
                            label="PLZ" 
                            value={editedStudent?.postal_code} 
                            className="col-span-1"
                            editable
                            onChange={(v) => setEditedStudent({...editedStudent, postal_code: v})}
                          />
                          <FormField 
                            label="Ort" 
                            value={editedStudent?.city} 
                            className="col-span-2"
                            editable
                            onChange={(v) => setEditedStudent({...editedStudent, city: v})}
                          />
                          <div className="col-span-4">
                            <PlzOrtBridge
                              postalCode={editedStudent?.postal_code}
                              city={editedStudent?.city}
                              onCity={(value) => setEditedStudent((prev) => ({ ...prev, city: value }))}
                              testId="uv-student-plz"
                            />
                          </div>
                        </>
                      ) : (
                        <FormField 
                          label="PLZ/Ort" 
                          value={student ? `${student.postal_code || ''} ${student.city || ''}`.trim() : '-'} 
                          className="col-span-3" 
                          {...sf('q_student_plz_ort', 'PLZ/Ort…')}
                        />
                      )}
                      {searchMode ? (
                        <FormField label="Areal" className="col-span-1" {...sf('q_areal', 'NRW…')} />
                      ) : (
                      <SelectField 
                        label="Areal" 
                        value={editingStudent ? editedStudent?.areal : resolveAreal(student?.areal, activeTenant)}
                        options={AREAL_OPTIONS}
                        className="col-span-1" 
                        editable={editingStudent}
                        onChange={(v) => setEditedStudent({...editedStudent, areal: v})}
                      />
                      )}
                      
                      <FormField 
                        label="Geb." 
                        value={editingStudent && !searchMode ? editedStudent?.date_of_birth : (student?.date_of_birth ? formatDate(student.date_of_birth) : '-')}
                        className="col-span-2" 
                        editable={editingStudent && !searchMode}
                        type={editingStudent && !searchMode ? 'date' : 'text'}
                        onChange={(v) => setEditedStudent({...editedStudent, date_of_birth: v})}
                        {...sf('q_student_dob', 'TT.MM.JJJJ')}
                      />
                      <FormField 
                        label="Alter" 
                        value={calculateAge(
                          editingStudent && !searchMode
                            ? editedStudent?.date_of_birth
                            : student?.date_of_birth
                        )} 
                        className="col-span-1"
                        data-testid="participant-age"
                        {...sf('q_student_age', '>18')}
                      />
                      <FormField 
                        label="1.Kont." 
                        value={formatDate(selectedEnrollment?.start_date)} 
                        className="col-span-1" 
                        {...sf('q_eintritt', 'TT.MM.JJJJ')}
                      />
                    </div>
                    )}
                  </div>

                  {/* Payer Section */}
                  <div className="bg-white rounded-lg border border-slate-200 p-2 shrink-0">
                    {searchMode ? (
                      <SectionHeader title="Zahlungspflichtige:r" color="amber" />
                    ) : (
                      <div className="grid grid-cols-2 gap-1 mb-1" data-testid="uv-person-tabs">
                        <div className={`flex items-center min-w-0 ${personTab === 'payer' ? 'border-b-2 border-slate-900' : 'border-b border-slate-200'}`}>
                          <button
                            type="button"
                            className="flex-1 min-w-0 truncate px-1 py-1 text-left text-xs font-bold uppercase tracking-wider text-slate-900"
                            onClick={() => { setPersonTab('payer'); setEditingCustodian(false); }}
                            data-testid="uv-tab-zahler"
                          >
                            Zahlungspflichtiger
                          </button>
                          {editingPayer && personTab === 'payer' ? (
                            <span className="flex shrink-0">
                              <button type="button" onClick={handleSavePayer} className="p-1 text-emerald-600" data-testid="section-save-zahlungspflichtige:r" aria-label="Zahlungspflichtigen speichern"><Save className="w-3.5 h-3.5" /></button>
                              <button type="button" onClick={cancelEditGuardian} className="p-1 text-slate-400" data-testid="section-cancel-zahlungspflichtige:r" aria-label="Abbrechen"><X className="w-3.5 h-3.5" /></button>
                            </span>
                          ) : guardian && (
                            <button type="button" onClick={stiftZahler} className="p-1 text-slate-400 hover:text-blue-600 shrink-0" data-testid="section-edit-zahlungspflichtige:r" aria-label="Zahlungspflichtigen bearbeiten"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                        <div className={`flex items-center min-w-0 ${personTab === 'custodian' ? 'border-b-2 border-slate-900' : 'border-b border-slate-200'}`}>
                          <button
                            type="button"
                            className={`flex-1 min-w-0 truncate px-1 py-1 text-left text-xs font-bold uppercase tracking-wider ${custodian ? 'text-slate-900' : 'text-slate-300'}`}
                            onClick={() => {
                              setEditingPayer(false);
                              setPersonTab('custodian');
                              if (custodian && personTab === 'custodian') {
                                navigate(`/guardians?guardian=${custodian.id}`);
                              }
                            }}
                            data-testid="uv-tab-erziehungsberechtigter"
                          >
                            Erziehungsberechtigter
                          </button>
                          {editingCustodian && personTab === 'custodian' ? (
                            <span className="flex shrink-0">
                              <button type="button" onClick={handleSaveCustodian} className="p-1 text-emerald-600" data-testid="section-save-erziehungsberechtigter" aria-label="Erziehungsberechtigten speichern"><Save className="w-3.5 h-3.5" /></button>
                              <button type="button" onClick={() => setEditingCustodian(false)} className="p-1 text-slate-400" aria-label="Abbrechen"><X className="w-3.5 h-3.5" /></button>
                            </span>
                          ) : student && (
                            <button type="button" onClick={stiftErzieher} className="p-1 text-slate-400 hover:text-blue-600 shrink-0" data-testid="section-edit-erziehungsberechtigter" aria-label="Erziehungsberechtigten bearbeiten"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      </div>
                    )}
                    {zeigtErzieher && !custodian ? (
                      <div className="text-center py-3 text-slate-400 text-xs" data-testid="empty-erziehungsberechtigter">
                        <p>Kein Erziehungsberechtigter hinterlegt</p>
                      </div>
                    ) : !kartenPerson && !searchMode ? (
                      <div className="text-center py-3 text-slate-400 text-xs space-y-2" data-testid="empty-payer">
                        <p>Kein:e Zahlungspflichtige:r hinterlegt</p>
                        <Button
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => openNewEnrollment({ withStudent: !!student })}
                          data-testid="create-payer-btn"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Zahlungspflichtige:r anlegen
                        </Button>
                      </div>
                    ) : (
                    <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                      <FormField label="Kassenz." value={student?.imikel_kassenzeichen || kartenPerson?.id?.slice(0, 8) || '-'} className="col-span-2" {...sf('q_kassenzeichen', 'Kassenz.…')} />
                      <FormField label="Mand." value={searchMode ? '' : getMandantKuerzel(activeTenant, kartenPerson?.gkz || student?.gkz)} className="col-span-1" {...sf('q_mandant')} />
                      <FormField label="PK" value={searchMode ? '' : (kartenPerson ? formatPersonPk(activeTenant, kartenPerson?.gkz || student?.gkz, kartenPerson.id) : '-')} className="col-span-1" {...sf('q_payer_pk')} />
                      
                      {searchMode ? (
                        <>
                          <FormField label="Anrede" className="col-span-2" {...sf('q_payer_anrede')} />
                          <FormField label="Titel" className="col-span-2" {...sf('q_payer_titel')} />
                        </>
                      ) : (
                        <>
                          <FormField label="Anrede" value="Familie" className="col-span-2" />
                          <FormField label="Titel" value="" className="col-span-2" />
                        </>
                      )}
                      
                      {kartenBearbeiten ? (
                        <>
                          <FormField
                            label="Nachname"
                            value={kartenEntwurf?.last_name}
                            className="col-span-2"
                            editable
                            onChange={(v) => setKartenEntwurf({ ...kartenEntwurf, last_name: v })}
                          />
                          <FormField
                            label="Vorname"
                            value={kartenEntwurf?.first_name}
                            className="col-span-2"
                            editable
                            onChange={(v) => setKartenEntwurf({ ...kartenEntwurf, first_name: v })}
                          />
                        </>
                      ) : (
                        <FormField
                          label="Name"
                          value={kartenPerson ? payerNameLabel(kartenPerson.last_name, kartenPerson.first_name) : '-'}
                          className="col-span-4"
                          {...sf('q_payer_name', 'Name…')}
                        />
                      )}
                      
                      <FormField
                        label="Adresse"
                        value={kartenBearbeiten ? kartenEntwurf?.address : (kartenPerson?.address || (!zeigtErzieher ? student?.address : '') || '-')}
                        className="col-span-3"
                        editable={kartenBearbeiten}
                        onChange={(v) => setKartenEntwurf({ ...kartenEntwurf, address: v })}
                        {...sf('q_payer_address', 'Straße…')}
                      />
                      {searchMode ? (
                        <FormField label="Gkz" className="col-span-1" {...sf('q_gkz', 'Gkz…')} />
                      ) : (
                        <FormField
                          label="Gkz"
                          value={kartenBearbeiten ? (kartenEntwurf?.gkz ?? '') : (kartenPerson?.gkz || (!zeigtErzieher ? student?.gkz : '') || '')}
                          className="col-span-1"
                          editable={kartenBearbeiten}
                          onChange={(v) => setKartenEntwurf({ ...kartenEntwurf, gkz: v })}
                          data-testid="payer-gkz"
                        />
                      )}
                      
                      {kartenBearbeiten ? (
                        <>
                          <FormField
                            label="PLZ"
                            value={kartenEntwurf?.postal_code}
                            className="col-span-1"
                            editable
                            onChange={(v) => setKartenEntwurf({ ...kartenEntwurf, postal_code: v })}
                          />
                          <FormField
                            label="Ort"
                            value={kartenEntwurf?.city}
                            className="col-span-2"
                            editable
                            onChange={(v) => setKartenEntwurf({ ...kartenEntwurf, city: v })}
                          />
                          <div className="col-span-4">
                            <PlzOrtBridge
                              postalCode={kartenEntwurf?.postal_code}
                              city={kartenEntwurf?.city}
                              onCity={(value) => setKartenEntwurf((prev) => ({ ...prev, city: value }))}
                              testId="uv-payer-plz"
                            />
                          </div>
                        </>
                      ) : (
                        <FormField
                          label="PLZ/Ort"
                          value={kartenPerson ? `${kartenPerson.postal_code || ''} ${kartenPerson.city || ''}`.trim() : (student && !zeigtErzieher ? `${student.postal_code || ''} ${student.city || ''}`.trim() : '-')}
                          className="col-span-3"
                          {...sf('q_payer_plz_ort', 'PLZ/Ort…')}
                        />
                      )}
                      {searchMode ? (
                        <FormField label="Areal" className="col-span-1" {...sf('q_payer_areal')} />
                      ) : (
                      <SelectField 
                        label="Areal" 
                        value={resolveAreal(kartenPerson?.areal || (!zeigtErzieher ? student?.areal : ''), activeTenant)}
                        readOnlyLabel={getArealLabel(kartenPerson?.areal || (!zeigtErzieher ? student?.areal : ''))}
                        options={AREAL_OPTIONS}
                        className="col-span-1" 
                      />
                      )}
                      <FormField
                        label="Geb."
                        value={kartenBearbeiten ? kartenEntwurf?.date_of_birth : (kartenPerson?.date_of_birth ? formatDate(kartenPerson.date_of_birth) : '-')}
                        className="col-span-2"
                        editable={kartenBearbeiten}
                        type={kartenBearbeiten ? 'date' : 'text'}
                        onChange={(v) => setKartenEntwurf({ ...kartenEntwurf, date_of_birth: v })}
                        data-testid="payer-dob"
                      />
                      <FormField
                        label="Alter"
                        value={calculateAge(kartenBearbeiten ? kartenEntwurf?.date_of_birth : kartenPerson?.date_of_birth)}
                        className="col-span-1"
                        data-testid="payer-age"
                      />
                    </div>
                    )}
                  </div>

                  {/* Contacts Section - Under Payer */}
                  <div className="bg-white rounded-lg border border-slate-200 p-2 shrink-0" data-testid="uv-kontakte">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Kontakte</div>
                      {guardian && custodian && !searchMode && (
                        <button
                          type="button"
                          onClick={sendBeideZugaenge}
                          className="text-[10px] font-medium text-slate-600 hover:text-slate-900"
                          data-testid="uv-app-zugang-beide"
                        >
                          App-Zugang an beide
                        </button>
                      )}
                      {searchMode && (
                        <FormField label="Telefon / E-Mail" className="col-span-2" {...sf('q_kontakt', 'Nummer oder E-Mail')} />
                      )}
                      {student && !searchMode && (
                        <button
                          type="button"
                          onClick={() => setContactPersonsOpen(true)}
                          className="p-1 rounded hover:bg-slate-100 text-slate-500"
                          title="Kontaktpersonen verwalten"
                          aria-label="Kontaktpersonen verwalten"
                          data-testid="open-contact-persons"
                        >
                          <Users className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-1 text-xs">
                      {/* Der Stift beim Teilnehmer schaltet dessen Telefon und
                          E-Mail auf Eingabe um; gespeichert wird mit dem Haken
                          oben in der Teilnehmer-Karte. */}
                      {studentEditing ? (
                        <div className="grid grid-cols-2 gap-2">
                          <FormField
                            label="Telefon (TN)"
                            value={editedStudent?.phone}
                            editable
                            placeholder="mehrere mit Komma"
                            onChange={(v) => setEditedStudent({ ...editedStudent, phone: v })}
                            data-testid="student-phone"
                          />
                          <FormField
                            label="E-Mail (TN)"
                            value={editedStudent?.email}
                            editable
                            type="email"
                            onChange={(v) => setEditedStudent({ ...editedStudent, email: v })}
                            data-testid="student-email"
                          />
                        </div>
                      ) : (
                        <>
                          {student?.phone && (
                            <div className="flex items-start gap-2">
                              <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                              <div className="min-w-0 flex-1">
                                <PhoneLink phone={student.phone} className="font-medium" data-testid="uv-call-student" />
                              </div>
                              <span className="text-slate-400 shrink-0">(TN)</span>
                            </div>
                          )}
                          {student?.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              <EmailLink email={student.email} className="font-medium break-all min-w-0 flex-1" data-testid="uv-email-student" />
                              <span className="text-slate-400 shrink-0">(TN)</span>
                            </div>
                          )}
                        </>
                      )}
                      {/* Im Bearbeiten-Modus des Zahlers werden dessen Kontaktangaben
                          zu Eingaben. */}
                      {payerEditing ? (
                        <div className="grid grid-cols-2 gap-2">
                          <FormField
                            label="Telefon (ZP)"
                            value={editedGuardian?.phone}
                            editable
                            placeholder="mehrere mit Komma"
                            onChange={(v) => setEditedGuardian({ ...editedGuardian, phone: v })}
                            data-testid="payer-phone"
                          />
                          <FormField
                            label="E-Mail (ZP)"
                            value={editedGuardian?.email}
                            editable
                            type="email"
                            onChange={(v) => setEditedGuardian({ ...editedGuardian, email: v })}
                            data-testid="payer-email"
                          />
                        </div>
                      ) : (
                        <>
                          {guardian?.phone && (
                            <div className="flex items-start gap-2">
                              <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                              <div className="min-w-0 flex-1">
                                <PhoneLink phone={guardian.phone} className="font-medium" data-testid="uv-call-guardian" />
                              </div>
                              <span className="text-slate-400 shrink-0">(ZP)</span>
                            </div>
                          )}
                          {guardian?.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              <EmailLink email={guardian.email} className="font-medium break-all min-w-0 flex-1" data-testid="uv-email-guardian" />
                              <span className="text-slate-400 shrink-0">(ZP)</span>
                            </div>
                          )}
                        </>
                      )}
                      {editingCustodian && !searchMode && (
                        <div className="grid grid-cols-2 gap-2">
                          <FormField
                            label="Telefon (EB)"
                            value={editedCustodian?.phone}
                            editable
                            placeholder="mehrere mit Komma"
                            onChange={(v) => setEditedCustodian({ ...editedCustodian, phone: v })}
                            data-testid="custodian-phone-edit"
                          />
                          <FormField
                            label="E-Mail (EB)"
                            value={editedCustodian?.email}
                            editable
                            type="email"
                            onChange={(v) => setEditedCustodian({ ...editedCustodian, email: v })}
                            data-testid="custodian-email-edit"
                          />
                        </div>
                      )}
                      {/* Weitere Angehörige: Mutter neben Vater, oder die
                          abweichende Person aus der Online-Anmeldung. Hier nur
                          zum Lesen — geändert wird im Kontaktpersonen-Dialog. */}
                      {!searchMode && weitereKontakte.map((rel) => (
                        <div
                          key={rel.id}
                          className="pt-1 border-t border-slate-100"
                          data-testid="weiterer-kontakt"
                        >
                          <div className="flex items-center gap-1 text-slate-400">
                            <User className="w-3.5 h-3.5 shrink-0" />
                            <span className="font-medium text-slate-700 truncate">
                              {`${rel.guardian.first_name} ${rel.guardian.last_name}`.trim()}
                            </span>
                            <span className="shrink-0">{`(${contactRoleLabel(rel.relationship_type)})`}</span>
                          </div>
                          {rel.guardian.phone && (
                            <div
                              className="flex items-start gap-2 pl-4"
                              data-testid={rel.guardian.id === custodian?.id ? 'uv-kontakt-erziehungsberechtigter' : undefined}
                            >
                              <div className="min-w-0 flex-1">
                                <PhoneLink phone={rel.guardian.phone} className="font-medium" />
                              </div>
                            </div>
                          )}
                          {rel.guardian.email && (
                            <div className="flex items-center gap-2 pl-4">
                              <EmailLink email={rel.guardian.email} className="break-all min-w-0 flex-1" />
                            </div>
                          )}
                        </div>
                      ))}
                      {!payerEditing && !studentEditing && !student?.phone && !student?.email && !guardian?.phone && !guardian?.email && weitereKontakte.length === 0 && (
                        <span className="text-slate-400">Keine Kontakte</span>
                      )}
                    </div>
                  </div>

                  {selectedEnrollment && !searchMode && (
                    <EnrollmentCorrespondence enrollmentId={selectedEnrollment.id} />
                  )}
                </div>

                {/* Right Column - Course Details */}
                <div className="flex flex-col gap-2 min-h-0 h-full overflow-y-auto pb-6">
                  <div className="bg-white rounded-lg border border-slate-200 p-2 shrink-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        {/* Checkbox for bulk selection */}
                        {showBulkActions && selectedEnrollment && (
                          <button
                            onClick={() => toggleSelection(selectedEnrollment.id)}
                            className="p-0.5 rounded hover:bg-slate-100"
                          >
                            {selectedIds.has(selectedEnrollment.id) ? (
                              <CheckSquare className="w-4 h-4 text-brand-red" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        )}
                        
                        {/* Section Title */}
                        {selectedEnrollment ? (
                          <button
                            onClick={() => navigate(`/enrollments/${selectedEnrollment.id}`)}
                            className="text-xs font-bold uppercase tracking-wider text-brand-red hover:underline cursor-pointer"
                            title="Zur Belegungs-Detailseite"
                            data-testid="section-link-belegungsdetails"
                          >
                            Belegungsdetails
                          </button>
                        ) : (
                          <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Belegungsdetails</span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {selectedEnrollment && (
                          <HistoryEyeButton
                            onClick={() => setHistoryOpen(true)}
                            title="Änderungsprotokoll dieser Belegung"
                          />
                        )}
                        {selectedEnrollment && course && !searchMode && (
                          <button
                            type="button"
                            onClick={() => setActiveTab('events')}
                            className="text-[10px] font-medium text-slate-600 hover:text-slate-900 hover:underline px-1"
                            data-testid="enrollment-open-event"
                          >
                            Zur Veranstaltung
                          </button>
                        )}
                        {selectedEnrollment && (
                          <>
                            {editingEnrollment ? (
                              <>
                                <button 
                                  onClick={() => handleSaveEnrollment()}
                                  className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                                  title="Speichern"
                                >
                                  <Save className="w-3.5 h-3.5" />
                                </button>
                                <button 
                                  onClick={() => {
                                    setEditingEnrollment(false);
                                    setEditedEnrollment({
                                      status: selectedEnrollment.status,
                                      start_date: selectedEnrollment.start_date,
                                      end_date: selectedEnrollment.end_date
                                    });
                                  }}
                                  className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                                  title="Abbrechen"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button 
                                  onClick={() => {
                                    setEditingEnrollment(true);
                                    // Always load filtered teachers when entering edit mode
                                    loadFilteredTeachers(editedEnrollment?.subject_id, editedEnrollment?.tariff_id, editedEnrollment?.teacher_id);
                                  }}
                                  className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                  title="Bearbeiten"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button 
                                  onClick={() => setShowDeleteDialog(true)}
                                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                  title="Löschen"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </>
                        )}
                        
                        {selectedEnrollment && (
                          editingEnrollment ? (
                            <Select 
                              value={editedEnrollment?.status || 'active'}
                              onValueChange={(v) => setEditedEnrollment({...editedEnrollment, status: v})}
                            >
                              <SelectTrigger className="w-20 h-6 text-[10px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active" className="text-[11px]">Aktiv</SelectItem>
                                <SelectItem value="ended" className="text-[11px]">Beendet</SelectItem>
                                <SelectItem value="cancelled" className="text-[11px]">Gekündigt</SelectItem>
                                <SelectItem value="paused" className="text-[11px]">Pausiert</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={`text-[10px] border ${
                              selectedEnrollment?.status === 'active'
                                ? CHIP_CLASS.success
                                : selectedEnrollment?.status === 'cancelled' || selectedEnrollment?.status === 'ended'
                                ? CHIP_CLASS.danger
                                : selectedEnrollment?.status === 'paused'
                                ? CHIP_CLASS.warning
                                : CHIP_CLASS.neutral
                            }`} data-testid="enrollment-status-badge">
                              {statusLabel(selectedEnrollment?.status)}
                            </Badge>
                          )
                        )}
                      </div>
                    </div>

                    {(selectedEnrollment || searchMode) ? (
                      <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                        <FormField 
                          label="Eintritt" 
                          value={editingEnrollment ? editedEnrollment?.start_date : formatDate(selectedEnrollment?.start_date)}
                          className="col-span-2"
                          editable={editingEnrollment}
                          type={editingEnrollment ? 'date' : 'text'}
                          onChange={(v) => setEditedEnrollment({...editedEnrollment, start_date: v})}
                          data-testid="enrollment-eintritt"
                          {...sf('q_eintritt')}
                        />
                        <FormField 
                          label="Austritt" 
                          value={editingEnrollment ? (editedEnrollment?.end_date || '') : (formatDate(selectedEnrollment?.end_date) || '-')}
                          className="col-span-2"
                          editable={editingEnrollment}
                          type={editingEnrollment ? 'date' : 'text'}
                          onChange={(v) => setEditedEnrollment({...editedEnrollment, end_date: v || null})}
                          data-testid="enrollment-austritt"
                          {...sf('q_austritt')}
                        />
                        
                        {searchMode ? (
                          <FormField label="Fach" className="col-span-2" {...sf('q_fach')} />
                        ) : (
                        <SelectField 
                          label="Fach" 
                          value={course?.subject_id}
                          options={subjects.map(s => ({ ...s, label: subjectBaseName(s.label) }))}
                          className="col-span-2" 
                          editable={false}
                        />
                        )}
                        
                        {searchMode ? (
                          <FormField label="Tarif" className="col-span-2" {...sf('q_unterrichtsform')} />
                        ) : (
                        <SelectField 
                          label="Tarif" 
                          value={course?.tariff_id || undefined}
                          options={tariffs}
                          className="col-span-2" 
                          editable={false}
                          readOnlyLabel={
                            tariffFormName(course?.tariff_name)
                              || (subjectFormSuffix(course?.subject_name) || '—')
                          }
                        />
                        )}
                        
                        <FormField 
                          label="Bezeichnung" 
                          value={courseDisplayName(
                            course?.name || selectedEnrollment?.course_name,
                            course?.subject_name,
                          ) || '-'}
                          className="col-span-4"
                          editable={false}
                          {...sf('q_bezeichnung')}
                        />
                        
                        <FormField 
                          label="Instrument" 
                          value="-"
                          className="col-span-2"
                          editable={false}
                        />
                        
                        {searchMode ? (
                          <FormField label="Lehrkraft" className="col-span-2" {...sf('q_lehrkraft')} />
                        ) : (
                        <div className="col-span-2 relative">
                          <SelectField
                            label="Lehrkraft"
                            value={course?.teacher_id}
                            options={teachers}
                            readOnlyLabel={course?.teacher_name}
                            editable={false}
                          />
                          {course?.teacher_id && (
                            <button
                              type="button"
                              title="Lehrkraft-Wochenkalender öffnen"
                              aria-label="Kalender der Lehrkraft"
                              className="absolute bottom-1 right-1 rounded bg-white p-1 text-brand-red hover:bg-brand-red/10"
                              onClick={() => navigate(teacherWeekCalendarUrl(course.teacher_id))}
                              data-testid="enrollment-teacher-calendar"
                            >
                              <Calendar className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        )}
                        
                        {searchMode ? (
                          <FormField label="Termin" className="col-span-1" {...sf('q_termin')} />
                        ) : (
                        <SelectField 
                          label="Termin" 
                          value={course?.day_of_week?.toString()}
                          options={WEEKDAY_OPTIONS}
                          className="col-span-1"
                          editable={false}
                        />
                        )}
                        <FormField 
                          label="Zeit" 
                          value={formatTime(course?.start_time) || '-'}
                          className="col-span-1"
                          editable={false}
                          {...sf('q_zeit')}
                        />
                        {searchMode ? (
                          <FormField label="Dauer" className="col-span-1" {...sf('q_dauer')} />
                        ) : (
                        <SelectField 
                          label="Dauer" 
                          value={course?.duration_minutes?.toString()}
                          options={DURATION_OPTIONS}
                          className="col-span-1"
                          editable={false}
                        />
                        )}
                        {searchMode ? (
                          <FormField label="Turnus" className="col-span-1" {...sf('q_turnus')} />
                        ) : (
                        <SelectField
                          label="Turnus"
                          value={parseCourseRhythm(course?.rhythm)}
                          options={TURNUS_OPTIONS}
                          className="col-span-1"
                          editable={false}
                          testId="enrollment-turnus"
                        />
                        )}
                        
                        {searchMode ? (
                          <FormField
                            label="Unterrichtsort"
                            className="col-span-2"
                            {...sf('q_standort', 'Standort / Suchbegriff…')}
                          />
                        ) : (
                        <SelectField 
                          label="Unterrichtsort" 
                          value={course?.location_id}
                          options={locations}
                          className="col-span-2"
                          editable={false}
                        />
                        )}
                        
                        <FormField 
                          label="Raum" 
                          value={course?.room_name || '-'}
                          className="col-span-1"
                          editable={false}
                          {...sf('q_raum')}
                        />
                        <SelectField 
                          label="Areal" 
                          value={defaultArealForTenant(activeTenant)}
                          options={AREAL_OPTIONS}
                          className="col-span-1"
                          editable={false}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-3 text-slate-400 text-xs space-y-2" data-testid="empty-enrollment">
                        <p>Keine Belegung ausgewählt</p>
                        <Button
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => openNewEnrollment({ withStudent: !!student, withGuardian: !!guardian })}
                          data-testid="create-enrollment-btn"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Belegung anlegen
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Bankdaten Section */}
                  <div className="bg-white rounded-lg border border-slate-200 p-2 shrink-0" data-testid="enrollment-bank-section">
                    <SectionHeader
                      title="Bankdaten"
                      color="amber"
                      onTitleClick={guardian ? () => navigate(`/payer-accounts?guardian=${guardian.id}&tab=bank`) : undefined}
                      onEdit={guardian && !searchMode ? () => navigate(`/payer-accounts?guardian=${guardian.id}&tab=bank`) : undefined}
                      editing={false}
                    />
                    {(!guardian || bankIsEmpty) && !editingBank && !searchMode ? (
                      <div className="text-center py-3 text-slate-400 text-xs space-y-2" data-testid="empty-bank">
                        <p>{guardian ? 'Keine Bankdaten hinterlegt' : 'Zuerst Zahlungspflichtige:r anlegen'}</p>
                        {guardian ? (
                          <Button
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => navigate(`/payer-accounts?guardian=${guardian.id}&tab=bank`)}
                            data-testid="create-bank-btn"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Bankdaten anlegen
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => openNewEnrollment({ withStudent: !!student })}
                            data-testid="create-bank-via-payer-btn"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Zahlungspflichtige:r anlegen
                          </Button>
                        )}
                      </div>
                    ) : (
                    <div className="grid grid-cols-4 gap-x-2 gap-y-1">
                      <FormField
                        label="Kontoinhaber"
                        value={editingBank
                          ? editedGuardian?.bank_account_holder
                          : (guardian ? accountHolderLabel(guardian.bank_account_holder, guardian.first_name, guardian.last_name) : '-')}
                        className="col-span-2"
                        editable={editingBank}
                        onChange={(v) => setEditedGuardian({ ...editedGuardian, bank_account_holder: v })}
                        data-testid="enrollment-bank-holder"
                        {...sf('q_kontoinhaber', 'Kontoinhaber…')}
                      />
                      <FormField
                        label="Bank"
                        value={editingBank ? editedGuardian?.bank_name : (guardian?.bank_name || '-')}
                        className="col-span-2"
                        editable={editingBank}
                        onChange={(v) => setEditedGuardian({ ...editedGuardian, bank_name: v })}
                        {...sf('q_bank', 'Bank…')}
                      />
                      {editingBank ? (
                        <FormField
                          label="IBAN"
                          value={editedGuardian?.bank_iban}
                          className="col-span-3"
                          editable
                          onChange={(v) => {
                            setEditedGuardian({ ...editedGuardian, bank_iban: v });
                            const cleaned = String(v || '').replace(/[\s-]/g, '');
                            if (cleaned.length < 15) return;
                            lookupIbanInfo(cleaned).then((info) => {
                              if (!info?.bic) return;
                              setEditedGuardian((prev) => ({
                                ...prev,
                                bank_iban: v,
                                bank_bic: info.bic,
                                bank_name: info.bank_name || prev.bank_name,
                              }));
                            });
                          }}
                          data-testid="enrollment-bank-iban"
                        />
                      ) : searchMode ? (
                        <FormField label="IBAN" className="col-span-3" {...sf('q_iban', 'DE…')} />
                      ) : (
                      <div className="col-span-3">
                        <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">IBAN</label>
                        <div className="text-xs font-medium text-slate-800 bg-white border border-slate-200 rounded px-2 py-1 min-h-[24px] leading-tight flex items-center justify-between gap-2">
                          {guardian?.bank_iban ? (
                            <>
                              <span className="font-medium" data-testid="enrollment-bank-iban">
                                {showBankIban
                                  ? guardian.bank_iban
                                  : `${guardian.bank_iban.slice(0, 4)} •••• •••• ${guardian.bank_iban.slice(-4)}`}
                              </span>
                              <button
                                onClick={() => setShowBankIban((v) => !v)}
                                className="text-slate-400 hover:text-blue-600 shrink-0"
                                title={showBankIban ? 'IBAN verbergen' : 'IBAN anzeigen'}
                                data-testid="enrollment-bank-iban-toggle"
                              >
                                {showBankIban ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                            </>
                          ) : <span data-testid="enrollment-bank-iban">-</span>}
                        </div>
                      </div>
                      )}
                      <FormField
                        label="BIC"
                        value={editingBank ? editedGuardian?.bank_bic : (guardian?.bank_bic || '-')}
                        className="col-span-1"
                        editable={editingBank}
                        onChange={(v) => setEditedGuardian({ ...editedGuardian, bank_bic: v })}
                        {...sf('q_bic', 'BIC…')}
                      />
                      <FormField
                        label="Mandatsreferenz"
                        value={editingBank ? editedGuardian?.sepa_mandate_reference : (guardian?.sepa_mandate_reference || '-')}
                        className="col-span-2"
                        editable={editingBank}
                        onChange={(v) => setEditedGuardian({ ...editedGuardian, sepa_mandate_reference: v })}
                        data-testid="enrollment-bank-mandate"
                        {...sf('q_mandat', 'Mandat…')}
                      />
                      <FormField
                        label="Mandat erteilt am"
                        value={editingBank ? editedGuardian?.sepa_mandate_date : (guardian?.sepa_mandate_date ? formatDate(guardian.sepa_mandate_date) : '-')}
                        className="col-span-1"
                        editable={editingBank}
                        type={editingBank ? 'date' : 'text'}
                        onChange={(v) => setEditedGuardian({ ...editedGuardian, sepa_mandate_date: v })}
                        {...sf('q_mandat_datum', 'TT.MM.JJJJ')}
                      />
                      {editingBank ? (
                        <SelectField
                          label="Einzugstag"
                          value={String(editedGuardian?.sepa_debit_day || 1)}
                          options={[
                            { value: '1', label: '1. des Monats' },
                            { value: '15', label: '15. des Monats' },
                          ]}
                          className="col-span-1"
                          editable
                          onChange={(v) => setEditedGuardian({ ...editedGuardian, sepa_debit_day: Number(v) })}
                        />
                      ) : searchMode ? (
                        <div className="col-span-1">
                          <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">Zahlungstermin</label>
                          <select
                            value={fieldQuery.q_einzugstag || ''}
                            onChange={(e) => setFieldQuery((q) => ({ ...q, q_einzugstag: e.target.value }))}
                            data-testid="search-field-q_einzugstag"
                            className={`w-full h-6 text-xs rounded border px-2 ${searchInputClassName(fieldQuery.q_einzugstag)}`}
                          >
                            <option value="">—</option>
                            <option value="1">zum 1.</option>
                            <option value="15">zum 15.</option>
                          </select>
                        </div>
                      ) : (
                        <FormField label="Einzugstag" value={guardian?.sepa_debit_day ? `${guardian.sepa_debit_day}. des Monats` : '-'} className="col-span-1" />
                      )}
                      {searchMode && (
                        <>
                          <div className="col-span-2">
                            <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">Zahlungstyp</label>
                            <select
                              value={fieldQuery.q_zahlungsart || ''}
                              onChange={(e) => setFieldQuery((q) => ({ ...q, q_zahlungsart: e.target.value }))}
                              data-testid="search-field-q_zahlungsart"
                              className={`w-full h-6 text-xs rounded border px-2 ${searchInputClassName(fieldQuery.q_zahlungsart)}`}
                            >
                              <option value="">—</option>
                              <option value="sepa">SEPA-Lastschrift</option>
                              <option value="transfer">Selbstzahler / Überweisung</option>
                            </select>
                          </div>
                          <div className="col-span-1">
                            <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">Sperre</label>
                            <select
                              value={fieldQuery.q_zahlungssperre || ''}
                              onChange={(e) => setFieldQuery((q) => ({ ...q, q_zahlungssperre: e.target.value }))}
                              data-testid="search-field-q_zahlungssperre"
                              className={`w-full h-6 text-xs rounded border px-2 ${searchInputClassName(fieldQuery.q_zahlungssperre)}`}
                            >
                              <option value="">—</option>
                              <option value="ja">Ja</option>
                              <option value="nein">Nein</option>
                            </select>
                          </div>
                          <div className="col-span-1">
                            <label className="text-[10px] text-slate-500 uppercase tracking-wide leading-none block mb-0.5">Mandat</label>
                            <select
                              value={fieldQuery.q_sepa_mandat || ''}
                              onChange={(e) => setFieldQuery((q) => ({ ...q, q_sepa_mandat: e.target.value }))}
                              data-testid="search-field-q_sepa_mandat"
                              className={`w-full h-6 text-xs rounded border px-2 ${searchInputClassName(fieldQuery.q_sepa_mandat)}`}
                            >
                              <option value="">—</option>
                              <option value="vorhanden">vorhanden</option>
                              <option value="fehlt">nicht vorhanden</option>
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                    )}
                  </div>

                  {/* Abrechnung — Familien-Monatsvorschau des Zahlungspflichtigen */}
                  <div className="bg-white rounded-lg border border-slate-200 p-2 shrink-0" data-testid="enrollment-billing-section">
                    <SectionHeader
                      title="Abrechnung"
                      color="amber"
                      onTitleClick={guardian ? () => navigate(`/payer-accounts?guardian=${guardian.id}&tab=abrechnung`) : undefined}
                    />
                    {!guardian && !searchMode ? (
                      <div className="text-center py-3 text-slate-400 text-xs space-y-2" data-testid="empty-billing">
                        <p>Zuerst Zahlungspflichtige:r anlegen</p>
                        <Button
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => openNewEnrollment({ withStudent: !!student })}
                          data-testid="create-billing-via-payer-btn"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" /> Zahlungspflichtige:r anlegen
                        </Button>
                      </div>
                    ) : guardian ? (
                      <GuardianBillingPreview
                        guardian={guardian}
                        billingPreview={payerAccount?.billing_preview}
                        invoices={payerAccount?.invoices}
                        loading={payerAccountLoading}
                        compact
                        previewTestId="enrollment-billing-preview"
                        invoicesTestId="enrollment-billing-invoices"
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ListenAusdruckeDialog
        open={listenOpen}
        onClose={() => setListenOpen(false)}
        enrollments={enrollments}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Belegung löschen?</DialogTitle>
            <DialogDescription>
              Möchten Sie die Belegung von <strong>{student?.first_name} {student?.last_name}</strong> im Kurs <strong>{formatClocks(course?.name) || course?.name}</strong> wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDeleteEnrollment}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkEventOpen} onOpenChange={setBulkEventOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="uv-bulk-event-dialog"
          onInteractOutside={(e) => {
            const t = e.target;
            if (t instanceof Element && t.closest('[role="listbox"], [data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Veranstaltung ändern</DialogTitle>
            <DialogDescription className="text-xs">
              Nur ausgefüllte Felder werden geändert. Die Änderung gilt ab dem genannten Datum.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-700" data-testid="uv-bulk-summary">
            {selectedBulkEnrollments.length} Belegungen · {bulkCourseIds.length}{' '}
            {bulkCourseIds.length === 1 ? 'Veranstaltung' : 'Veranstaltungen'}
            {bulkChangeLabels().length ? ` · ${bulkChangeLabels().join(', ')}` : ''}
            {bulkEvent.valid_from ? ` · gültig ab ${formatDate(bulkEvent.valid_from)}` : ''}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <SelectField
              label="Lehrkraft"
              value={bulkEvent.teacher_id}
              options={teachers}
              editable
              onChange={(v) => setBulkEvent({ ...bulkEvent, teacher_id: v })}
              testId="uv-bulk-teacher"
            />
            <SelectField
              label="Termin"
              value={bulkEvent.day_of_week}
              options={WEEKDAY_OPTIONS}
              editable
              onChange={(v) => setBulkEvent({ ...bulkEvent, day_of_week: v })}
              testId="uv-bulk-day"
            />
            <FormField
              label="Zeit"
              value={bulkEvent.start_time}
              editable
              type="time"
              onChange={(v) => setBulkEvent({ ...bulkEvent, start_time: v })}
            />
            <SelectField
              label="Dauer"
              value={bulkEvent.duration_minutes}
              options={DURATION_OPTIONS}
              editable
              onChange={(v) => setBulkEvent({ ...bulkEvent, duration_minutes: v })}
              testId="uv-bulk-duration"
            />
            <SelectField
              label="Turnus"
              value={bulkEvent.rhythm}
              options={TURNUS_OPTIONS}
              editable
              onChange={(v) => setBulkEvent({ ...bulkEvent, rhythm: v })}
              testId="uv-bulk-rhythm"
            />
            <SelectField
              label="Unterrichtsort"
              value={bulkEvent.location_id}
              options={locations}
              editable
              onChange={(v) => {
                const roomOk = rooms.some((r) => r.value === bulkEvent.room_id && (!r.locationId || r.locationId === v));
                setBulkEvent({
                  ...bulkEvent,
                  location_id: v,
                  room_id: roomOk ? bulkEvent.room_id : '',
                });
              }}
              testId="uv-bulk-location"
            />
            <SelectField
              label="Raum"
              value={bulkEvent.room_id}
              options={rooms.filter((r) => !bulkEvent.location_id || !r.locationId || r.locationId === bulkEvent.location_id)}
              editable
              onChange={(v) => setBulkEvent({ ...bulkEvent, room_id: v })}
              testId="uv-bulk-room"
            />
            <div>
              <Label className="text-xs">Gültig ab</Label>
              <DateInput
                value={bulkEvent.valid_from}
                onChange={(e) => setBulkEvent({ ...bulkEvent, valid_from: e.target.value })}
                className="h-8"
                data-testid="uv-bulk-valid-from"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkEventOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={submitBulkEvent}
              disabled={bulkActionLoading || !bulkEvent.valid_from || bulkChangeLabels().length === 0}
              data-testid="uv-bulk-save"
            >
              {bulkActionLoading ? 'Speichere…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">{selectedIds.size} Belegungen löschen?</DialogTitle>
            <DialogDescription>
              Möchten Sie wirklich <strong>{selectedIds.size} Belegungen</strong> dauerhaft löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)} disabled={bulkActionLoading}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkActionLoading}>
              {bulkActionLoading ? 'Lösche...' : `${selectedIds.size} Belegungen löschen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancellation Editor Dialog */}
      <Dialog open={!!cancEditor} onOpenChange={(v) => !v && setCancEditor(null)}>
        <DialogContent className="max-w-md" data-testid="ausfall-editor">
          <DialogHeader>
            <DialogTitle>{cancEditor?.id ? 'Ausfall bearbeiten' : 'Ausfall erfassen'}</DialogTitle>
            <DialogDescription className="text-xs">
              Erstattung wird automatisch aus Tarif/Quotient berechnet, kann aber überschrieben werden.
            </DialogDescription>
          </DialogHeader>
          {cancEditor && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Datum</Label>
                  <DateInput
                    
                    value={cancEditor.cancelled_date || ''}
                    onChange={(e) => setCancEditor({ ...cancEditor, cancelled_date: e.target.value })}
                    className="h-8"
                    data-testid="ausfall-date"
                  />
                </div>
                <div>
                  <Label className="text-xs">Kategorie</Label>
                  <select
                    value={cancEditor.category || ''}
                    onChange={(e) => setCancEditor({ ...cancEditor, category: e.target.value })}
                    className="h-8 w-full border border-slate-200 rounded px-2 text-sm"
                    data-testid="ausfall-category"
                  >
                    {['entschuldigt abwesend','krank','Ferien','Lehrer ausgefallen','Verlegt','Sonstiges'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Bemerkungen</Label>
                <Input
                  value={cancEditor.notes || ''}
                  onChange={(e) => setCancEditor({ ...cancEditor, notes: e.target.value })}
                  className="h-8"
                  placeholder="optional"
                  data-testid="ausfall-notes"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="ausfall-erteilt"
                  type="checkbox"
                  checked={!!cancEditor.erteilt}
                  onChange={(e) => setCancEditor({ ...cancEditor, erteilt: e.target.checked })}
                  data-testid="ausfall-erteilt"
                />
                <Label htmlFor="ausfall-erteilt" className="text-xs cursor-pointer">
                  Trotzdem erteilt (z.B. Online-Ersatz)
                </Label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Erstattungs-Quotient (0–1)</Label>
                  <Input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={cancEditor.erstatten_quotient ?? 0}
                    onChange={(e) => setCancEditor({ ...cancEditor, erstatten_quotient: e.target.value, erstatten_amount: '' })}
                    className="h-8 font-medium"
                    data-testid="ausfall-quotient"
                  />
                </div>
                <div>
                  <Label className="text-xs">Betrag (€) · leer = Auto</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cancEditor.erstatten_amount ?? ''}
                    onChange={(e) => setCancEditor({ ...cancEditor, erstatten_amount: e.target.value })}
                    placeholder="auto"
                    className="h-8 font-medium"
                    data-testid="ausfall-amount"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancEditor(null)} disabled={cancSaving}>
              Abbrechen
            </Button>
            <Button onClick={saveCancellation} disabled={cancSaving} data-testid="ausfall-save">
              {cancSaving ? 'Speichere...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ausfallRow} onOpenChange={(v) => !v && setAusfallRow(null)}>
        <DialogContent className="max-w-md" data-testid="att-ausfall-dialog">
          <DialogHeader>
            <DialogTitle>Ausfall</DialogTitle>
            <DialogDescription className="text-xs">
              EA, UA und LA. Die Abrechnung folgt der eingestellten Kategorie.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            {AUSFALL_ACTIONS.map((act) => (
              <Button
                key={act.code}
                type="button"
                variant="outline"
                className="justify-start"
                data-testid={`att-ausfall-${act.code}`}
                onClick={() => applyAusfall(ausfallRow, act.code)}
              >
                {act.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!moveRow} onOpenChange={(v) => { if (!v) { setMoveRow(null); setMoveDate(''); } }}>
        <DialogContent className="max-w-md" data-testid="att-move-dialog">
          <DialogHeader>
            <DialogTitle>Termin verlegen</DialogTitle>
            <DialogDescription className="text-xs">
              Der ursprüngliche Termin bleibt als verlegt stehen. Der neue Termin ist ein normaler Termin.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Neuer Termin</Label>
            <DateInput
              value={moveDate}
              onChange={(e) => setMoveDate(e.target.value)}
              className="h-8"
              data-testid="att-move-date"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setMoveRow(null); setMoveDate(''); }}>
              Abbrechen
            </Button>
            <Button type="button" onClick={submitMove} disabled={!moveDate} data-testid="att-move-save">
              Verlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendance Editor Dialog */}
      <Dialog open={!!attEditor} onOpenChange={(v) => !v && setAttEditor(null)}>
        <DialogContent className="max-w-md" data-testid="att-editor">
          <DialogHeader>
            <DialogTitle>Termin erfassen</DialogTitle>
            <DialogDescription className="text-xs">
              Manuell einen An-/Abwesenheits-Datensatz anlegen — z.B. für eine
              spontan eingelegte Online-Stunde oder eine Vertretung.
            </DialogDescription>
          </DialogHeader>
          {attEditor && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">Datum</Label>
                <DateInput
                  value={attEditor.lesson_date || ''}
                  onChange={(e) => setAttEditor({ ...attEditor, lesson_date: e.target.value })}
                  className="h-8"
                  data-testid="att-date"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!attEditor.erteilt}
                    onChange={(e) => setAttEditor({ ...attEditor, erteilt: e.target.checked })}
                    data-testid="att-edit-erteilt"
                  />
                  Erteilt
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!attEditor.online_flag}
                    onChange={(e) => setAttEditor({ ...attEditor, online_flag: e.target.checked })}
                    data-testid="att-edit-online"
                  />
                  Online-Unterricht
                </label>
              </div>
              <div>
                <Label className="text-xs">Bemerkungen</Label>
                <Input
                  value={attEditor.notes || ''}
                  onChange={(e) => setAttEditor({ ...attEditor, notes: e.target.value })}
                  className="h-8"
                  placeholder="optional"
                  data-testid="att-edit-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttEditor(null)} disabled={attSaving}>
              Abbrechen
            </Button>
            <Button onClick={saveAttendance} disabled={attSaving} data-testid="att-save">
              {attSaving ? 'Speichere...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Neue Belegung / Neuerfassung — iMikel-Style Modal */}
      <NewEnrollmentDialog
        open={showNewEnrollmentDialog}
        variant={enrollmentDialogVariant}
        prefill={enrollmentDialogPrefill}
        onClose={() => {
          setShowNewEnrollmentDialog(false);
          setEnrollmentDialogPrefill(null);
          setEnrollmentDialogVariant('belegung');
        }}
        onSaved={async (result) => {
          const regId = enrollmentDialogPrefill?.registrationId;
          setShowNewEnrollmentDialog(false);
          setEnrollmentDialogPrefill(null);
          setEnrollmentDialogVariant('belegung');
          if (regId && result?.student_id) {
            try {
              await api.post(`/admin/registrations/${regId}/link-manual-enrollment`, {
                student_id: result.student_id,
                enrollment_id: result.enrollment_id || null,
                course_id: result.course_id || null,
                guardian_id: result.guardian_id || null,
                family_id: result.family_id || null,
                notes: 'Manuell in Unterrichtsverwaltung eingeteilt',
              });
              toast.success('Online-Anmeldung als manuell eingeteilt markiert');
            } catch (e) {
              toast.message('Belegung angelegt — Online-Anmeldung bitte noch manuell abschließen', {
                description: e?.response?.data?.detail || undefined,
              });
            }
          }
          if (result?.enrollment_id) {
            await openLinkedEnrollment(result.enrollment_id);
          } else if (result?.student_id) {
            handleSelectStudent({ id: result.student_id, first_name: '', last_name: '' });
          }
        }}
      />

      <BulkEnrollStudentsDialog
        open={showBulkEnrollDialog}
        prefillCourseId={selectedEnrollment?.course_id || null}
        onClose={() => setShowBulkEnrollDialog(false)}
        onSaved={() => {
          setShowBulkEnrollDialog(false);
          const footer = getFooterSettings();
          if (selectedEnrollment?.course_id) {
            loadEnrollmentsFiltered(
              { course_id: selectedEnrollment.course_id },
              selectedEnrollment.course_name || 'Kurs',
            );
          } else {
            loadEnrollments(footerParams(footer));
          }
        }}
      />

      <CustodianAssignDialog
        open={custodianAssignOpen}
        onOpenChange={setCustodianAssignOpen}
        studentId={student?.id}
        payerId={guardian?.id}
        onChanged={reloadStudent}
      />

      <ContactPersonsDialog
        open={contactPersonsOpen}
        onOpenChange={setContactPersonsOpen}
        studentId={student?.id}
        studentName={student}
        guardians={student?.guardians || []}
        onChanged={reloadStudent}
      />

      <RecordHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        enrollmentId={selectedEnrollment?.id}
        title="Änderungsprotokoll"
      />
      <EffectiveDateDialog
        open={!!effectiveDialog}
        onOpenChange={(o) => { if (!o) setEffectiveDialog(null); }}
        onConfirm={(iso) => {
          const kind = effectiveDialog?.kind;
          setEffectiveDialog(null);
          if (kind === 'event') handleSaveEvent(iso);
          if (kind === 'bank') handleSaveBank(iso);
        }}
      />
    </div>
    </Layout>
  );
}
